import { INTEREST_COPY } from './interestsCopy';
/**
 * Console PageBuilder and hosted translations used to say “pretty face”
 * or “friendly voice and a kind ear”. Strip those lines on TEST so they
 * never reach the UI or SSR HTML.
 */
import { PUBLIC_CONTACT_EMAIL } from './publicContact';
import { isTestMarketplace } from './isTestMarketplace';

export const LANDING_HERO_TITLE = 'Talk with someone who actually listens!';
export const LANDING_HERO_TITLE_RICH = 'Talk with someone who {actually} listens!';
/** Checkout / listing hold copy. Charge is after the session, not on accept. */
export const SESSION_CHARGE_COPY = 'Your card won’t be charged until the session is completed.';
/** Booking / checkout cancel rule. Keep in sync with CANCEL_FREE_NOTICE_HOURS. */
export const SESSION_CANCEL_COPY =
  'Cancel a scheduled session at least 1 hour before the start to release the hold. Cancel inside 1 hour and you are charged in full — the listener is paid. Start now sessions can be canceled without a charge until you join.';

/** Session report control — never the bare word “Report”. */
export const SESSION_REPORT_BUTTON_LABEL = 'Report a problem';
/** Session report modal title / help heading. */
export const SESSION_REPORT_MODAL_TITLE = 'Report a violation or issue';
export const SESSION_REPORT_MODAL_HELP =
  'Tell us what went wrong in this session. We’ll review it. The other person will not see this note.';

/**
 * Console-hosted translations used to ship SessionPanel.report as “Report”.
 * Refuse that word, missing-key ids, and empty values so the live button
 * and modal always show the shipped copy.
 *
 * @param {string} value
 * @param {string} fallback
 * @returns {string}
 */
export const visibleSessionReportCopy = (value, fallback) => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text || /^report$/i.test(text)) {
    return fallback;
  }
  if (/^(SessionPanel|ReportModal|TransactionPanel)\.[A-Za-z0-9.]+$/.test(text)) {
    return fallback;
  }
  return text;
};

const REPLACEMENTS = [
  [/A pretty face and a kind ear/g, LANDING_HERO_TITLE],
  [/A friendly voice and a kind ear/g, LANDING_HERO_TITLE],
  [/a pretty face and a kind ear/gi, 'someone who actually listens'],
  [/a friendly voice and a kind ear/gi, 'someone who actually listens'],
  [/pretty face and a kind ear/gi, 'someone who actually listens'],
  [/friendly voice and a kind ear/gi, 'someone who actually listens'],
  [/a friendly voice/gi, 'someone to talk to'],
  [/friendly voice/gi, 'someone to talk to'],
  [/a kind ear/gi, 'someone who listens'],
  [/kind ear/gi, 'someone who listens'],
  [/\bFmail\b/g, 'Email'],
  [/Waiting for listing owner review/gi, 'Completed'],
  [/Waiting for listener review/gi, 'Completed'],
  [/Waiting for provider review/gi, 'Completed'],
  [/Waiting for customer review/gi, 'Completed'],
  [/Waiting for your review/gi, 'Completed'],
  [/Waiting for review/gi, 'Completed'],
  [/This session is completed(?: · Reviewed)?\./gi, 'Session completed'],
  [/Your booking was completed\./gi, 'Session completed'],
  [
    /Your card will only be charged when \{[^}]+\} accepts the booking request\.?/gi,
    SESSION_CHARGE_COPY,
  ],
  [
    /You will only be charged if your request is accepted by (the provider|\{[^}]+\})\.?/gi,
    SESSION_CHARGE_COPY,
  ],
  [/You won['’]t be charged yet\.?/gi, SESSION_CHARGE_COPY],
  [/Reviews from people I listened to/gi, 'Reviews from listeners'],
  [/Reviews as a customer/gi, 'Reviews from listeners'],
  [/Reviews I left/gi, 'Reviews from listeners'],
];

const rewriteString = value =>
  REPLACEMENTS.reduce((next, [pattern, replacement]) => next.replace(pattern, replacement), value);

const TEMPLATE_CREDIT_PATTERNS = [
  /You can find the (original )?template \[[^\]]+\]\([^)]+\)\.?/gi,
  /You can find the (original )?template <a\b[^>]*>.*?<\/a>\.?/gi,
  /You can find the (original )?template here\.?/gi,
  /To launch your marketplace fast, use our template as a basis for your (terms|policy)\.?/gi,
  /Every marketplace business needs terms of service\.?/gi,
  /Every marketplace business needs a privacy policy\.?/gi,
  /https:\/\/(www\.)?sharetribe\.com\/help\/en\/articles\/8410839[^\s)"']*/gi,
];

const stripTemplateCredit = value => {
  const stripped = TEMPLATE_CREDIT_PATTERNS.reduce(
    (next, pattern) => next.replace(pattern, ''),
    value
  )
    .replace(/\[your email\]/gi, PUBLIC_CONTACT_EMAIL)
    .replace(/Info@sweetlisteners\.com/g, PUBLIC_CONTACT_EMAIL)
    .replace(/info@sweetlisteners\.com/gi, PUBLIC_CONTACT_EMAIL)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return stripped;
};

const isPageBuilderBlock = item =>
  Boolean(item && typeof item === 'object' && (item.blockId || item.blockType));

const hasNonEmptyText = data => typeof data?.content === 'string' && data.content.trim() !== '';

const isUnsetField = data => {
  if (data == null || typeof data !== 'object') {
    return true;
  }
  if (data.fieldType === 'none') {
    return true;
  }
  if (Object.prototype.hasOwnProperty.call(data, 'content')) {
    return !hasNonEmptyText(data);
  }
  return false;
};

const isEmptyMarkdownBlock = item => {
  if (!isPageBuilderBlock(item)) {
    return false;
  }
  return (
    isUnsetField(item.title) &&
    isUnsetField(item.text) &&
    isUnsetField(item.media) &&
    isUnsetField(item.callToAction)
  );
};

const sectionHasVisibleContent = item => {
  if (!item || typeof item !== 'object' || !Array.isArray(item.blocks)) {
    return true;
  }
  return item.blocks.length > 0 || hasNonEmptyText(item.title) || hasNonEmptyText(item.description);
};

/**
 * Recursively rewrite retired taglines in hosted translations, PageBuilder
 * JSON, and any other nested Console content.
 */
export const rewritePrettyFaceCopy = value => {
  if (typeof value === 'string') {
    return stripTemplateCredit(rewriteString(value));
  }
  if (Array.isArray(value)) {
    return value
      .map(rewritePrettyFaceCopy)
      .filter(item => !isEmptyMarkdownBlock(item))
      .map(item => {
        if (!item || typeof item !== 'object' || !Array.isArray(item.blocks)) {
          return item;
        }
        return { ...item, blocks: item.blocks.filter(block => !isEmptyMarkdownBlock(block)) };
      })
      .filter(item => {
        if (!item || typeof item !== 'object' || !Array.isArray(item.blocks)) {
          return true;
        }
        return sectionHasVisibleContent(item);
      });
  }
  if (value && typeof value === 'object') {
    let current = value;
    if (
      value.fieldType === 'heading1' &&
      typeof value.content === 'string' &&
      value.content.length > 60
    ) {
      if (
        /terms of service/i.test(value.content) ||
        /by creating an account/i.test(value.content)
      ) {
        current = { ...value, content: 'Terms of Service' };
      } else if (/privacy policy/i.test(value.content)) {
        current = { ...value, content: 'Privacy Policy' };
      }
    }
    const rewritten = Object.keys(current).reduce((acc, key) => {
      const next = rewritePrettyFaceCopy(current[key]);
      if (isEmptyMarkdownBlock(next)) {
        return acc;
      }
      acc[key] = next;
      return acc;
    }, {});
    return rewritten;
  }
  return value;
};

/**
 * Console-hosted translations can overwrite en.json. Pin the landing H1
 * and footer line so the retired tagline cannot come back.
 */
export const withPinnedLandingCopy = messages => {
  const next = { ...messages };
  delete next['LandingPage.heroSubhead'];
  next['LandingPage.heroTitle'] = LANDING_HERO_TITLE;
  next['LandingPage.heroTitleRich'] = LANDING_HERO_TITLE_RICH;
  next['LandingPage.heroHeadline'] = 'Sometimes you just need someone to talk to.';
  next['LandingPage.heroLede'] =
    'Have real conversations with kind, supportive people — anytime you need a friendly ear.';
  next['SiteFooter.tagline'] = LANDING_HERO_TITLE;
  next['SiteFooter.motto'] = 'Listen. Care. Connect. Learn.';
  next['SiteFooter.flourish'] = '';
  next['SiteFooter.notTherapy'] =
    'Feel heard, lighter and more connected. Your first 15 minutes are free.';
  next['LandingPage.findListener'] = 'Find someone to talk to or learn from';
  next['LandingPage.createFreeAccount'] = 'Create a free account';
  next['LandingPage.dashboard'] = 'Listening dashboard';
  next['LandingPage.hubDashboard'] = 'Dashboard';
  next['LandingPage.hubHello'] = 'Hello{name}';
  next['LandingPage.hubPrompt'] = 'Good to see you. What would you like to do today?';
  next['LandingPage.hubFavorites'] = 'Favorites';
  next['LandingPage.hubLinksLabel'] = 'Quick links';
  next['LandingPage.pathTalkTitle'] = 'Talk';
  next['LandingPage.pathListenTitle'] = 'Listen';
  next['LandingPage.pathListenBody'] =
    'You earn 70% of the rates you set. Set your own rate for text, voice, or video.';
  next['LandingPage.pathBothKicker'] = 'I want to';
  next['LandingPage.pathBothTitle'] = 'Both';
  next['LandingPage.pathBothBody'] = 'Want to listen and talk? You can do both.';
  next['LandingPage.pathBothCta'] = 'Create a free account';
  next['LandingPage.heroEyebrow'] = 'FRIENDLY LISTENERS. PRIVATE CONVERSATIONS.';
  next['LandingPage.newHereTitle'] = 'New to Sweet Listeners?';
  next['LandingPage.learnTitle'] = 'Learn More';
  next['LandingPage.learnBody'] =
    'Get the details on how it works, pricing, safety, and what to expect.';
  next['LandingPage.learnCta'] = 'How Sweet Listeners works';
  next['LandingPage.learnMoreButton'] = 'Learn More';
  next['LandingPage.roleLearnMore'] = 'Learn more';
  next['ContentPage.howItWorks.learnMore'] = 'Learn more';
  next['LandingPage.pathBandLabel'] = 'Ways to use Sweet Listeners';
  next['LandingPage.talkCardTitle'] = 'For talkers';
  next['LandingPage.talkCardKicker'] = 'A safe space for your mind';
  next['LandingPage.talkBenefit1Title'] = 'Feel better';
  next['LandingPage.talkBenefit1Body'] = 'Get it off your chest without judgment.';
  next['LandingPage.talkBenefit2Title'] = 'Gain new perspectives';
  next['LandingPage.talkBenefit2Body'] =
    'Talk through life, relationships, work, or anything on your mind.';
  next['LandingPage.talkBenefit3Title'] = 'Feel less alone';
  next['LandingPage.talkBenefit3Body'] = 'Connect with kind, supportive people who truly listen.';
  next['LandingPage.talkCta'] = 'Find a Listener →';
  next['LandingPage.listenCardTitle'] = 'For listeners';
  next['LandingPage.listenCardKicker'] = 'Make a positive impact';
  next['LandingPage.listenBenefit1Title'] = 'Earn extra income, work from wherever you are';
  next['LandingPage.listenBenefit1Body'] = 'Get paid for your time and empathy.';
  next['LandingPage.listenBenefit2Title'] = 'Choose your own schedule';
  next['LandingPage.listenBenefit2Body'] = 'Be available when it works for you.';
  next['LandingPage.listenBenefit3Title'] = 'Make a difference';
  next['LandingPage.listenBenefit3Body'] = "Your support can truly brighten someone's day.";
  next['LandingPage.listenCta'] = 'Become a Listener →';
  next['LandingPage.listenDashboardCta'] = 'Go to my Listener dashboard →';
  next['LandingPage.viewProfile'] = 'View Profile';
  next['LandingPage.online'] = 'Online';
  next['LandingPage.offline'] = 'Book a time';
  next['TopbarDesktop.login'] = 'Log In';
  next['TopbarDesktop.signup'] = 'Sign Up';
  next['LandingPage.pathBothTertiary'] = 'I want to listen and talk';
  next['LandingPage.learnHighlight.conversation'] = 'A real.';
  next['LandingPage.learnHighlight.formats'] = 'Text, Voice, or Video for {minutes} minutes.';
  next['LandingPage.learnHighlight.earn'] = 'Listeners earn 70% of the rates you set.';
  next['LandingPage.learnHighlight.safety'] = 'Safe and private. Conversations stay here.';
  next['LandingPage.featuredGuestNote'] =
    'You can read a profile now. Book and Start now need an account.';
  next['LandingPage.learnSafety'] = 'Safety';
  next['LandingPage.learnPricing'] = 'Prices';
  next['LandingPage.alreadyHaveAccount'] = 'Sign in';
  next['DashboardPage.findSomeone'] = 'Find a Listener';
  next['DashboardPage.navDashboard'] = 'Dashboard';
  next['DashboardPage.navAvailability'] = 'My Availability';
  next['DashboardPage.navSessions'] = 'My Bookings';
  next['DashboardPage.navProfile'] = 'My Profile';
  next['DashboardPage.navSettings'] = 'Settings';
  next['DashboardPage.navFavorites'] = 'My Favorites';
  next['DashboardPage.needHelp'] = 'Tech Support';
  next['DashboardPage.contactSupport'] = 'Tech Support';
  next['DashboardPage.techSupport'] = 'Tech Support';
  next['DashboardPage.listenerHeroLede'] = 'You’re making a difference.';
  next['DashboardPage.talkerHeroLede'] = 'Who would you like to talk to today?';
  next['DashboardPage.talkerHeroLedeMobile'] = 'Who would you like to talk to today?';
  next['DashboardPage.talkerAskWho'] = 'Who would you like to talk to today?';
  next['DashboardPage.talkerHi'] = 'Hi {name},';
  next['DashboardPage.navPayments'] = 'Payments';
  next['DashboardPage.navCalendar'] = 'Calendar';
  next['DashboardPage.navBookings'] = 'My Bookings';
  next['DashboardPage.upcomingSessionsTitle'] = 'Your Upcoming Sessions';
  next['DashboardPage.statUpcomingShort'] =
    '{count, plural, one {Upcoming Session} other {Upcoming Sessions}}';
  next['DashboardPage.statRequestsShort'] =
    '{count, plural, one {New Request} other {New Requests}}';
  next['DashboardPage.joinSession'] = 'Join Session';
  next['DashboardPage.thanksImpact'] = 'Thank you for being here. You make a positive impact.';
  next['DashboardPage.activityPayment'] = 'Payment received {amount}';
  next['DashboardPage.statPastSessions'] =
    '{count, plural, one {Past Session} other {Past Sessions}}';
  next['DashboardPage.statFavoriteListeners'] =
    '{count, plural, one {Favorite Listener} other {Favorite Listeners}}';
  next['DashboardPage.statBalance'] = 'Balance';
  next['DashboardPage.statActiveBookings'] =
    '{count, plural, one {Active Booking} other {Active Bookings}}';
  next['DashboardPage.upcomingEmptyTitle'] = 'No upcoming Sessions';
  next['DashboardPage.upcomingEmpty'] =
    'You have no upcoming sessions. Find a Listener to get started!';
  next['DashboardPage.messageListener'] = 'Message';
  next['DashboardPage.bookAgain'] = 'Book again';
  next['DashboardPage.findSomeoneLede'] =
    'Browse listeners any time. Start now if they are available.';
  next['DashboardPage.availableListenersTitle'] = 'Available listeners';
  next['DashboardPage.availableListenersEmpty'] =
    'No one is available right now. Browse any time to book a session.';
  next['DashboardPage.availableListenersBrowse'] = 'Browse any time';
  next['DashboardPage.recentListenersTitle'] = 'Recent Conversations';
  next['DashboardPage.recentListenersEmpty'] = 'Your conversation history will appear here.';
  next['DashboardPage.messagesTitle'] = 'Messages';
  next['DashboardPage.requestsTitle'] = 'Requests';
  next['DashboardPage.incomingTitle'] = 'Incoming / Confirm';
  next['DashboardPage.confirmTitle'] = 'Incoming / Confirm';
  next['DashboardPage.confirmEmpty'] = 'No requests waiting. New bookings to confirm show up here.';
  next['DashboardPage.inSessionLabel'] = 'In session';
  next['DashboardPage.upcomingLabel'] = 'Upcoming';
  next['DashboardPage.confirmLabel'] = 'Confirm';
  next['DashboardPage.seePastSessions'] = 'Past sessions';
  next['DashboardPage.reviewsToSubmitTitle'] = 'Reviews left to submit';
  next['DashboardPage.leaveReview'] = 'Leave a review';
  next['DashboardPage.howDidItGoTitle'] = 'How did it go?';
  next['DashboardPage.howDidItGo'] = 'How did it go?';
  next['DashboardPage.welcomeHowDidItGo'] =
    'How did {count, plural, one {# session} other {# sessions}} go?';
  next['DashboardPage.pastLinkLede'] = 'Finished sessions live on a separate page.';
  next['PastSessionsPage.title'] = 'Past sessions';
  next['PastSessionsPage.heading'] = 'Past sessions';
  next['PastSessionsPage.lede'] =
    'Sessions that already ended. The dashboard only shows Incoming / Confirm, Upcoming, and In session.';
  next['PastSessionsPage.empty'] = 'No past sessions yet.';
  next['PastSessionsPage.talker'] = 'You were the Talker';
  next['PastSessionsPage.listener'] = 'You were the Listener';
  next['SessionsCalendarPage.title'] = 'Calendar';
  next['SessionsCalendarPage.heading'] = 'Calendar';
  next['SessionsCalendarPage.lede'] =
    'Your 15-minute sessions on the America/New_York calendar. Open a session to go to that booking.';
  next['SessionsCalendarPage.openBooking'] = 'View';
  next['SessionsCalendarPage.loading'] = 'Loading your sessions…';
  next['SessionsCalendarPage.dayEmpty'] = 'No sessions on this day.';
  next['SessionsCalendarPage.prevMonth'] = 'Previous month';
  next['SessionsCalendarPage.nextMonth'] = 'Next month';
  next['SessionsCalendarPage.listener'] = 'Listening';
  next['SessionsCalendarPage.talker'] = 'Talking';
  next['SessionsCalendarPage.bookingCount'] = '{count, plural, one {# booking} other {# bookings}}';
  next['SessionsCalendarPage.upcomingThisMonth'] =
    '{count, plural, one {# upcoming this month} other {# upcoming this month}}';
  next['SessionsCalendarPage.pastThisMonth'] =
    '{count, plural, one {# past this month} other {# past this month}}';
  next['SessionsCalendarPage.cancelledThisMonth'] =
    '{count, plural, one {# cancelled this month} other {# cancelled this month}}';
  next['SessionsCalendarPage.allThisMonth'] =
    '{count, plural, one {# session this month} other {# sessions this month}}';
  next['SessionsCalendarPage.legendUpcoming'] = 'Upcoming';
  next['SessionsCalendarPage.legendPending'] = 'Pending';
  next['SessionsCalendarPage.legendPast'] = 'Past';
  next['ContentPage.howItWorks.kicker'] = 'Learn More';
  next['ContentPage.howItWorks.heroEyebrow'] = 'About Us';
  next['ContentPage.howItWorks.heroTitle'] = 'A more human <line>way to connect.</line>';
  next['ContentPage.howItWorks.heroLede'] =
    'Sometimes you just need someone to talk to — and someone who’s really listening. We’re here to make the world a kinder, more connected place — one conversation at a time.';
  next['ContentPage.howItWorks.heroScript'] = 'Listen. Care. Connect. Learn.';
  next['ContentPage.howItWorks.talkCta'] = 'I want to talk →';
  next['ContentPage.howItWorks.listenCta'] = 'I want to listen →';
  next['ContentPage.howItWorks.simpleTitle'] = 'A simple way to talk — or earn by listening.';
  next['ContentPage.howItWorks.simpleLede'] = 'Friendly listeners. Private conversations.';
  next['ContentPage.howItWorks.factHowTitle'] = 'How it works';
  next['ContentPage.howItWorks.factHowBody'] =
    'Pick a topic, find a listener who loves it, choose text, voice, or video, then Start now or book a 15-minute session.';
  next['ContentPage.howItWorks.factPriceTitle'] = 'Prices';
  next['ContentPage.howItWorks.factPriceBody'] =
    'Each listener sets their own prices for text, voice and video.';
  next['ContentPage.howItWorks.factSafetyTitle'] = 'Safety';
  next['ContentPage.howItWorks.factSafetyBody'] =
    'Adults only. Conversations stay on the site.';
  next['ContentPage.howItWorks.findTitle'] = 'Find a listener';
  next['ContentPage.howItWorks.findBody'] =
    'Read a short bio and find someone who feels like a good fit.';
  next['ContentPage.howItWorks.chooseTitle'] = 'Choose how to talk';
  next['ContentPage.howItWorks.chooseBody'] =
    'Text, voice or video at the listener’s own prices, or talk now if they are available';
  next['ContentPage.howItWorks.connectTitle'] = 'Connect';
  next['ContentPage.howItWorks.connectBody'] =
    'Book a {minutes}-minute session and start your conversation.';
  next['ContentPage.howItWorks.forTalkers'] = 'For talkers';
  next['ContentPage.howItWorks.talkerHeading'] = 'A safe space for your mind.';
  next['ContentPage.howItWorks.talkerCheck1'] = 'Find a listener who understands';
  next['ContentPage.howItWorks.talkerCheck2'] = 'Talk about whatever is on your mind';
  next['ContentPage.howItWorks.talkerCheck3'] = 'No judgment, just real conversation';
  next['ContentPage.howItWorks.findListenerCta'] = 'Find a Listener →';
  next['ContentPage.howItWorks.forListeners'] = 'For listeners';
  next['ContentPage.howItWorks.listenerHeading'] = 'Make a positive impact.';
  next['ContentPage.howItWorks.listenerCheck1'] =
    'Create your profile and offer text, voice, or video';
  next['ContentPage.howItWorks.listenerCheck2'] = 'Choose when you’re available';
  next['ContentPage.howItWorks.listenerCheck3'] =
    'Connect for {minutes} minutes and earn 70% of the rates you set';
  next['ContentPage.howItWorks.becomeListenerCta'] = 'Become a Listener →';
  next['ContentPage.howItWorks.ourPrices'] = 'Our Prices';
  next['ContentPage.howItWorks.ourPricesLede'] = 'Pick a length and a format, or talk now.';
  next['ContentPage.howItWorks.priceChat'] = 'Chat';
  next['ContentPage.howItWorks.priceVoice'] = 'Voice';
  next['ContentPage.howItWorks.priceVideo'] = 'Video';
  next['ContentPage.howItWorks.perMinutes'] = 'per {minutes} min';
  next['ContentPage.howItWorks.communityHeading'] = 'A safe and supportive community';
  next['ContentPage.howItWorks.communityCheck1'] = 'Conversations stay on the site';
  next['ContentPage.howItWorks.communityCheck2'] = 'No dating or adult content';
  next['ContentPage.howItWorks.communityCheck3'] = 'Personal contact information stays private';
  next['ContentPage.howItWorks.communityCheck4'] = 'Reporting tools if something feels off';
  next['ContentPage.howItWorks.communityCheck5'] = 'Adults 18+ only';
  next['ContentPage.howItWorks.notTherapy'] =
    'This site is for conversation and companionship. If you need emergency help, please contact local emergency services.';
  next['ContentPage.howItWorks.haveQuestions'] = 'Have questions?';
  next['ContentPage.howItWorks.haveQuestionsLede'] =
    'Visit our FAQ page for answers to common questions.';
  next['ContentPage.howItWorks.viewFaqs'] = 'View FAQs →';
  next['ContentPage.howItWorks.heading'] = 'How it works';
  next['ContentPage.howItWorks.lede'] =
    'How it works, prices, safety, what listeners keep, and what to expect. Every session is {minutes} minutes.';
  next['ContentPage.howItWorks.stepsHeading'] = 'How it works';
  next['ContentPage.howItWorks.pricesHeading'] = 'Prices';
  next['ContentPage.howItWorks.pricesLede'] =
    'Text, voice or video. Each listener sets their own prices.';
  next['ContentPage.howItWorks.safetyHeading'] = 'Safety';
  next['ContentPage.howItWorks.earnHeading'] = 'You earn 70% of the rates you set';
  next['ContentPage.howItWorks.earnBody'] =
    'Listeners earn 70% of the rates you set. Set your own rate for text, voice, or video.';
  next['ContentPage.howItWorks.expectHeading'] = 'What to expect';
  next['ContentPage.howItWorks.whatTitle'] = 'What it is';
  next['ContentPage.howItWorks.whatBody'] =
    'It’s a warm conversation with someone who will listen, so you feel heard.';
  next['ContentPage.howItWorks.talkTitle'] = 'How Talk works';
  next['ContentPage.howItWorks.talkBody'] =
    'Browse Featured listeners, pick Text, Voice, or Video, and talk for {minutes} minutes.';
  next['ContentPage.howItWorks.listenTitle'] = 'How Listen works';
  next['ContentPage.howItWorks.listenBody'] =
    'Earn money by listening. You earn 70% of the rates you set. Turn Available now on when you can take a Start now session.';
  next['ContentPage.howItWorks.bothTitle'] = 'Both';
  next['ContentPage.howItWorks.bothBody'] = 'You can talk and listen with the same account.';
  next['ContentPage.howItWorks.safetyTitle'] = 'Safety and prices';
  next['ContentPage.howItWorks.safetyBody'] =
    'Conversations stay on the site. Each listener sets their own prices.';
  next['ContentPage.about.kicker'] = 'Our Story';
  next['ContentPage.about.heading'] = 'It started with <line>the conversations.</line>';
  next['ContentPage.about.lede'] =
    'It’s a place to find someone to talk to — an online listener you can sit with for a short, kind conversation.';
  next['ContentPage.about.p1'] =
    'When you need someone to talk to, you can pay someone to listen. Sessions are 15 minutes of text, voice, or video.';
  next['ContentPage.about.originHeading'] = 'Our Story';
  next['ContentPage.about.originP1'] =
    'The idea for our site came from noticing something unexpected in the online creator world. Platforms built around paid content were also creating something much more human behind the scenes: conversations. People would come for the content, but many stayed because they valued having someone to talk to — someone who would listen, respond, and make them feel seen.';
  next['ContentPage.about.originP2'] =
    'So we took that one meaningful part and built something completely different around it. It’s a place to find someone to talk to — an online listener you can sit with for a short, kind conversation. When you need someone to talk to, you can pay someone to listen. Sessions are 15 minutes of text, voice, or video.';
  next['ContentPage.about.howItWorks'] = 'See How It Works →';
  next['ContentPage.about.howToUseIt'] = 'See How It Works →';
  next['ContentPage.about.createAccount'] = 'Create an account →';
  next['ContentPage.about.becomeListener'] = 'Become a listener';
  next['ContentPage.about.question1'] =
    'What if the conversation wasn’t something that came with the content?';
  next['ContentPage.about.question2'] = 'What if the conversation was the entire experience?';
  next['ContentPage.about.emergency'] =
    'If you need emergency help, please contact a professional.';
  next['GuestTopbar.findListener'] = 'Find a Listener';
  next['GuestTopbar.becomeListener'] = 'Become a Listener';
  next['GuestTopbar.howItWorks'] = 'How It Works';
  next['GuestTopbar.forTalkers'] = 'For Talkers';
  next['GuestTopbar.forListeners'] = 'For Listeners';
  next['GuestTopbar.about'] = 'About';
  next['GuestTopbar.faq'] = 'FAQ';
  next['GuestTopbar.login'] = 'Log In';
  next['GuestTopbar.signup'] = 'Sign Up';
  // Sam-locked FAQ mock answers. Do not say prices vary by listener.
  next['FAQPage.kicker'] = 'Questions';
  next['FAQPage.heading'] = 'FAQ';
  next['FAQPage.lede'] =
    "Find quick answers below. Can't find what you're looking for? We're here to help.";
  next['FAQPage.listHeading'] = 'Common questions';
  next['FAQPage.howItWorks'] = 'How it works';
  next['FAQPage.becomeListener'] = 'Become a listener';
  next['FAQPage.searchLabel'] = 'Search questions';
  next['FAQPage.searchPlaceholder'] = 'Search questions...';
  next['FAQPage.categoriesLabel'] = 'FAQ categories';
  next['FAQPage.categoryAll'] = 'All Questions';
  next['FAQPage.categoryAllShort'] = 'All';
  next['FAQPage.categoryGeneral'] = 'General';
  next['FAQPage.categoryTalkers'] = 'For Talkers';
  next['FAQPage.categoryListeners'] = 'For Listeners';
  next['FAQPage.categoryPayments'] = 'Payments & Pricing';
  next['FAQPage.categorySafety'] = 'Safety & Moderation';
  next['FAQPage.empty'] = 'No matching questions.';
  next['FAQPage.stillHave'] = 'Still have a question?';
  next['FAQPage.stillHaveLede'] = "We're here to help.";
  next['FAQPage.contactSupport'] = 'Contact Support';
  next['FAQPage.footerMotto'] = 'Listen. Care. Connect.';
  next['FAQPage.footerContact'] = 'Contact Us';
  next['FAQPage.footerCopyright'] = '© 2026 Sweet Listeners LLC. All rights reserved.';
  next['FAQPage.qWhatIs'] = 'What is this site?';
  next['FAQPage.aWhatIs'] =
    'We are an online marketplace where adults can pay for one-on-one conversation through text, voice, or video. It’s about support and human connection — not dating or adult content.';
  next['FAQPage.qHowItWorks'] = 'How does it work?';
  next['FAQPage.aHowItWorks'] =
    'Pick a topic, find a listener who loves it, choose text, voice, or video, then Start now or book a 15-minute session. Every booking is confirmed instantly, and you’re only charged after the session.';
  next['FAQPage.qAcceptWindow'] = 'Do listeners need to accept my booking?';
  next['FAQPage.aAcceptWindow'] =
    'No. Every booking is confirmed instantly, whether you Start now or book a time. You’re only charged after the session.';
  next['ForTalkersPage.schemaDescription'] =
    'Find a listener. Listeners set their own prices. Start now or book a time.';
  next['ForTalkersPage.whenHeading'] = 'Start now or book a time';
  next['ForTalkersPage.startNowTitle'] = 'Start now';
  next['ForTalkersPage.startNowBody'] =
    'If they are Online, join immediately. Start now sessions are never charged if you don’t join.';
  next['ForTalkersPage.bookLaterBody'] =
    'Pick a slot. Every booking is confirmed instantly, and you’re only charged after the session.';
  next['FAQPage.qDating'] = 'Is this a dating site?';
  next['FAQPage.aDating'] =
    'No. We are strictly for platonic conversation. Dating, sexual content, and adult content are not allowed.';
  next['FAQPage.qAge'] = 'How old do I have to be?';
  next['FAQPage.aAge'] =
    "This site is for adults 18 and older. To talk or learn, you only confirm you’re 18+. No ID check. Listeners and teachers complete a one-time ID check before they can be booked.";
  next['FAQPage.qVerified'] = 'How are listeners verified?';
  next['FAQPage.aVerified'] =
    'Listeners must complete our identity and age-verification process before offering paid sessions.';
  next['FAQPage.qWhenPaid'] = 'When do I get paid?';
  next['FAQPage.aWhenPaid'] =
    'After each completed session. You earn 70%: $7 for chat, $14 for voice, $21 for video. Your bank deposit usually arrives in a few business days. Stripe may email you about the payout. If a talker misses a scheduled session after you join, you are still paid.';
  next['FAQPage.qTalkAbout'] = 'What can I talk about?';
  next['FAQPage.aTalkAbout'] =
    'You can talk about almost anything — life, relationships, work, stress, goals, or simply needing a conversation. Follow our community guidelines.';
  next['FAQPage.qCost'] = 'How much does it cost?';
  next['FAQPage.aCost'] = 'Each listener sets their own prices for text, voice and video. Text is $1–$50 and voice and video are $5–$50 per 15 minutes.';
  next['FAQPage.qCancel'] = 'Can I cancel a booking?';
  next['FAQPage.aCancel'] =
    'For a scheduled session, cancel at least 1 hour before the start and the hold is released — you are not charged. Cancel inside 1 hour and you are charged in full and the listener is paid, the same as a no-show. Start now sessions can be canceled without a charge until you join. If a listener cancels at any time, the talker is never charged.';
  // Signup side panel: copy ban on that phrase (Oct 19 2026).
  next['AuthenticationPage.valuePropPeopleTitle'] = 'Friendly Listeners';
  // Sweet Treat + first-session guarantee FAQ (Oct 19 2026).
  next["FAQPage.qSweetTreat"] =
    "What is the Sweet Treat?";
  next["FAQPage.aSweetTreat"] =
    "Your first session is free, any format, with listeners and teachers showing the Sweet Treat badge. It’s one free 15-minute session per person on the site: chat, voice or video, whichever they offer. Look for the badge on Browse or tap the “Sweet Treat” filter, and the payment page shows $0.00. Use it within 30 days of signing up. You still add a card, but it isn’t charged for this session. If you or your listener cancels, or your listener doesn’t join, you get your Sweet Treat back. If you don’t join after your listener does, it’s used up. Listeners give Sweet Treat sessions without being paid, so please show up.";
  next["FAQPage.qSweetTreatListener"] =
    "Listeners: what is the Sweet Treat, and am I paid for it?";
  next["FAQPage.aSweetTreatListener"] =
    "The Sweet Treat is one free 15-minute first session for each new talker, in any format you offer. The talker pays nothing, and you are not paid for that session — you give the 15 minutes yourself. Each one is labelled “Sweet Treat” in your email, texts, inbox, dashboard and earnings. Why give it: it lets new talkers try the site without paying, and it’s the quickest way to get your first reviews and talkers who come back and book you at your normal price. It’s on by default and your profile shows a “Sweet Treat” badge. To turn it off, use the Sweet Treat switch on your dashboard. It stops right away, sessions already booked still happen, and you can turn it back on anytime. You also choose your weekly limit: 1, 3, 5 or 10 Sweet Treat sessions a week (Monday to Sunday, Eastern Time; 3 if you don’t choose). When you reach it, your badge hides until Monday. Sessions that are cancelled, or that you couldn’t join, don’t count toward your limit. If you only want paid sessions, turn Sweet Treat off.";
  next["FAQPage.qRefund"] =
    "What is the Sweet Guarantee?";
  next["FAQPage.aRefund"] =
    "If you’re not happy with a completed paid session, your next one is on us. Within 24 hours after the session ends, tap “Claim my free Sweet Guarantee session” on that session’s page. No form, no questions. You get a free 15-minute make-good session in the same format (text, voice, or video) with any listener, and checkout shows FREE ($0.00). Sweet Listeners pays the listener for the make-good, and your original listener keeps their pay. There’s no lifetime limit. If you’ve claimed on more than half of your paid sessions, we may ask a few questions or review before more make-goods, and abuse of the guarantee can lead to your account being canceled. Not covered: sessions ended for breaking the Community Guidelines, no-shows (missed sessions and late cancels follow the cancellation policy), and free sessions like the Sweet Treat. The program has a monthly budget; if it’s reached, claims reopen next month. Sweet Listeners is self-help listening, not therapy or counseling, and listeners aren’t clinicians. Questions: info@sweetlisteners.com.";
  next["FAQPage.qRefundListener"] =
    "Listeners: if a talker claims the Sweet Guarantee, do I still get paid?";
  next["FAQPage.aRefundListener"] =
    "Yes. You keep your full pay for the original session. The free make-good session is paid by Sweet Listeners, so the listener the talker books for it is paid normally too.";
  next['FAQPage.qWhenCharged'] = 'When am I charged?';
  next['FAQPage.aWhenCharged'] =
    'After your session ends — adding a card never charges you. When you book, your bank may show a temporary hold for the session price until then. Cancel a scheduled session at least 1 hour before the start and you’re not charged. If you cancel inside 1 hour, or miss a scheduled session after your listener joins, you’re charged the full session price. Start now is never charged if you don’t join. If your listener cancels or doesn’t join, you’re never charged. Your card is kept securely by Stripe, our payment provider — we never see your full card number.';
  next['FAQPage.qPaymentMethods'] = 'What payment methods do you accept?';
  next['FAQPage.aPaymentMethods'] =
    'We accept all major credit and debit cards through our secure payment provider.';
  next['FAQPage.qRequestListener'] = 'Can I request a specific listener?';
  next['FAQPage.aRequestListener'] =
    'Yes. You can browse listener profiles and choose who you’d like to talk with based on their profile and availability.';
  next['FAQPage.qRules'] = 'What happens if someone breaks the rules?';
  next['FAQPage.aRules'] =
    'If you report inappropriate behavior, we review all reports and may restrict or remove accounts that violate our community guidelines.';
  next['FAQPage.qTherapy'] = 'What kind of support do we offer?';
  next['FAQPage.aTherapy'] =
    'Friendly conversation. Listeners hear you out so you feel lighter. They are not a medical or crisis service.';
  next['FAQPage.qEmergency'] = 'What if I need emergency help?';
  next['FAQPage.aEmergency'] =
    "If you or someone else is in immediate danger, call 911 now. If you're in crisis or thinking about suicide, call or text 988 (Suicide & Crisis Lifeline). This site is peer support, not emergency or medical care.";
  next['SiteFooter.faq'] = 'FAQ';
  next['SiteDrawer.about'] = 'About';
  next['SiteDrawer.faq'] = 'FAQ';
  next['SiteDrawer.support'] = 'Tech Support';
  next['SiteDrawer.help'] = 'Tech Support';
  next['SiteDrawer.dashboard'] = 'Dashboard';
  next['SiteDrawer.myListeningProfile'] = 'My Listening Profile';
  next['SiteDrawer.myAvailability'] = 'My Availability';
  next['SiteDrawer.availability'] = 'Set availability';
  next['SiteDrawer.mySessions'] = 'My Bookings';
  next['SiteDrawer.favorites'] = 'Favorites';
  next['SiteDrawer.tipsResources'] = 'Tips & Resources';
  next['SiteDrawer.myBookings'] = 'My Bookings';
  next['SiteDrawer.find'] = 'Browse Listeners';
  next['SiteDrawer.myTalkingProfile'] = 'My Profile';
  next['SiteDrawer.switchToTalker'] = 'Switch to Talker';
  next['SiteDrawer.switchToListener'] = 'Switch to Listener';
  next['SiteDrawer.become'] = 'Become an earner';
  next['SiteDrawer.becomeTalker'] = 'Become a Talker';
  next['SiteDrawer.promote'] = 'Promote';
  next['DashboardPage.tipsLink'] = 'Tips & Resources';
  next['DashboardPage.promoteLink'] = 'Promote Our Site';
  next['ModeSwitchToggle.listener'] = 'Listener';
  next['ModeSwitchToggle.talker'] = 'Talker';
  next['ModeSwitchToggle.label'] = "I'm using the site as a";
  next['ContentPage.howItWorks.createAccount'] = 'Create a free account';
  next['ContentPage.howItWorks.earnCta'] = 'Earn $ as a listener';
  next['ContentPage.howItWorks.signIn'] = 'Sign in';
  next['SiteNav.groupMain'] = 'Main';
  next['SiteNav.groupLearn'] = 'Learn';
  next['SiteNav.groupMore'] = 'More';
  next['LandingPage.checkOutListeners'] = 'Meet our listeners';
  next['LandingPage.featuredKicker'] = 'Featured';
  next['LandingPage.featuredTitle'] = 'Featured Listeners';
  next['LandingPage.becomeListener'] = 'Earn $ as a listener';
  next['LandingPage.searchPlaceholder'] = 'A name, or whatever’s on your mind';
  next['LandingPage.trustLine'] = 'For adults 18+ · Conversations stay on the site';
  next['LandingPage.searchTrust'] = 'For adults 18+ · Conversations stay on the site';
  next['LandingPage.filterFormatText'] = 'Text';
  next['LandingPage.findHint'] = 'Browse 15-minute talks.';
  next['DashboardPage.availableNowNotOwner'] =
    "You're signed in as a different account than this Listening profile. Sign out and sign in as that listener, then try Available Now again.";
  next['DashboardPage.availableNowTitle'] = 'Available Now';
  next['DashboardPage.availableNowLede'] =
    "Off until you turn it on. Turn it on when you're ready for Start now requests. It stays on until you turn it off or log out (or after 2 hours away if text alerts are off).";
  next['StartNowPanel.ownListing'] =
    "You’re showing as available now. You stay available until you turn Available Now off or log out (or are away for 2 hours with text alerts off).";
  next['DashboardPage.availableNowActive'] =
    "You are available for Start now. Turn this off when you step away. It stays on until you turn it off or log out (or after 2 hours away if text alerts are off).";
  next['DashboardPage.availableNowInactive'] =
    "Offline. You're Available during your scheduled work hours. Adjust your hours to change that. People can still book a later time.";
  next['ProfilePage.reviewsFromListenersTitle'] = 'Reviews from listeners ({count})';
  next['ProfilePage.about'] = 'About';
  next['ProfilePage.topics'] = 'Topics';
  next['ProfilePage.memberSince'] = 'Member since {date}';
  next['ProfilePage.sessionsCompleted'] =
    '{count, plural, one {# session completed} other {# sessions completed}}';
  next['ProfilePage.emptyState'] =
    'No extra details yet. This talker hasn’t added more about themselves.';
  next['ProfilePage.desktopHeading'] = '{name}';
  next['ProfileSettingsForm.imageUploadBlocked'] =
    "This photo can't be used. Please choose a different one.";
  next['ProfileSettingsForm.imageUploadUnavailable'] = "Couldn't check this photo, try again.";
  next['ProfileSettingsForm.communityGuidelines'] =
    "Please reword this. It doesn't meet our Community Guidelines.";
  next['EditListingPhotosForm.imageUploadBlocked'] =
    "This photo can't be used. Please choose a different one.";
  next['EditListingPhotosForm.imageUploadUnavailable'] = "Couldn't check this photo, try again.";
  next['EditListingDetailsForm.communityGuidelines'] =
    "Please reword this. It doesn't meet our Community Guidelines.";
  delete next['ProfilePage.reviewsAsACustomerTitle'];
  delete next['ProfilePage.reviewsFromMyCustomersTitle'];
  next['ListingPage.reviewsTitle'] = 'Reviews ({count})';
  next['LandingPage.featuredSeeAll'] = 'See all listeners';
  next['LandingPage.featuredEmpty'] =
    'No featured listeners right now. Search when you want someone to talk with.';
  next['LandingPage.featuredLoading'] = 'Loading featured listeners…';
  next['ListingCard.book'] = 'Book';
  next['ListingCard.chooseName'] = 'Choose {name}';
  next['ListingCard.talkNow'] = 'Start now';
  next['ListingCard.bookLater'] = 'Book for Later';
  next['ListingCard.bookNext'] = 'Book Next Available';
  next['ListingCard.viewAvailability'] = 'View Availability';
  next['ListingCard.viewProfile'] = 'View profile';
  next['ListingProfileBookingCtas.chooseName'] = 'Choose {name}';
  next['TalkerBookingPanel.onlineNow'] = 'Online now';
  next['TalkerBookingPanel.step1'] = 'Step 1';
  next['TalkerBookingPanel.step2'] = 'Step 2';
  next['TalkerBookingPanel.stepWhen'] = 'When would you like to talk with {name}?';
  next['TalkerBookingPanel.stepWhenLater'] = 'Choose a date and time';
  next['TalkerBookingPanel.stepFormat'] = 'Choose your session type';
  next['TalkerBookingPanel.talkNowTitle'] = 'Start now';
  next['TalkerBookingPanel.talkNowLede'] = '{name} is online and available now';
  next['TalkerBookingPanel.talkNowOffline'] = '{name} is not available right now';
  next['TalkerBookingPanel.bookLaterTitle'] = 'Book for Later';
  next['TalkerBookingPanel.bookLaterLede'] = 'Choose a date and time that works for you';
  next['TalkerBookingPanel.chooseName'] = 'Choose {name}';
  next['TalkerBookingPanel.ctaChat'] = 'Start my text chat with {name}';
  next['TalkerBookingPanel.ctaVoice'] = 'Start my voice call with {name}';
  next['TalkerBookingPanel.ctaVideo'] = 'Start my video call with {name}';
  next['TalkerBookingPanel.ctaPickFormat'] = 'Choose a session type';
  next['TalkerBookingPanel.ctaLater'] = 'Choose a time';
  next['TalkerBookingPanel.ctaBookNext'] = 'Book Next Available';
  next['TalkerBookingPanel.ctaViewAvailability'] = 'View Availability';
  next['TalkerBookingPanel.ctaContinueCheckout'] = 'Continue to payment';
  next['TalkerBookingPanel.ctaStartFree'] = 'Start free';
  next['TalkerBookingPanel.inConversation'] = 'In a conversation';
  next['TalkerBookingPanel.inConversationNext'] = 'In a conversation • Next available at {time}';
  next['TalkerBookingPanel.trustSecureTitle'] = 'Secure & Safe';
  next['TalkerBookingPanel.trustSecureBody'] = 'Secure card payments';
  next['TalkerBookingPanel.trustHoldTitle'] = "You won't be charged now";
  next['TalkerBookingPanel.trustHoldBody'] = 'Not charged until after the session';
  next['TalkerBookingPanel.trustVerifyTitle'] = '18+ verified';
  next['TalkerBookingPanel.trustVerifyBody'] = 'Every Listener and Talker is age-verified';
  next['ListingBookingPage.viewProfile'] = 'View profile';
  next['ListingBookingPage.close'] = 'Close';
  next['LandingPage.featuredEmptyAvailableNow'] =
    'No featured listeners are available now. Choose Any time to browse everyone you can Book a time with.';
  next['LandingPage.featuredEmptyFormat'] =
    'No featured listeners offer that format. Choose Any, or search all listeners.';
  next['MainPanelHeader.anyTime'] = 'Any time';
  next['MainPanelHeader.availableNow'] = 'Online';
  next['MainPanelHeader.availableNowFirst'] = 'Any time';
  next['MainPanelHeader.availableNowFirstHint'] =
    'Everyone you can search — book a time, or Start now if they are online.';
  next['MainPanelHeader.availableNowOnly'] = 'Online';
  next['MainPanelHeader.availableNowOnlyHint'] =
    'Only listeners who turned on Available Now. Their cards show Start now.';
  next['MainPanelHeader.onlineNowResults'] = 'Only listeners who are available now.';
  next['MainPanelHeader.availableToTalkNow'] = 'Available to talk now';
  next['MainPanelHeader.readyToListen'] =
    '{count, number} {count, plural, one {Listener is} other {Listeners are}} ready to listen';
  next['MainPanelHeader.seeAllListeners'] = 'See all Listeners to book a time';
  next['SortBy.recommended'] = 'Recommended';
  next['SortBy.newListeners'] = 'New Listeners';
  next['SortBy.highestRated'] = 'Highest rated';
  next['SearchResultsPanel.noOnlineNow'] =
    'No listeners are online. Switch to Any time to see people you can Book a time with.';
  next['SearchResultsPanel.showAnyTime'] = 'See Any time';
  next['LandingPage.sessionHighlight'] = 'Book or join a {minutes}-minute session today';
  next['LandingPage.priceTextLabel'] = 'Text';
  next['LandingPage.priceTextAmount'] = '$1–$50';
  next['LandingPage.priceVoiceLabel'] = 'Voice';
  next['LandingPage.priceVoiceAmount'] = '$5–$50';
  next['LandingPage.priceVideoLabel'] = 'Video';
  next['LandingPage.priceVideoAmount'] = '$5–$50';
  next['LandingPage.pricePer'] = 'Per {minutes} min';
  next['LandingPage.stillLifeAlt'] =
    'A mug, dried flowers, and a lit candle on a sunlit windowsill';
  next['LandingPage.heroPhotoAlt'] =
    'A split-screen remote conversation: a woman in bed talks on a laptop, and a woman in a cafe listens on her phone.';
  next['LandingPage.howStep2Body'] =
    'Text, voice or video at the listener’s own prices. Pick a time that’s convenient.';
  next['LandingPage.howStep3Body'] =
    'Join the way you chose. Chat stays in text. Voice is a voice. Video is a face and a voice.';
  next['ContentPage.howItWorks.step3Body'] =
    'Join the way you chose. Chat stays in text. Voice is a voice. Video is a face and a voice.';
  next['GuidePage.chat-voice-video.p1'] =
    'Chat stays in text. Voice is a voice. Video is a face and a voice.';
  next['LandingPage.becomeHint'] = 'Help others feel heard.';
  next['LoginForm.emailRequired'] = 'Enter your email.';
  next['LoginForm.passwordRequired'] = 'Enter your password.';
  next['LoginForm.emailLabel'] = 'Email';
  next['SignupForm.emailLabel'] = 'Email';
  next['SignupForm.firstNameLabel'] = 'First name';
  next['SignupForm.lastNameLabel'] = 'Last name';
  next['SignupForm.passwordLabel'] = 'Password';
  next['SignupForm.emailPlaceholder'] = 'jane.doe@example.com';
  next['AuthenticationPage.signupTitle'] = 'Create your account';
  next['AuthenticationPage.signupLede'] = 'Join a kinder, more connected community. You must be 18 or older.';
  next['LoginForm.emailPlaceholder'] = 'jane.doe@example.com';
  next['LoginForm.passwordLabel'] = 'Password';
  next['LoginForm.passwordPlaceholder'] = 'Enter your password…';
  next['LoginForm.logIn'] = 'Log in';
  next['LoginForm.keepLoggedIn'] = 'Keep me logged in';
  next['LoginForm.forgotPassword'] = 'Forgot your password?';
  next['LoginForm.showPassword'] = 'Show password';
  next['LoginForm.hidePassword'] = 'Hide password';
  next['AuthenticationPage.welcomeBack'] = 'Welcome Back';
  next['AuthenticationPage.loginLede'] = 'Log in to your account.';
  next['AuthenticationPage.lifestyleHeadline'] = 'Sometimes you just need someone to talk to.';
  next['AuthenticationPage.lifestyleBulletConversations'] = 'Real conversations';
  next['AuthenticationPage.lifestyleBulletCommunity'] = 'A kinder community';
  next['AuthenticationPage.lifestyleBulletTerms'] = 'On your terms';
  next['AuthenticationPage.mugLine'] = 'Good people. Great conversations.';
  next['AuthenticationPage.mobileTagline'] = 'Listen. Care. Connect.';
  next['AuthenticationPage.noAccount'] = 'Don’t have an account?';
  next['AuthenticationPage.or'] = 'OR';
  next['AuthenticationPage.loginWithGoogle'] = 'Continue with Google';
  next['AuthenticationPage.loginWithApple'] = 'Continue with Apple';
  next['AuthenticationPage.loginLinkText'] = 'Log in';
  next['AuthenticationPage.signupLinkText'] = 'Sign up';
  next['FieldSelectUserType.label'] = 'How will you use the site?';
  next['FieldSelectUserType.required'] = 'Choose how you will use the site.';
  next['FieldSelectUserType.optionTalk'] = 'I want to talk';
  next['FieldSelectUserType.optionListen'] = 'I want to listen';
  next['FieldSelectUserType.hintClient'] = 'Book time with someone who will hear you out.';
  next['FieldSelectUserType.hintListener'] =
    'You earn 70% of the rates you set. Set your own rate for text, voice, or video.';
  next['LandingPage.searchLabel'] = 'Search listeners';
  next['LandingPage.becomeListener'] = 'Earn $ as a listener';
  next['LandingPage.signUpToBrowse'] = 'Sign up to browse listeners';
  next['LandingPage.signInToBrowse'] = 'Sign in';
  next['TopbarSearchForm.placeholder'] = 'Name or topic';
  next['SearchHistory.recent'] = 'Recent';
  next['SearchHistory.saved'] = 'Saved';
  next['SearchHistory.menuLabel'] = 'Recent and saved searches';
  next['SearchHistory.emptyRecent'] = 'No recent searches yet.';
  next['SearchHistory.emptySaved'] = 'Star a search to save it.';
  next['SearchHistory.save'] = 'Save';
  next['SearchHistory.unsave'] = 'Saved';
  next['TopbarDesktop.screenreader.search'] = 'Search listeners';
  next['TopbarDesktop.createListing'] = 'Become a listener';
  next['TopbarMobileMenu.newListingLink'] = 'Become a listener';
  next['TopbarDesktop.yourListingsLink'] = 'Listening profile';
  next['TopbarMobileMenu.yourListingsLink'] = 'Listening profile';
  next['UserNav.yourListings'] = 'Listening profile';
  next['ManageListingsPage.title'] = 'Your Listening profile';
  next['ManageListingsPage.noResults'] = "You haven't set up a Listening profile yet.";
  next['ManageListingsPage.becomeListener'] = 'Become a listener';
  next['ManageListingsPage.createListing'] = 'Create Listening profile';
  next['ManageListingsPage.loadingOwnListings'] = 'Loading your Listening profile…';
  next['ManageListingsPage.youHaveListings'] =
    'You have {count} {count, plural, one {Listening profile} other {Listening profiles}}';
  next['EditListingPage.titleCreateListing'] = 'Set up your Listening profile | {panelHeading}';
  next['EditListingPage.titleEditListing'] = 'Edit Listening profile | {panelHeading}';
  next['EditListingPage.loadingListingData'] = 'Loading your Listening profile…';
  next['EditListingDetailsPanel.createListingTitle'] = 'Listening profile details';
  next['EditListingDetailsPanel.title'] = 'Listening profile details';
  next['EditListingDetailsForm.title'] = 'Display name';
  next['BecomeListenerProgress.title'] = 'Become a Listener: 1 Verify, 2 Get paid, 3 Profile';
  next['BecomeListenerProgress.payoutLede'] =
    'Link a bank account before you set up the listening profile. Identity is already checked, so this step opens first.';
  next['BecomeListenerProgress.continuePayout'] = 'Continue to Stripe';
  next['BecomeListenerProgress.continueProfile'] = 'Continue to your listening profile';
  next['AccountSetupProgress.talk'] = 'Set up your Talker account';
  next['AccountSetupProgress.listen'] = 'Set up your Listener account';
  next['AccountSetupProgress.both'] = 'Set up Talker and Listener';
  next['CreateProfileWizard.title'] = 'Create your profile';
  next['CreateProfileWizard.lede'] =
    'Tell us a bit about yourself so the right people can connect with you.';
  next['CreateProfileWizard.stepBasic'] = 'Basic info';
  next['CreateProfileWizard.stepAbout'] = 'About you';
  next['CreateProfileWizard.stepTopics'] = 'Listening';
  next['CreateProfileWizard.stepTeach'] = 'Teaching';
  next['CreateProfileWizard.stepFormats'] = 'Formats';
  next['CreateProfileWizard.stepReview'] = 'Review';
  next['CreateProfileWizard.roleListenTitle'] = 'Listener';
  next['CreateProfileWizard.roleListenBody'] = 'I want to listen to someone.';
  next['CreateProfileWizard.roleTalkTitle'] = 'Talker';
  next['CreateProfileWizard.roleTalkBody'] = 'I want to talk to someone.';
  next['CreateProfileWizard.roleLabel'] = 'How will you use the site?';
  next['CreateProfileWizard.roleHint'] =
    'Listener listens. Talker talks. You can enable both on the same account.';
  next['CreateProfileWizard.photoLabel'] = 'Profile photo (optional)';
  next['CreateProfileWizard.photoUpload'] = 'Add photo';
  next['CreateProfileWizard.photoChange'] = 'Change photo';
  next['CreateProfileWizard.photoCropHint'] =
    'After you pick a photo, zoom and drag to frame your face.';
  next['CreateProfileWizard.photoLater'] =
    'You can add a profile photo later. It is never required.';
  next['CreateProfileWizard.displayNameMissingOnStep1'] = 'Display name missing on Step 1';
  next['CreateProfileWizard.aboutMissingOnStep2'] = 'About you missing on Step 2';
  next['CreateProfileWizard.topicsMissingOnStep3'] = 'Topics missing on Step 3';
  next['CreateProfileWizard.formatsMissingOnStep4'] = 'Formats missing on Step 4';
  next['CreateProfileWizard.hoursMissingOnStep4'] = 'Hours missing — add when you are available';
  next['CreateProfileWizard.guidelinesMissing'] = 'Accept the Community Guidelines';
  next['CreateProfileWizard.identityMissing'] = '18+ check missing — complete it to publish';
  next['CreateProfileWizard.payoutsMissing'] = 'Finish Stripe Get verified to publish';
  next['CreateProfileWizard.nextAbout'] = 'Next: About you';
  next['CreateProfileWizard.nextTopics'] = 'Next: Listening';
  next['CreateProfileWizard.nextFormats'] = 'Next: Formats';
  next['CreateProfileWizard.nextHours'] = 'Next: Hours';
  next['CreateProfileWizard.nextReview'] = 'Next: Review';
  next['CreateProfileWizard.back'] = 'Back';
  next['CreateProfileWizard.reviewTitle'] = 'Review your profile';
  next['CreateProfileWizard.reviewLede'] =
    'This is how people will see your public card. You can edit any section.';
  next['CreateProfileWizard.advancedEditHint'] =
    'After you publish, Photos, Pricing, and Availability stay available for later edits.';
  next['CreateProfileWizard.finish'] = 'Finish and create profile';
  next['CreateProfileWizard.topicsRequired'] = 'Pick at least one topic before you finish.';
  next['CreateProfileWizard.fromPrice'] = 'From {price}';
  next['CreateProfileWizard.stepOf'] = 'Step {step} of {total}';
  next['CreateProfileWizard.hoursTitle'] = 'Hours';
  next['CreateProfileWizard.edit'] = 'Edit';
  next['EditListingDetailsForm.description'] = 'About you';
  next['EditListingDetailsForm.titlePlaceholder'] = 'Create a unique name to be seen as';
  next['EditListingDetailsForm.titleRequired'] = 'You need to add a display name.';
  next['EditListingDetailsForm.titleMinLength'] = 'You need to add a display name.';
  next['EditListingDetailsForm.createListingDraftError'] =
    "Couldn't save your Listening profile. Please try again.";
  next['EditListingDetailsForm.updateFailed'] =
    "Couldn't save your Listening profile. Please try again.";
  next['EditListingDetailsForm.featuredTitle'] = 'How to get Featured';
  next['EditListingDetailsForm.featuredLede'] =
    'Featured is a paid homepage strip — ${monthly}/month or ${yearly}/year. It puts your Listening profile in the Featured listeners row on the homepage. It does not buy a higher star rating or a better search rank.';
  next['EditListingDetailsForm.featuredPath'] =
    'After you publish, open Dashboard to turn Featured on.';
  next['EditListingDetailsForm.featuredCta'] = 'Open Dashboard for Featured';
  next['EditListingDetailsForm.featuredPathDraft'] =
    'Publish your profile first. Then open Dashboard to turn Featured on.';
  next['EditListingDetailsForm.featuredCtaDisabled'] =
    'Featured controls open from Dashboard after you publish.';
  next['AlreadyListenerNotice.heading'] = "You're already a listener";
  next['AlreadyListenerNotice.body'] =
    'You already have a Listening profile. Open it to make changes, or go to Dashboard for Featured and availability.';
  next['AlreadyListenerNotice.viewProfile'] = 'View my Listening profile';
  next['AlreadyListenerNotice.editProfile'] = 'Edit Listening profile';
  next['AlreadyListenerNotice.dashboard'] = 'Listening dashboard';
  next['StripeConnectAccountForm.accountsV1SupportNeeded'] =
    'Payouts can’t be set up right now. We’re fixing this on our side, so please try again later. Your draft is saved.';
  next['StripeConnectAccountForm.transfersNeeded'] =
    'Stripe still needs transfers on this payout account. Tap Get verified again. Book and Start now stay off until that is on.';
  next['StripeConnectAccountForm.connectedNextStep'] =
    'Save is only for the first country step. Tap Get verified and finish payout details. Publishing waits until Stripe is verified.';
  next['StripeConnectAccountForm.publishListeningProfile'] = 'Publish Listening profile';
  next['EditListingWizard.publishNeedsStripe'] =
    'You can keep saving this draft. Publishing a Listening profile waits until you finish Get verified and payout details — Stripe must be ready to take charges and send payouts.';
  next['StripeConnectAccountStatusBox.verificationNeededText'] =
    'Finish Get verified and payout details before you publish a Listening profile. You can keep saving this draft.';
  next['ProfileMenu.profile'] = 'Profile';
  next['ProfileMenu.yourProfiles'] = 'Your profiles';
  next['TopbarDesktop.yourListeningProfile'] = 'Profile';
  next['TopbarDesktop.yourProfile'] = 'Profile';
  next['TopbarDesktop.yourProfiles'] = 'Your profiles';
  next['TopbarMobileMenu.yourListeningProfile'] = 'Profile';
  next['TopbarMobileMenu.yourProfile'] = 'Profile';
  next['TopbarMobileMenu.yourProfiles'] = 'Your profiles';
  // Hosted leftovers must not pile View / Edit / Listening profile into the menu.
  next['TopbarDesktop.viewListeningProfile'] = 'Profile';
  next['TopbarDesktop.viewTalkingProfile'] = 'Profile';
  next['TopbarMobileMenu.viewListeningProfile'] = 'Profile';
  next['TopbarMobileMenu.viewTalkingProfile'] = 'Profile';
  next['UserNav.yourProfile'] = 'Profile';
  next['UserNav.yourProfiles'] = 'Your profiles';
  next['UserNav.profileSettings'] = 'Profile';
  next['DashboardPage.bothTitle'] = 'Listening dashboard';
  next['TopbarDesktop.listenerDashboard'] = 'Dashboard';
  next['TopbarDesktop.yourSessions'] = 'Dashboard';
  next['TopbarDesktop.home'] = 'Home';
  next['TopbarDesktop.more'] = 'More';
  next['TopbarDesktop.myProfile'] = 'My profile';
  next['TopbarDesktop.dashboard'] = 'Dashboard';
  next['TopbarDesktop.inbox'] = 'My Bookings';
  next['TopbarMobileMenu.listenerDashboard'] = 'Dashboard';
  next['TopbarMobileMenu.yourSessions'] = 'Dashboard';
  next['TopbarMobileMenu.dashboard'] = 'Dashboard';
  next['DashboardPage.bothKicker'] = 'Talk and listen';
  next['DashboardPage.listenerKicker'] = 'Listening today';
  next['DashboardPage.customerKicker'] = 'Talking today';
  next['DashboardPage.bothHeading'] = 'Listening & Talking';
  next['DashboardPage.bothLede'] = 'Your listening and talking tools.';
  next['DashboardPage.bothRoleLine'] = 'You’re a Listener and a Talker.';
  next['DashboardPage.bothTagline'] = 'Different conversations. A kinder world.';
  next['DashboardPage.talkerMode'] = 'Talker Mode';
  next['DashboardPage.listenerMode'] = 'Listener Mode';
  next['DashboardPage.asTalker'] = 'As a Talker';
  next['DashboardPage.asListener'] = 'As a Listener';
  next['DashboardPage.statTalkerRequests'] =
    '{count, plural, one {New Talker request} other {New Talker requests}}';
  next['DashboardPage.statListenerUpcoming'] =
    '{count, plural, one {Upcoming Listener session} other {Upcoming Listener sessions}}';
  next['DashboardPage.statThisWeek'] = 'This Week';
  next['DashboardPage.statYourRating'] = 'Your Rating';
  next['DashboardPage.todayScheduleTitle'] = 'Today’s schedule';
  next['DashboardPage.viewCalendar'] = 'My Bookings';
  next['DashboardPage.viewOnCalendar'] = 'View on calendar';
  next['DashboardPage.viewAll'] = 'View all';
  next['DashboardPage.todayScheduleEmpty'] =
    'Nothing on the calendar today. Later bookings show under Upcoming.';
  next['DashboardPage.recentActivityTitle'] = 'Recent activity';
  next['DashboardPage.recentActivityEmpty'] =
    'Nothing new yet. Your next session will show up here.';
  next['DashboardPage.scheduleTalker'] = 'Talker session';
  next['DashboardPage.scheduleListener'] = 'Listener session';
  next['DashboardPage.scheduleMinutes'] = '{minutes} minutes';
  next['DashboardPage.talkerToolsTitle'] = 'Your Talker tools';
  next['DashboardPage.listenerToolsTitle'] = 'Your Listener tools';
  next['DashboardPage.goTalkerMode'] = 'Go to Talker Mode';
  next['DashboardPage.goListenerMode'] = 'Go to Listener Mode';
  next['DashboardPage.activityCompletedListen'] = 'Session completed with {name}';
  next['DashboardPage.activityCompletedTalk'] = 'You finished a session with {name}';
  next['DashboardPage.bothMotto'] = 'Sometimes you just need someone to talk to.';
  next['DashboardPage.bothMottoSub'] = 'Listen. Care. Connect.';
  next['DashboardPage.bothValueAccountTitle'] = 'One account';
  next['DashboardPage.bothValueAccount'] = 'Two ways to make a difference. Switch anytime.';
  next['DashboardPage.bothValueFlexTitle'] = 'Flexibility';
  next['DashboardPage.bothValueFlex'] = 'Listen when you can. Talk when you want.';
  next['DashboardPage.bothValueCommunityTitle'] = 'Same community';
  next['DashboardPage.bothValueCommunity'] = 'A kinder, more connected world — together.';
  next['DashboardPage.listenerTitle'] = 'Listening dashboard';
  next['DashboardPage.listenerHeading'] = 'Listening dashboard';
  next['DashboardPage.customerTitle'] = 'Talking dashboard';
  next['DashboardPage.customerHeading'] = 'Talking dashboard';
  next['DashboardPage.listenerLede'] = 'Your listening tools.';
  next['DashboardPage.customerLede'] = 'Find a listener when you’re ready.';
  next['DashboardPage.welcomeListeningRequests'] =
    'You have {count, plural, one {# new session} other {# new sessions}} waiting.';
  next['DashboardPage.welcomeListeningToday'] =
    'You have {count, plural, one {# session} other {# sessions}} on today’s calendar.';
  next['DashboardPage.welcomeListeningReady'] = 'Start sessions now when you’re ready.';
  next['DashboardPage.welcomeTalkingUpcoming'] =
    'You have {count, plural, one {# upcoming session} other {# upcoming sessions}}.';
  next['DashboardPage.welcomeTalkingReady'] = 'Find a listener when you’re ready.';
  next['DashboardPage.listeningTab'] = 'Listening';
  next['DashboardPage.talkingTab'] = 'Talking';
  next['DashboardPage.listeningSectionTitle'] = 'Listening';
  next['DashboardPage.listeningSectionLede'] =
    'Available Now, Featured, your Listening profile, and listener bookings.';
  next['DashboardPage.talkingSectionTitle'] = 'Talking';
  next['DashboardPage.talkingSectionLede'] = 'Find a listener, your bookings, and Your profile.';
  next['FavoriteButton.favorite'] = 'Favorite';
  next['FavoriteButton.unfavorite'] = 'Unfavorite';
  next['DashboardPage.favoritesTitle'] = 'Your Favorites';
  next['DashboardPage.favoritesLede'] = '';
  next['DashboardPage.favoritesEmpty'] = "You haven't added any favorites yet.";
  next['FavoritesPage.listenerNote'] =
    "Favorites are listeners you've saved as a talker. Switch to Talker to see them.";
  next['DashboardPage.hubTitle'] = 'From this dashboard';
  next['DashboardPage.jumpNav'] = 'On this page';
  next['DashboardPage.jumpEarnings'] = 'Earnings';
  next['DashboardPage.jumpToday'] = 'Today';
  next['DashboardPage.jumpAvailableNow'] = 'Available Now';
  next['DashboardPage.liveTitle'] = 'Join Session';
  next['DashboardPage.incomingNowJoin'] = 'Join Session';
  next['DashboardPage.jumpIncoming'] = 'Incoming';
  next['DashboardPage.jumpCalendar'] = 'Availability';
  next['DashboardPage.jumpFeatured'] = 'Featured';
  next['DashboardPage.account'] = 'Account';
  next['DashboardPage.hubViewListeningLede'] = 'See how people see your profile.';
  next['DashboardPage.hubEditListeningLede'] = 'Photo, topics, and how you listen.';
  next['DashboardPage.hubInboxSalesLede'] = 'Bookings and messages from the people you meet.';
  next['DashboardPage.hubPayoutsLede'] = 'Stripe payouts and Get verified.';
  next['DashboardPage.hubAccountLede'] = 'Notifications, email, and texts.';
  next['DashboardPage.hubPromoteLede'] = 'Share the whole site — not a profile.';
  next['DashboardPage.findListener'] = 'Find someone to talk to or learn from';
  next['DashboardPage.hubFindLede'] = 'Search by topic and start a talk.';
  next['DashboardPage.hubInboxOrdersLede'] = 'Your bookings and session messages.';
  next['DashboardPage.hubViewTalkingLede'] = 'See the profile listeners see.';
  next['DashboardPage.hubEditTalkingLede'] = 'Name, photo, and what you want to talk about.';
  next['DashboardPage.hubPaymentsLede'] = 'The card you use to book talks.';
  next['DashboardPage.hubBecomeLede'] = 'Earn by listening — start a Listening profile.';
  next['DashboardPage.view'] = 'View';
  next['DashboardPage.edit'] = 'Edit';
  // Hosted leftovers must never show “Your Listening profile” next to View.
  next['DashboardPage.yourListeningProfile'] = 'Edit';
  next['DashboardPage.listeningProfileLink'] = 'Your Listening profile';
  next['DashboardPage.listeningProfileLinkLede'] =
    'View or edit how people see you — photo, About you, topics, and styles.';
  next['DashboardPage.editListeningProfile'] = 'Edit';
  next['DashboardPage.viewListeningProfile'] = 'View';
  next['DashboardPage.yourProfile'] = 'Edit';
  next['DashboardPage.viewYourProfile'] = 'View';
  next['DashboardPage.viewYourProfileAria'] = 'View your profile';
  next['DashboardPage.editYourProfile'] = 'Edit';
  next['YourProfilePage.title'] = 'Your profile';
  next['YourProfilePage.heading'] = 'Your profile';
  next['YourProfilePage.listeningTab'] = 'Listening';
  next['YourProfilePage.talkingTab'] = 'Talking';
  next['YourProfilePage.listeningHeading'] = 'Listening profile';
  next['YourProfilePage.talkingHeading'] = 'Talking profile';
  next['YourProfilePage.hubLede'] =
    'Open a profile to change your photo, topics, or how you show up.';
  next['YourProfilePage.listeningLede'] =
    'Photo, topics, and how you listen. Open the editor to change this Listening profile.';
  next['YourProfilePage.listeningEmpty'] = 'You have not set up a Listening profile yet.';
  next['YourProfilePage.listeningBusy'] =
    'This page is busy. We could not load your Listening profile.';
  next['YourProfilePage.listeningBusyCta'] = 'Try again';
  next['YourProfilePage.listeningLoading'] = 'Loading your Listening profile…';
  next['YourProfilePage.view'] = 'View';
  next['YourProfilePage.edit'] = 'Edit';
  next['YourProfilePage.viewListeningProfile'] = 'View';
  next['YourProfilePage.editListeningProfile'] = 'Edit';
  next['YourProfilePage.editListeningCta'] = 'Edit Listening Profile';
  next['YourProfilePage.editTalkingCta'] = 'Talking preferences (optional)';
  next['YourProfilePage.createListeningProfile'] = 'Set up your Listening profile';
  next['YourProfilePage.viewYourProfile'] = 'View';
  next['YourProfilePage.editYourProfile'] = 'Edit';
  next['YourProfilePage.talkingLede'] =
    'Name, photo, and the public profile people see when they talk with you.';
  next['YourProfilePage.scheduleTitle'] = 'Set your schedule';
  next['YourProfilePage.scheduleLede'] =
    'Set weekly hours so people can Book a time. They still wait for your confirmation.';
  next['YourProfilePage.scheduleCta'] = 'Set your schedule';
  next['YourProfilePage.scheduleSetup'] = 'Set your schedule';
  next['TopicPicker.addTopic'] = '+ Add a topic';
  next['ProfileEditFields.backToMyProfile'] = 'Back to Account';
  next['ProfileEditFields.teachTitle'] = 'Set up teaching';
  next['ProfileEditFields.teachLede'] = 'Tell learners what you teach on Sweet Skillz and how your lessons work.';
  next['ProfileEditFields.teachBio'] = 'About you';
  next['ProfileEditFields.teachBioPlaceholder'] = 'What you teach, how you like to teach it, and who you enjoy helping.';
  next['ProfileEditFields.listeningTitle'] = 'Edit Listening Profile';
  next['ProfileEditFields.listeningLede'] =
    'Show how you listen, only the topics you’re confident and comfortable talking about, and your style.';
  next['ProfileEditFields.talkingTitle'] = 'Talking preferences (optional)';
  next['ProfileEditFields.talkingLede'] = 'Show what you’re looking for and how you like to talk.';
  next['ProfileEditFields.photoLabel'] = 'Profile photo (optional)';
  next['ProfileEditFields.photoHelper'] = 'A clear, friendly photo helps build trust.';
  next['ProfileEditFields.addPhoto'] = 'Add photo';
  next['ProfileEditFields.changePhoto'] = 'Change photo';
  next['ProfileEditFields.removePhoto'] = 'Remove';
  next['ProfileEditFields.displayNameHelper'] =
    'This is how people will see you here.';
  next['PrivacyTip.screenName'] =
    "Pick a screen name, not your real name. For your privacy, don't share your full name, workplace, or where you live.";
  next['PrivacyTip.bio'] =
    "For your privacy, don't put your full name, workplace, contact details, or where you live in your bio.";
  next['ProfileEditFields.listeningBio'] = 'Listening bio';
  next['ProfileEditFields.listeningBioPlaceholder'] =
    'I’m a caring and open-minded listener who believes everyone deserves to feel heard.';
  next['ProfileEditFields.listeningTopics'] = 'Topics you’re confident and comfortable talking about';
  next['ProfileEditFields.listeningStyleLegend'] = 'Your Listening Style';
  next['ProfileEditFields.availabilityNote'] = 'Availability note (optional)';
  next['ProfileEditFields.availabilityNotePlaceholder'] =
    'I usually reply quickly and often am available in the evenings.';
  next['ProfileEditFields.saveListening'] = 'Save Listening Profile';
  next['ProfileEditFields.talkingTopics'] = 'Topics you’re interested in';
  next['ProfileEditFields.formatLegend'] = 'Preferred conversation format';
  next['ProfileEditFields.format.chat'] = 'Chat (text)';
  next['ProfileEditFields.format.voice'] = 'Voice call';
  next['ProfileEditFields.format.video'] = 'Video call';
  next['ProfileEditFields.sessionLengthLegend'] = 'Preferred session length';
  next['ProfileEditFields.sessionLengthValue'] = '{minutes} minutes';
  next['ProfileEditFields.sessionLengthHint'] =
    'Every session is {minutes} minutes.';
  next['ProfileEditFields.talkingNote'] = 'Anything else? (optional)';
  next['ProfileEditFields.talkingNotePlaceholder'] =
    'Feel free to ask me anything. I’m here for a good conversation.';
  next['ProfileEditFields.saveTalking'] = 'Save Talking Profile';
  next['ProfileSettingsForm.saved'] = 'Saved. Taking you to the next step…';
  next['EditListingDetailsForm.saved'] = 'Saved. Taking you to the next step…';
  next['YourProfilePage.talkingSaved'] = 'Talking profile saved.';
  next['YourProfilePage.listeningSaved'] = 'Listening profile saved.';
  next['ProfileSettingsForm.bioLabel'] = 'What are you looking for in a conversation?';
  next['ProfileSettingsForm.bioPlaceholder'] =
    'A friendly, open-minded listener to chat with about everyday life, ideas, and advice.';
  next['ProfileSettingsForm.saveChanges'] = 'Save Talking Profile';
  next['ProfileSettingsForm.displayNameInfo'] =
    'This is how people will see you here.';
  next['ProfileSettingsPage.heading'] = 'Your profile';
  next['ProfileSettingsPage.title'] = 'Your profile';
  next['ProfileSettingsPage.viewProfileLink'] = 'View your profile';
  next['DashboardPage.inboxSales'] = 'My Bookings';
  next['DashboardPage.inboxOrders'] = 'My Bookings';
  next['DashboardPage.openInbox'] = 'Open My Bookings';
  next['DashboardPage.payouts'] = 'Payouts';
  next['DashboardPage.paymentMethods'] = 'Payment methods';
  next['DashboardPage.paymentHistory'] = 'Payment history';
  next['LayoutWrapperAccountSettingsSideNav.manageAccountTabTitle'] = 'Manage account';
  next['LayoutWrapperAccountSettingsSideNav.profileTabTitle'] = 'Profile';
  next['LayoutWrapperAccountSettingsSideNav.contactDetailsTabTitle'] = 'Notifications & contact';
  next['AccountProfilePage.title'] = 'Profile';
  next['AccountProfilePage.heading'] = 'Profile';
  next['AccountProfilePage.lede'] =
    'Your Listening and Talking profiles. Edit opens the same Profile hub as the menu.';
  next['AccountProfilePage.ledeListening'] =
    'Your Listening profile. Edit opens the same Profile hub as the menu.';
  next['AccountProfilePage.ledeTalking'] =
    'Your Talking profile. Edit opens the same Profile hub as the menu.';
  next['ChooseAccountRolePage.setupTitle'] = 'Finish your profile';
  next['ChooseAccountRolePage.setupTitleBoth'] = 'Finish your profiles';
  next['ChooseAccountRolePage.setupLedeListening'] =
    'You can listen now. Set up your Listening profile so talkers can find you.';
  next['ChooseAccountRolePage.setupLedeTalking'] =
    'Your Talking profile is optional. You can book a listener right away — add this anytime.';
  next['ChooseAccountRolePage.setupLedeBoth'] =
    'You can listen and talk. Set up the profiles you still need — you can finish the other later.';
  next['ChooseAccountRolePage.setupListeningCta'] = 'Set up Listening profile';
  next['ChooseAccountRolePage.setupTalkingCta'] = 'Set up Talking profile';
  next['ChooseAccountRolePage.setupSkip'] = 'Skip for now';
  next['ManageAccountPage.lede'] = 'Your account type and whether you’re available to listen.';
  next['PasswordChangePage.heading'] = 'Password settings';
  next['PasswordChangePage.title'] = 'Password';
  next['PasswordChangePage.lede'] =
    'You don’t need your current password. We’ll email you a reset link.';
  next['PasswordChangeForm.emailHelp'] = 'We’ll send a reset link to {email}.';
  next['PasswordChangeForm.sendResetEmail'] = 'Send reset email';
  next['PasswordChangeForm.resendPasswordLinkText'] = 'Send again';
  next['PasswordChangeForm.resetPasswordLinkSent'] = 'We emailed a reset link to {email}.';
  next['PasswordChangeForm.textResetNote'] =
    'Reset by text isn’t available yet. For now we’ll email you a reset link.';
  next['PasswordChangeForm.textResetNoteOptIn'] =
    'Texts are on for this account. Password reset links still go by email.';
  next['LayoutWrapperAccountSettingsSideNav.paymentsTabTitle'] = 'Payment & payouts';
  next['ContactDetailsPage.title'] = 'Notifications & contact';
  next['ContactDetailsPage.heading'] = 'Notifications & contact';
  next['ContactDetailsPage.lede'] =
    'How we reach you — email, texts, and which booking alerts you want.';
  next['ContactDetailsForm.contactTitle'] = 'Contact method';
  next['ContactDetailsForm.contactHelp'] =
    'Change the email on this account. Add a mobile if you want texts. Your number stays in Account settings — it is never shown on a profile or to the other person.';
  next['ContactDetailsForm.emailNotifyTitle'] = 'Email notifications';
  next['ContactDetailsForm.emailNotifyHelp'] =
    'Alerts we send for bookings, messages, and Join. Password, verification, and booking receipt emails always arrive.';
  next['ContactDetailsForm.notifyAccepted'] = 'Session confirmed';
  next['ContactDetailsForm.notifyMessage'] = 'New messages';
  next['ContactDetailsForm.notifySession'] = 'Session starting / Join';
  next['ContactDetailsForm.smsKindHelp'] =
    'Which texts to send once you opt in. We save these even if SMS is not configured on this host yet.';
  next['SignupForm.smsHelp'] =
    'Optional — skip this if you prefer. We can text you about bookings and sessions. You can add or change this later in Account settings → Notifications & contact. The number stays in your account. It is never shown on your profile, chat, or to the other person.';
  next['LayoutWrapperAccountSettingsSideNav.paymentMethodsTabTitle'] = 'Payment methods';
  next['StripePayoutPage.title'] = 'Payments';
  next['StripePayoutPage.heading'] = 'Payment & payouts';
  next['StripePayoutPage.lede'] =
    'Set up payouts and cards in one place. You only see the sections that apply to your account.';
  next['StripePayoutPage.retryHint'] = 'Payout details are taking a moment to load.';
  next['StripePayoutPage.retry'] = 'Retry';
  next['StripePayoutPage.payoutsHeading'] = 'Payouts';
  next['StripePayoutPage.payoutsLede'] =
    'Set up Stripe, update payout details, and Get verified so you can get paid for sessions.';
  next['StripePayoutPage.payoutsTiming'] =
    'After a session completes, your payout usually reaches your bank in a few business days. Stripe may email you about the payout.';
  next['StripePayoutPage.paymentMethodsHeading'] = 'Payment methods';
  next['StripePayoutPage.paymentMethodsLede'] =
    'Add, replace, or remove the card you use to book talks. One card is saved as your default.';
  next['StripePayoutPage.noCardYet'] = 'No card saved yet. Add one to book talks.';
  next['SavedCardDetails.replaceCard'] = 'Replace card';
  next['SavedCardDetails.useDifferentCard'] = 'Use a different card';
  next['SavedCardDetails.keepCard'] = 'Keep this card';
  next['SavedCardDetails.addCard'] = 'Add card';
  next['PaymentMethodsForm.addCard'] = 'Add card';
  next['PaymentMethodsForm.replaceAndSave'] = 'Save new card';
  next['PaymentMethodsForm.replaceHint'] =
    'Your current card stays until the new one saves. Then this card becomes your default.';
  next['StripePayoutPage.loadingPaymentMethods'] = 'Loading your card…';
  next['StripePayoutPage.paymentHistoryHeading'] = 'Payment history';
  next['StripePayoutPage.paymentHistoryLede'] =
    'Holds, charges, hold releases, and refunds after a charge for talks you booked.';
  next['StripePayoutPage.paymentHistoryEmpty'] = 'No payments yet.';
  next['StripePayoutPage.paymentHistoryLoading'] = 'Loading payment history…';
  next['StripePayoutPage.paymentHistoryError'] = 'Couldn’t load payment history. Try again.';
  next['StripePayoutPage.paymentHistorySomeone'] = 'A listener';
  next['StripePayoutPage.paymentHistoryStatusHold'] = 'Hold';
  next['StripePayoutPage.paymentHistoryStatusCharged'] = 'Charged';
  next['StripePayoutPage.paymentHistoryStatusRefunded'] = 'Refunded';
  next['StripePayoutPage.paymentHistoryStatusFailed'] = 'Failed';
  next['StripePayoutPage.paymentHistoryStatusReleased'] = 'Hold released';
  next['StripePayoutPage.paymentHistoryFormatChat'] = 'Text';
  next['StripePayoutPage.paymentHistoryFormatVoice'] = 'Voice';
  next['StripePayoutPage.paymentHistoryFormatVideo'] = 'Video';
  next['DashboardPage.paymentHistory'] = 'Payment history';
  next['StripePayoutPage.taxHeading'] = 'Tax info (1099)';
  next['StripePayoutPage.taxLede'] =
    'Stripe collects your SSN or EIN for year-end 1099s. Update it anytime in Stripe.';
  next['StripePayoutPage.taxNote'] =
    'You’ll add or update this on Stripe — we don’t host a 1099 form here.';
  next['StripePayoutPage.taxStatusSetupFirst'] =
    'Finish Get verified in Payouts first. Stripe will ask for your SSN or EIN in that same flow.';
  next['StripePayoutPage.taxStatusNeeded'] =
    'Stripe still needs your SSN or EIN. Open Stripe to add it.';
  next['StripePayoutPage.taxStatusReady'] =
    'Your tax ID is with Stripe. Update it there if your SSN or EIN changes.';
  next['StripePayoutPage.taxCtaAdd'] = 'Add tax info in Stripe';
  next['StripePayoutPage.taxCtaUpdate'] = 'Update tax info in Stripe';
  next['PaymentMethodsPage.title'] = 'Payment methods';
  next['PaymentMethodsPage.heading'] = 'Payment methods';
  next['DashboardPage.becomeListener'] = 'Become a listener';
  next['AuthenticationPage.creatingAccount'] = 'Creating your account…';
  next['AuthenticationPage.signupFailedFixFields'] =
    'Please fix the highlighted fields and try again.';
  next['AuthenticationPage.signupFailedEmailAlreadyTaken'] =
    'An account with this email address already exists. Try logging in instead.';
  next['SignupForm.heardAboutLabel'] = 'How did you hear about us? (optional)';
  next['SignupForm.heardAboutPlaceholder'] = 'Choose one';
  next['SignupForm.heardAboutRequired'] = 'Tell us how you heard about us.';
  next['SignupForm.heardAboutOtherLabel'] = 'Tell us a bit more';
  next['SignupForm.heardAboutOtherPlaceholder'] = 'A podcast, search, an event…';
  next['SignupForm.heardAboutOtherRequired'] = 'Add a short note.';
  next['SignupForm.heardAbout.instagram'] = 'Instagram';
  next['SignupForm.heardAbout.tiktok'] = 'TikTok';
  next['SignupForm.heardAbout.facebook'] = 'Facebook';
  next['SignupForm.heardAbout.reddit'] = 'Reddit';
  next['SignupForm.heardAbout.friend'] = 'A friend';
  next['SignupForm.heardAbout.other'] = 'Other';
  next['HowToGetBooked.title'] =
    'Earners Playbook: we bring the crowd, you reel them in 💸';
  next['TipsPage.schemaTitle'] = 'Tips & Resources | {marketplaceName}';
  next['TipsPage.schemaDescription'] =
    'Listener and talker tips from How it works, FAQ, Safety, and Community Guidelines.';
  next['TipsPage.kicker'] = 'Tips';
  next['TipsPage.tabListener'] = 'Listener';
  next['TipsPage.tabTalker'] = 'Talker';
  next['TipsPage.extendBody'] =
    'Ask to extend near the end. If your Listener accepts, your saved card is charged and you both get 15 more minutes.';
  next['TipsPage.browseListeners'] =
    "Browse Listener profiles and choose who you'd like to talk with, based on their profile and availability.";
  next['TipsPage.platonic'] =
    'Conversations here are strictly platonic. Dating, sexual content, and adult content are not allowed.';
  next['TipsPage.reportReview'] =
    'We review every report and may restrict or remove accounts that break our community guidelines.';
  next['TipsPage.leaveReview'] =
    'Leave a review after your session. It helps keep our community safe and supportive.';
  next['ListenerEarnings.title'] = 'Earnings';
  next['ListenerEarnings.lede'] =
    'After a session completes, your payout usually reaches your bank in a few business days. Stripe may email you.';
  next['ListenerEarnings.thisWeek'] = 'This week';
  next['ListenerEarnings.thisMonth'] = 'This month';
  next['ListenerEarnings.ytd'] = 'Year to date';
  next['ListenerEarningsPage.title'] = 'Earnings';
  next['ListenerEarningsPage.heading'] = 'Earnings';
  next['ListenerEarningsPage.lede'] =
    'Your listener payout from completed sessions. Same amounts as the dashboard This week card.';
  next['ListenerEarningsPage.itemized'] = 'Itemized earnings';
  next['ListenerEarningsPage.empty'] = 'No earnings yet.';
  next['ListenerEarningsPage.loading'] = 'Loading your earnings…';
  next['AvailabilityPage.title'] = 'Set My Available Times';
  next['AvailabilityPage.back'] = '‹ Back to Dashboard';
  next['AvailabilityPage.loading'] = 'Loading your hours…';
  next['AvailabilityPage.empty'] =
    'Create your Listening profile to set the weekly hours people can book.';
  next['AvailabilityPage.setup'] = 'Set up availability';
  next['ListenerEarnings.allTime'] = 'All time';
  next['ListenerEarnings.sessionCount'] = '{count, plural, one {# session} other {# sessions}}';
  next['ListenerEarnings.payouts'] = 'Payouts';
  next['ListenerEarnings.sales'] = 'Sales';
  next['ListenerEarnings.emptySales'] = 'No sales yet.';
  next['ListenerEarnings.someone'] = 'Someone';
  next['ListenerEarnings.pending'] = 'Pending';
  next['ListenerEarnings.pendingHint'] =
    'Accepted holds for upcoming or unfinished sessions. These are not earnings until the session is captured.';
  next['ListenerEarningsPage.pending'] = 'Pending';
  next['DashboardPage.todayTitle'] = 'Today’s calendar';
  next['DashboardPage.todayEmpty'] = 'Nothing today.';
  next['DashboardPage.currentSessionEmpty'] = 'No session right now.';
  next['DashboardPage.currentSessionTitle'] = 'In session';
  next['DashboardPage.jumpCurrent'] = 'In session';
  next['DashboardPage.nextSessionTitle'] = 'Next session';
  next['DashboardPage.nextSessionCurrentTitle'] = 'In session';
  next['DashboardPage.nextSessionCurrentEyebrow'] = 'In session';
  next['DashboardPage.nextSessionUpcomingEyebrow'] = 'Upcoming';
  next['DashboardPage.nextSessionRoleTalker'] = 'You’re the Talker';
  next['DashboardPage.nextSessionRoleListener'] = 'You’re the Listener';
  next['DashboardPage.nextSessionView'] = 'View';
  next['DashboardPage.incomingNowEmpty'] =
    'When a talker starts a session right now, it shows up here so you can join.';
  next['DashboardPage.availableNowOn'] = 'Available to listen';
  next['DashboardPage.availableNowOff'] = 'Available to listen';
  next['DashboardPage.availableNowRateLimited'] =
    'Wait {seconds} seconds, then try turning on again.';
  next['DashboardPage.availableNowFailed'] =
    'Could not update Available to listen. Try again in a moment.';
  next['DashboardPage.availableToListen'] = 'Available to listen';
  next['DashboardPage.availableToListenLede'] =
    "Off until you turn it on. While it’s on, we’ll email you — and text if SMS is on — when someone wants to talk now. It stays on until you turn it off or log out (or after 2 hours away if text alerts are off).";
  next['DashboardPage.availableReadyTitle'] = 'Ready to receive {talkers}?';
  next['DashboardPage.availableReadyTalkers'] = 'Talkers';
  next['DashboardPage.availableReadyLede'] =
    "You're Available during the work hours you set in your schedule, so Talkers can see you're ready to connect.";
  next['DashboardPage.availableBenefitFind'] = 'Talkers can find you';
  next['DashboardPage.availableBenefitNotify'] = "You'll get instant notifications";
  next['DashboardPage.availableBenefitTalk'] = 'Start meaningful conversations';
  next['DashboardPage.availableReadyHint'] =
    "It's off each time you log in, and stays on until you turn it off or log out (2 hours away if text alerts are off). Turn it on when you're near your phone or computer and ready to talk.";
  next['DashboardPage.availableLiveTitle'] = "You're Live & Available";
  next['DashboardPage.availableBannerLoading'] = 'Loading Available to Listen';
  next['DashboardPage.takeSessionsNow'] = 'Take sessions now';
  next['DashboardPage.takeSessionsTitle'] = "You're Offline for Start now";
  next['DashboardPage.takeSessionsLede'] = "You get Start now requests during the work hours in your schedule. Talkers can still book a later time.";
  next['DashboardPage.availableOnTitle'] = "You're Available to Listen";
  next['DashboardPage.availableOnLede'] = "Talkers can see you and send Start now requests. Stay near your phone or computer.";
  next['DashboardPage.availableOnHint'] = "Turn this off when you step away. It stays on until you turn it off or log out (or after 2 hours away if text alerts are off).";
  next['DashboardPage.getBookedKicker'] = 'Get booked';
  next['DashboardPage.getBookedAhead'] = 'Get booked ahead';
  next['DashboardPage.getBookedEmpty'] =
    'No upcoming bookings yet. Set your open times so people can book you.';
  next['DashboardPage.getBookedRequests'] =
    '{count, plural, one {# new request} other {# new requests}}';
  next['DashboardPage.setOpenTimes'] = 'Set my open times';
  next['DashboardPage.talkNowKicker'] = 'Right now';
  next['DashboardPage.talkNowTitle'] = 'Start now';
  next['DashboardPage.talkNowCount'] =
    '{count, plural, one {# listener is available now.} other {# listeners are available now.}}';
  next['DashboardPage.talkNowEmpty'] = '0 listeners are available now.';
  next['DashboardPage.talkNowBrowse'] = 'Browse Available Listeners';
  next['DashboardPage.bookAheadKicker'] = 'Later';
  next['DashboardPage.bookAheadTitle'] = 'Book for Later';
  next['DashboardPage.bookAheadEmpty'] = 'Find a listener and choose a time that works for you.';
  next['DashboardPage.findAListener'] = 'Find a Listener';
  next['DashboardPage.browseListeners'] = 'Browse Listeners';
  next['DashboardPage.tipsBannerLede'] = 'Get resources →';
  next['AccountSettings.availableDashboardHint'] = 'Turn Available to Listen on or off from your';
  next['AccountSettings.availableDashboardLink'] = 'dashboard';
  next['DashboardPage.availablePillOff'] = 'OFF - OFFLINE';
  next['DashboardPage.availableAutoOffTitle'] = 'AVAILABLE DURING YOUR SCHEDULED HOURS';
  next['DashboardPage.availableAutoOffBody'] =
    "You're Available during the hours in your work schedule. If you can't take calls, clock out. When someone wants to talk, we'll text you.";
  next['DashboardPage.availablePillOn'] = 'ON - AVAILABLE';
  next['DashboardPage.availableBubble'] = 'Someone wants to talk with you!';
  next['DashboardPage.viewMessages'] = 'View requests';
  next['DashboardPage.viewRequests'] = 'View requests';
  next['DashboardPage.startsSoonLabel'] = 'Starts soon';
  next['DashboardPage.startsAtTime'] = 'Starts at {time}';
  next['DashboardPage.startsInMinutes'] = 'Starts in {minutes} min';
  next['DashboardPage.viewEarnings'] = 'View earnings';
  next['DashboardPage.viewReviews'] = 'View reviews';
  next['DashboardPage.listeningUpcomingEmpty'] =
    'No upcoming sessions yet. When someone books you, they’ll show up here.';
  next['DashboardPage.talkerHeroLedeShort'] = 'Who would you like to talk to today?';
  next['DashboardPage.statReviewsLeft'] = '{count, plural, one {Review left} other {Reviews left}}';
  next['DashboardPage.statusCompleted'] = 'Completed';
  next['DashboardPage.statusMissed'] = 'Missed';
  next['DashboardPage.statusCanceled'] = 'Canceled';
  next['DashboardPage.statusNoConfirmation'] = 'No confirmation';
  next['ListingRating.empty'] = 'New listener.';
  next['ListingPage.reviewsEmpty'] =
    'After a completed 15-minute session, both people can leave 1–5 stars and a short note.';
  next['ListingPage.backToFavorites'] = '‹ Back to Favorites';
  next['ListingPage.backToListeners'] = '‹ Back to Listeners';
  next['ListingPage.backToMyBookings'] = '‹ Back to My Bookings';
  next['ListingPage.backToDashboard'] = '‹ Back to Dashboard';
  next['ListingPage.back'] = '‹ Back';
  next['EditListingPhotosForm.addImagesTip'] =
    'Use a clear face photo as the cover. No logos or flyers.';
  next['EditListingPhotosForm.imageRequired'] =
    'Add a face photo. Logos, flyers, and photos with text on them are not allowed.';
  next['EditListingPhotosForm.chooseImage'] = '+ Add a face photo…';
  next['EditListingPhotosForm.replacePhoto'] = 'Replace photo';
  next['EditListingPhotosForm.editPhoto'] = 'Edit';
  next['EditListingPhotosForm.setAsCover'] = 'Set as cover';
  next['EditListingAvailabilityPanel.availabilityPlanInfo'] =
    'When can people Book a time? Set a weekly schedule, then add date exceptions.';
  next['EditListingAvailabilityPanel.title'] = 'Set your schedule';
  next['EditListingAvailabilityPanel.createListingTitle'] = 'Set your schedule';
  next['ContactDetailsForm.smsHelp'] =
    'Optional. We can text you when a listener accepts a booking, when you get a new order message, when a booked session is about to start, or when a talker starts now. The number stays in your account settings. It is never shown on your profile, chat, or to the other person.';
  next['EditListingPricingForm.talkSessionLocked'] =
    'Turn on the formats you want to offer. You set your own rate per 15 minutes: text from $1, voice and video from $5, up to $50. People pay for the format they pick.';
  next['InboxPage.default-booking.reviewed-by-customer.status'] = 'Completed';
  next['InboxPage.default-booking.reviewed-by-provider.status'] = 'Completed';
  next['InboxPage.default-purchase.reviewed-by-customer.status'] = 'Completed';
  next['InboxPage.default-purchase.reviewed-by-provider.status'] = 'Completed';
  next['InboxPage.default-negotiation.reviewed-by-customer.status'] = 'Completed';
  next['InboxPage.default-negotiation.reviewed-by-provider.status'] = 'Completed';
  next['EditListingFilesForm.helpText'] =
    'The talker will receive the files automatically after purchase. Maximum: 10 files, 1 GB per file.';
  next['MakeOfferPage.defaultMessagePlaceholder'] = 'Is there something the talker should know?';
  next['MakeOfferPage.finePrint'] = "You'll be notified when the talker reacts to your offer.";
  next['StripePaymentForm.stripe.validation_error.missing'] =
    'There is no card on a talker that is being charged.';
  next['ListenerCalendar.title'] = 'Set My Available Times';
  next['ListenerCalendar.lede'] =
    "Set your weekly hours so people can book a time. They'll still wait for your confirmation.";
  next['ListenerCalendar.off'] = 'Off';
  next['ListenerCalendar.empty'] =
    "Set your weekly hours so people can book a time. They'll still wait for your confirmation.";
  next['ListenerCalendar.manage'] = 'Manage availability';
  next['ListenerCalendar.setup'] = 'Set up availability';
  next['ListenerCalendar.save'] = 'Save hours';
  next['ListenerCalendar.saveSchedule'] = 'Save schedule';
  next['ListenerCalendar.repeatsWeekly'] = 'Repeats every week';
  next['ListenerCalendar.repeatsWeeklyNote'] =
    'These hours stay on every week until you change them.';
  next['ListenerCalendar.takeADayOff'] = 'Take a day off';
  next['ListenerCalendar.dayOffsTitle'] = 'Days off';
  next['ListenerCalendar.dayOffsNote'] = 'A day off is one date. It does not repeat every week.';
  next['ListenerCalendar.dayOffOnce'] = 'This date only';
  next['ListenerCalendar.dayOffsEmpty'] = 'No days off yet.';
  next['ListenerCalendar.dayOffSaved'] = "Day off saved: {date}. You won't be bookable that day.";
  next['ListenerCalendar.dayOffSavedRange'] =
    "Day off saved: {date}. You won't be bookable on those dates.";
  next['ListenerCalendar.dayOffSavedOk'] = 'OK';
  next['ListenerCalendar.removeDayOff'] = 'Remove';
  next['ListenerCalendar.removeDayOffAria'] = 'Remove day off {date}';
  next['ListenerCalendar.dayOffRemoving'] = 'Removing...';
  next['ListenerCalendar.saving'] = 'Saving...';
  next['ListenerCalendar.saved'] = '✓ Availability saved';
  next['ListenerCalendar.scheduleSaved'] = 'Schedule saved';
  next['ListenerCalendar.unsavedWarning'] = 'You have unsaved hours. Leave without saving?';
  next['ListenerCalendar.saveError'] = "Couldn't save your availability. Please try again.";
  next['ListenerCalendar.copyToAllDays'] = "Copy {day}'s hours to every day";
  next['ListenerCalendar.copyConfirm'] =
    "Set {range} on all 7 days? This replaces other days' hours.";
  next['ListenerCalendar.copyConfirmWeekdays'] =
    'Set {range} on weekdays only? This replaces Monday–Friday.';
  next['ListenerCalendar.copyConfirmCancel'] = 'Cancel';
  next['ListenerCalendar.copyConfirmApply'] = 'Copy';
  next['ListenerCalendar.copyWeekdays'] = 'Weekdays only';
  next['ListenerCalendar.copyToast'] = 'Hours copied to all days — tap Save schedule';
  next['ListenerCalendar.copyToastWeekdays'] = 'Hours copied to weekdays — tap Save schedule';
  next['ListenerCalendar.addSlot'] = '+ Add time slot';
  next['ListenerCalendar.addAnotherSlot'] = '+ Add another time slot';
  next['ListenerCalendar.removeSlot'] = 'Remove {day} time slot';
  next['ListenerCalendar.notAvailable'] = 'Not available';
  next['ListenerCalendar.overlap'] = 'These times overlap. Please adjust one of the time slots.';
  next['ListenerCalendar.endBeforeStart'] = 'End time must be after the start time.';
  next['ListenerCalendar.timesShownIn'] = 'Times shown in: {timezone}';
  next['ListenerCalendar.toggleDay'] = '{day} available';
  next['TalkerCalendar.title'] = 'Talking schedule';
  next['TalkerCalendar.lede'] =
    'Set weekly hours so people can Book a time. They still wait for your confirmation.';
  next['TalkerCalendar.publicLede'] =
    'Open hours for Book a time. Each request still needs confirmation.';
  next['TalkerCalendar.publicEmpty'] = 'They have not set weekly hours yet.';
  next['TalkerCalendar.off'] = 'Off';
  next['TalkerCalendar.empty'] = 'Set weekly hours so people can Book a time.';
  next['TalkerCalendar.manage'] = 'Set your schedule';
  next['TalkerCalendar.bookLede'] =
    'Book one of these open 15-minute slots. They confirm before it starts.';
  next['TalkerCalendar.bookSlot'] = 'Book a time';
  next['TalkerScheduleEditor.title'] = 'Set My Available Times';
  next['TalkerScheduleEditor.lede'] =
    'Turn on your availability and choose the times you’re open so talkers can book you. These hours repeat every week.';
  next['TalkerScheduleEditor.to'] = 'to';
  next['TalkerScheduleEditor.save'] = 'Save schedule';
  next['TalkerScheduleEditor.copyLede'] = 'Copy hours to other days';
  next['TalkerScheduleEditor.copyFrom'] = 'Copy {day} to all days';
  next['TalkerScheduleEditor.repeatsWeekly'] = 'These hours repeat every week.';
  next['TalkerScheduleEditor.availableLabel'] = 'I’m available to listen';
  next['TalkerScheduleEditor.quickSetup'] = 'Quick setup';
  next['TalkerScheduleEditor.copyToAllDays'] = '+ Copy to all days';
  next['TalkerScheduleEditor.copyHoursTitle'] = 'Copy hours';
  next['TalkerScheduleEditor.copyHoursHint'] = 'Copy {start}–{end} to other days.';
  next['TalkerScheduleEditor.copyAllDays'] = 'All days';
  next['TalkerScheduleEditor.copyWeekdays'] = 'Weekdays only';
  next['TalkerScheduleEditor.copyWeekends'] = 'Weekends only';
  next['TalkerScheduleEditor.pickDays'] = 'Pick days…';
  next['TalkerScheduleEditor.applyPickedDays'] = 'Copy to picked days';
  next['TalkerScheduleEditor.done'] = 'Done';
  next['TalkerScheduleEditor.availableThisDay'] = 'Available this day';
  next['TalkerScheduleEditor.unavailable'] = 'Unavailable';
  next['TalkerScheduleEditor.changeOneDay'] = 'Take a day off';
  next['EditListingAvailabilityPlanForm.copyToOtherDays'] = 'Copy this day to the rest of the week';
  next['TalkerScheduleEditor.saving'] = 'Saving…';
  next['EditListingAvailabilityPanel.addException'] = 'Take a day off';
  next['EditListingAvailabilityPanel.timeOffTitle'] = 'Time off / one day';
  next['EditListingAvailabilityPanel.timezone'] = 'Times shown in {timezone}';
  next['EditListingAvailabilityPanel.allDay'] = 'All day';
  next['EditListingAvailabilityPanel.off'] = 'Off';
  next['ChangeOneDayEditor.title'] = 'Change one day';
  next['ChangeOneDayEditor.lede'] =
    'Pick a day, then say if you’re off or using different hours. Your usual week comes back after that.';
  next['ChangeOneDayEditor.pickDay'] = 'Pick a day';
  next['ChangeOneDayEditor.severalDays'] = 'Several days in a row';
  next['ChangeOneDayEditor.legend'] =
    'Grey is past. Navy ring is today. Pink is the day you picked.';
  next['ChangeOneDayEditor.rangeHint'] =
    'For a vacation or a stretch of days off. Choose the last day.';
  next['ChangeOneDayEditor.pickLastDay'] = 'Choose the last day';
  next['ChangeOneDayEditor.whatChange'] = 'What should change?';
  next['ChangeOneDayEditor.offTitle'] = 'Off this day';
  next['ChangeOneDayEditor.offCopy'] = 'No bookings. Your usual hours return the next day.';
  next['ChangeOneDayEditor.hoursTitle'] = 'Different hours this day';
  next['ChangeOneDayEditor.hoursCopy'] = 'Keep the day open, just shift the window.';
  next['ChangeOneDayEditor.usualHours'] = '{day}’s usual hours are {hours}';
  next['ChangeOneDayEditor.from'] = 'From';
  next['ChangeOneDayEditor.to'] = 'To';
  next['ChangeOneDayEditor.summaryEmpty'] = 'Pick a day to change.';
  next['ChangeOneDayEditor.summaryOff'] = '{when}: off (your usual hours return {next})';
  next['ChangeOneDayEditor.summaryHours'] = '{when}: {hours}';
  next['ChangeOneDayEditor.cancel'] = 'Cancel';
  next['ChangeOneDayEditor.save'] = 'Save';
  next['ChangeOneDayEditor.saving'] = 'Saving…';
  next['ChangeOneDayEditor.prevMonth'] = 'Previous month';
  next['ChangeOneDayEditor.nextMonth'] = 'Next month';
  next['PromoKit.title'] = 'Promote Our Site';
  next['PromoKit.lede'] =
    'Promote the whole site. Share ' +
    'sweetlisteners.com — not a Listening profile.';
  next['PromoKit.ledeLight'] = 'Know someone who needs an ear? Share the site.';
  next['PromoKit.privacy'] = 'Please don’t promote your profile so you stay private.';
  next['PromoKit.testNote'] = 'This copies the public site — sweetlisteners.com.';
  next['PromoKit.share'] = 'Share the site';
  next['PromoKit.copy'] = 'Copy site link';
  next['PromoKit.copyLink'] = 'Copy link';
  next['PromoKit.shareSocial'] = 'Share on social media';
  next['PromoKit.ledeDashboard'] = 'Share the site to drive traffic — not your personal profile.';
  next['PromoKit.copied'] = 'Link copied';
  next['PromoKit.copyCaption'] = 'Copy a caption';
  next['PromoKit.captionCopied'] = 'Caption copied';
  next['PromoKit.browsePromos'] = 'Browse ads & promos';
  next['PromotePage.title'] = 'Promote Our Site';
  next['PromotePage.kicker'] = 'Share kit';
  next['PromotePage.heading'] = 'Promote Our Site';
  next['PromotePage.lede'] =
    'Promote the whole site to drive traffic — not an individual Listening ' +
    'profile. Download images and copy captions that send people to ' +
    'sweetlisteners.com.';
  next['PromotePage.privacy'] = 'Please don’t promote your profile so you stay private.';
  next['PromotePage.testNote'] =
    'These files and captions go to the public site: sweetlisteners.com.';
  next['DashboardPage.share'] = 'Promote';
  next['DashboardPage.promote'] = 'Promote';
  next['DashboardPage.incomingNowTitle'] = 'Start sessions now';
  next['DashboardPage.incompleteProfileTitle'] = 'Finish your Listening profile';
  next['DashboardPage.incompleteProfileBody'] =
    'Your Listening profile isn’t complete yet. Finish setup so people can find you.';
  next['DashboardPage.incompleteProfileCta'] = 'Finish setup';
  next['DashboardPage.finishListenerSetupTitle'] = 'Finish setting up your earning profile';
  next['DashboardPage.finishListenerSetupBody'] =
    'You started setting up to earn. Finish identity, payouts, and your profile, or cancel anytime.';
  next['DashboardPage.finishListenerSetupCta'] = 'Continue setup';
  next['DashboardPage.finishListenerSetupCancel'] = 'Cancel setup';
  next['DashboardPage.talkerSetupTitle'] = 'Your Talking profile (optional)';
  next['DashboardPage.talkerSetupBody'] =
    'You can book a listener right away — add this anytime. A short private Talking profile just helps us suggest the right listeners. Only you and the people you book with see it.';
  next['DashboardPage.talkerSetupCta'] = 'Set up Talking profile';
  next['DashboardPage.talkerSetupVerifyCta'] = 'Verify identity';
  next['SiteFooter.promote'] = 'Promote';
  next['PromotePage.assetsHeading'] = 'Promo images';
  next['PromotePage.assetSquare'] = 'Square post';
  next['PromotePage.assetSquareSize'] = '1080 × 1080 — Instagram, Facebook, X';
  next['PromotePage.assetStory'] = 'Story / Reel';
  next['PromotePage.assetStorySize'] = '1080 × 1920 — Instagram and TikTok stories';
  next['PromotePage.assetLandscape'] = 'Landscape';
  next['PromotePage.assetLandscapeSize'] = '1200 × 628 — Facebook and X link cards';
  next['PromotePage.download'] = 'Download';
  next['PromotePage.captionsHeading'] = 'Ready captions';
  next['PromotePage.captionInstagram'] = 'Instagram';
  next['PromotePage.captionTikTok'] = 'TikTok';
  next['PromotePage.captionFacebook'] = 'Facebook';
  next['PromotePage.captionX'] = 'X';
  next['PromotePage.copyCaption'] = 'Copy caption';
  next['PromotePage.captionCopied'] = 'Caption copied';
  next['PromotePage.copied'] = 'Copied!';
  next['HowToGetBooked.availableNow'] =
    'Your calendar is your store: during your calendar hours you show as Available Now for Start now. Set hours you can really cover.';
  next['HowToGetBooked.showUp'] =
    "Show up on time. If you don't join within 4 minutes of the start, the session is cancelled and you won't be paid. If a talker misses a scheduled session after you join, you are still paid.";
  next['HowToGetBooked.profile'] =
    'More photos: add several clear, friendly, well-lit photos so your Listening profile stops the scroll.';
  next['HowToGetBooked.reviews'] =
    'Earn good reviews. They help search ranking. Featured does not buy higher stars.';
  next['HowToGetBooked.formats'] =
    'Offer the formats you can do — Text, Voice, or Video.';
  next['HowToGetBooked.availability'] =
    'Keep calendar hours open for scheduled Book a time too.';
  next['HowToGetBooked.intro'] =
    'We’re here to make you money. Sweet Listeners brings visitors in every day — your move is turning them into sessions with you. Knock out each step and watch your bar fill up.';
  next['HowToGetBooked.progress'] =
    '{done} of {total} done — keep going!';
  next['HowToGetBooked.go'] =
    'Go →';
  next['HowToGetBooked.autoDone'] =
    'Done from your profile';
  next['HowToGetBooked.moreTitle'] =
    'Bonus moves';
  next['HowToGetBooked.wording'] =
    'Better wording: write a warm About you in a few full sentences — who you are, what you listen to best, and how people feel after talking with you.';
  next['HowToGetBooked.notifications'] =
    'Notifications that work for you: in Account settings → Contact details → Notifications, opt in to shift reminders, or hourly nudges if you want them, so you’re there when visitors arrive.';
  next['HowToGetBooked.practice'] =
    'Practice: do free sessions with other listeners to get a feel for a real session before buyers arrive.';
  next['HowToGetBooked.promote'] =
    'Promote, promote, promote — share your profile link on Instagram, TikTok, Facebook, X, and with friends. Promote has ready-made captions.';
  next['HowToGetBooked.friends'] =
    'Invite friends to take their free first session with you and, if it helped, leave an honest review. Reviews help visitors pick you.';
  next['HowToGetBooked.gratuities'] =
    'Tips: talkers can leave an optional tip after a session. You keep 70% of tips — same split as sessions — paid with your regular payouts.';
  next['HowToGetBooked.earnings'] =
    'Know your money: paid sessions are $10 on the platform, and you keep 70%.';
  next['HowToGetBooked.aiCoach'] =
    'Optional: get strategy help from Grok or your usual AI coach — paste in your profile draft, photo ideas, calendar and notification plan, or promo copy.';
  next['HowToGetBooked.helloVideo'] =
    'Add a Hello video 🎥 (optional, helps bookings) — it shows as a play button on your profile. Quick recipe: about 30 seconds, good light, face clear in frame, smile. Say hi with your first name, who you are, and what you love listening to. No contact info or last name. Record it on Profile settings → Hello video card.';
  next['HowToGetBooked.topics'] =
    'Pick topics you’re genuinely comfortable with and have real experience in — life experience, listening, and teaching experience all count. Real know-how gets rebooked.';
  next['HowToGetBooked.trending'] =
    'Watch what’s trending (news, seasons, what talkers are searching) and add the hot topics you can honestly cover. Fresh topics = more buyers finding you.';
  next['DashboardPage.playbookTitle'] =
    'Earners Playbook →';
  next['DashboardPage.playbookLede'] =
    'Level up your checklist → (and tell us what you think!)';
  next['HowToGetBooked.feedback'] =
    'We’d love your feedback — what’s working, what’s not, what would help you earn more.';
  next['HowToGetBooked.feedbackCta'] =
    'Tell us →';
  next['HowToGetBooked.featured'] =
    'Featured (${monthly}/mo or ${yearly}/yr) is an optional paid homepage strip on top.';
  next['DashboardPage.featuredOnSuccess'] =
    'Featured is on. Your Listening profile can show in the homepage Featured strip.';
  next['DashboardPage.featuredOffSuccess'] =
    'Featured is off. Your Listening profile still shows in Search.';
  next['DashboardPage.featuredFailed'] =
    'Couldn’t update Featured. Try again — this does not restart your Listening profile.';
  next['DashboardPage.featuredSaving'] = 'Saving Featured…';
  next['PhotoCropModal.title'] = 'Frame your photo';
  next['PhotoCropModal.hint'] = 'Pinch or use + / − to zoom, then drag to center your face.';
  next['PhotoCropModal.previewAlt'] = 'Photo crop preview';
  next['PhotoCropModal.loading'] = 'Loading photo…';
  next['PhotoCropModal.zoomIn'] = 'Zoom in';
  next['PhotoCropModal.zoomOut'] = 'Zoom out';
  next['PhotoCropModal.zoomSlider'] = 'Zoom';
  next['PhotoCropModal.cancel'] = 'Cancel';
  next['PhotoCropModal.usePhoto'] = 'Use photo';
  next['ProfileSettingsForm.tip'] =
    'Tip: Frame your face in the circle. Pinch or use the slider to zoom.';
  next['ProfileSettingsForm.saveBlocked'] = 'Add {fields} above so Save changes can run.';
  next['ProfileSettingsForm.updateProfileFailed'] = 'Couldn’t save your profile. Try again.';
  next['EditListingDetailsForm.saveBlocked'] = 'Add {fields} above so you can continue.';
  next['MainPanelHeader.foundResults'] =
    '{count, number} {count, plural, one {result} other {results}}';
  next['SearchFiltersMobile.foundResults'] =
    '{count, number} {count, plural, one {result} other {results}}';
  next['AuthenticationPage.verifyEmailText'] =
    'Check your inbox and tap the link we sent to {email}. Once your email is verified, you can finish setting up your account.';
  next['AuthenticationPage.verifyEmailJunkHint'] =
    'Hotmail, Outlook, and other inboxes sometimes put our email in Junk. Check Junk/Spam. Resend below if nothing arrived.';
  next['AuthenticationPage.resendEmailSuccess'] =
    'A new verification email was sent. Check inbox and Junk.';
  next['EmailVerificationForm.checkInboxTitle'] = '{name}, check your inbox';
  next['EmailVerificationForm.checkInboxText'] =
    'We sent a link to {email}. Open that email and tap the link to continue. Wrong address? Use a different email below. You stay signed in.';
  next['EmailVerificationForm.needHelp'] = 'Need help?';
  next['EmailVerificationForm.helpLink'] = 'Visit Help';
  next['ChangeUnverifiedEmailForm.prompt'] = 'Wrong email?';
  next['ChangeUnverifiedEmailForm.promptLink'] = 'Fix it';
  next['ChangeUnverifiedEmailForm.submit'] = 'Send my code to this email';
  next['ChangeUnverifiedEmailForm.lede'] =
    'Enter the address you meant to use and we’ll send a new code there. You stay signed in.';
  next['ContactDetailsForm.unverifiedChangeInfo'] =
    'This account is not verified yet. Save the correct email and we will send a new verification link. You stay signed in — no password needed.';
  next['EmailVerificationForm.successButtonText'] = 'Continue';
  next['DashboardPage.featuredTitle'] = 'Featured placement';
  next['DashboardPage.featuredLede'] =
    '${monthly}/mo or ${yearly}/yr homepage Featured strip. Does not buy stars or rank.';
  next['DashboardPage.featuredCta'] = 'Get featured — ${monthly}/mo or ${yearly}/yr';
  next['DashboardPage.featuredToggleOn'] = 'Get featured';
  next['DashboardPage.featuredOn'] = 'Featured on';
  next['DashboardPage.featuredToggleOff'] = 'Turn off Featured';
  next['ListenerKeepShare.featured'] =
    'Optional Featured homepage strip is ${monthly}/month or ${yearly}/year. It does not change star ratings.';
  next['BookingTimeForm.timeZoneNote'] =
    'Times are shown in your local time zone ({timeZone}). The same moment is stored in UTC.';
  next['BookingFixedDurationForm.timeZoneNote'] =
    'Times are shown in your local time zone ({timeZone}). The same moment is stored in UTC.';
  next['BookingFixedDurationForm.datePlaceholder'] = 'Select a date';
  next['BookingFixedDurationForm.timePlaceholder'] = 'Select a time';
  next['BookingFixedDurationForm.chooseFormatFirst'] = 'Choose a session format first';
  next['BookingFixedDurationForm.noOpenTimes'] = 'No open times, try Start now or another day';
  next['EditListingPricingPanel.createListingTitle'] = 'Pricing';
  next['EditListingPricingPanel.title'] = 'Pricing for {listingTitle}';
  next['EditListingWizard.tabLabelDetails'] = 'Details';
  next['EditListingWizard.tabLabelPricing'] = 'Pricing';
  next['ListingPage.editListing'] = 'Edit Listening profile';
  next['ProfilePage.ownProfile'] = 'This is your Talking profile.';
  next['ProfilePage.editTalkingProfile'] = 'Edit profile';
  next['ProfilePage.editProfileLinkDesktop'] = 'Edit Talking profile';
  next['ProfilePage.editProfileLinkMobile'] = 'Edit Talking profile';
  next['ProfilePage.listingsTitle'] = 'My Listening profile ({count})';
  next['DashboardPage.availableNowEmpty'] =
    'Set up your Listening profile first, then you can show as available now.';
  next['DashboardPage.featuredEmpty'] =
    'Set up your Listening profile first, then you can feature it on the homepage.';
  next['NotFoundPage.SearchForm.placeholder'] = 'Search listeners…';
  next['NotFoundPage.screenreader.search'] = 'Search listeners';
  next['SearchPage.noResults'] = "Couldn't find listeners that match. Try a name or a topic.";
  next['KeywordSearchBar.label'] = 'Search listeners';
  next['KeywordSearchBar.placeholder'] = 'Search any topic or interest…';
  next['KeywordSearchBar.submit'] = 'Search';
  next['KeywordSearchBar.clear'] = 'Clear';
  next['SearchPage.createListing'] = 'Become a listener';
  next['SearchPage.notTherapy'] = 'These are friendly listeners for a short conversation. Your first 15 minutes are free.';
  next['TopicPicker.listenerLegend'] =
    'Only pick topics you’re confident and comfortable talking about';
  next['TopicPicker.listenerHint'] =
    'Only pick topics you’re confident and comfortable talking about. People can find you by these.';
  next['TopicPicker.placeholder'] = 'Choose conversation types';
  next['TopicPicker.selectedCount'] = '{count} selected';
  next['TopicPicker.menuLabel'] = 'Conversation types';
  next['TopicPicker.otherLabel'] = 'If you picked Other, add a short label (optional)';
  next['TopicPicker.otherPlaceholder'] = 'Add a short label if you want';
  next['TopicPicker.remove'] = 'Remove {label}';
  next['TopicPicker.required'] = 'Pick at least one conversation type to continue.';
  next['TopicSearchBar.label'] = 'Conversation types';
  next['TopicSearchBar.placeholder'] = 'Choose conversation types';
  next['TopicSearchBar.selectedCount'] = '{count} selected';
  next['TopicSearchBar.menuLabel'] = 'Conversation types';
  next['ListeningStylePicker.legend'] = 'How do you usually listen?';
  next['ListeningStylePicker.hint'] =
    'Pick any that fit. People can see this on your Listening profile.';
  next['ListeningStylePicker.placeholder'] = 'Choose listening styles';
  next['StyleSearchBar.label'] = 'Listening style';
  next['StyleSearchBar.placeholder'] = 'Choose listening styles';
  next['DescriptionIdeas.helper'] =
    'A few sentences about you, what you’re into and who you click with. Keep it warm and human.';
  next['DescriptionIdeas.needIdeas'] = 'Need ideas?';
  next['DraftWithGrok.cta'] = 'Draft with Grok';
  next['DraftWithGrok.copyAnyAi'] = 'Copy prompt for any AI';
  next['DraftWithGrok.helper'] = 'Paste the draft into About you.';
  next['DraftWithGrok.copied'] = 'Prompt copied — paste into Grok';
  next['DraftWithGrok.copiedAnyAi'] = 'Prompt copied — paste into any AI.';
  next['DraftWithGrok.opened'] = 'Grok opened in a new tab. Paste the prompt if it is empty.';
  next['DraftWithGrok.talkerHelper'] =
    'Paste the draft into “what I’d like to talk about.” Listeners only see this after you book.';
  next['EditListingDetailsForm.descriptionPlaceholder'] =
    'How you listen, who you click with, and what a 15-minute talk with you feels like.';
  next['ProfileReveals.legend'] = 'Optional details you can share';
  next['ProfileReveals.hint'] =
    'Off by default. Nothing shows on your Listening profile until you turn on Show on my profile. Sessions stay remote — city is only a vibe, not a meetup.';
  next['ProfileReveals.showAge'] = 'Show age on profile';
  next['ProfileReveals.showSex'] = 'Show my gender on my profile and in search';
  next['ProfileReveals.sexLabel'] = 'Gender';
  next['ProfileReveals.genderSearchNote'] =
    'Off by default. Turn it on and choose Woman to show up when talkers use the Woman listener filter.';
  next['ProfileReveals.ageRangeLabel'] = 'Age range';
  next['ProfileReveals.ageRangeFromBirthdate'] = '(from your birthdate)';
  next['ProfileReveals.showAgeRange'] = 'Show my age range on my profile and in search';
  next['ProfileReveals.ageRangeNote'] =
    'Off by default. Talkers only see the range, like 30–44 — never your birthdate or exact age.';
  next['ProfileReveals.showCity'] = 'Show city on profile';
  next['ProfileReveals.birthdateLabel'] = 'Birthdate';
  next['ProfileReveals.birthdateNote'] =
    'Your birthdate stays private and is never shown.';
  next['ProfileReveals.birthdateInvalid'] = 'Enter a real birthdate.';
  next['ProfileReveals.birthdateFuture'] = 'Birthdate can’t be in the future.';
  next['ProfileReveals.under18'] =
    'You must be 18+ to be a listener. This birthdate would make you under 18. On TEST, the last-4 form is a stand-in and does not verify anyone.';
  next['ProfileReveals.legacyAgeNote'] =
    'You previously saved age {age}. Add a birthdate so it stays current. Until then, that age still shows if Show age on profile is on. Your birthdate is never public.';
  next['ProfileReveals.cityNote'] =
    'Pick a metro or type your own. City or metro only — no street, neighborhood, ZIP, or map. Sessions stay remote.';
  next['ListeningStyles.easygoing'] = 'Easygoing';
  next['ListeningStyles.warm-encouraging'] = 'Warm and encouraging';
  next['ListeningStyles.detail-oriented'] = 'Detail-oriented';
  next['ListeningStyles.analytical'] = 'Analytical';
  next['ListeningStyles.direct'] = 'Direct';
  next['ListeningStyles.challenging'] = 'Challenging';
  next['ListeningStyles.reflective'] = 'Reflective';
  next['ListeningStyles.lighthearted'] = 'Lighthearted';
  next['ListeningStyles.calm-steady'] = 'Calm and steady';
  next['ListeningStyles.practical'] = 'Practical';
  next['ContentPage.safety.locationTitle'] = 'Remote sessions, optional city';
  next['ContentPage.safety.locationBody'] =
    'Sessions are remote chat, voice, or video. We do not collect a street address, neighborhood, ZIP, or map. A listener can optionally show a city or metro on their Listening profile — nothing else.';
  next['ContentPage.about.p2'] =
    'Listeners are verified adults. We do not publish city landing pages, and we never show a street address or map. A listener may choose to show a city. You talk with a person remotely — not a local office.';
  next['ContentPage.howItWorks.step1Body'] =
    'Read a short bio and pick a verified adult. Search is by name or what’s on your mind — not by street or neighborhood.';
  next['ContentPage.safety.schemaDescription'] =
    '18+ identity, verified people, remote sessions, no street address or map, and no sexual content or personal-info exchange.';
  next['SessionTopics.stress-venting'] = 'Stress or venting';
  next['SessionTopics.relationships-dating'] = 'Relationships or dating';
  next['SessionTopics.family'] = 'Family';
  next['SessionTopics.friendship'] = 'Friendship';
  next['SessionTopics.loneliness'] = 'Wanting company';
  next['SessionTopics.work-career'] = 'Work or career';
  next['SessionTopics.school-student'] = 'School or student life';
  next['SessionTopics.parenting'] = 'Parenting';
  next['SessionTopics.caregiving'] = 'Caregiving';
  next['SessionTopics.grief-loss'] = 'Grief or loss';
  next['SessionTopics.major-life-changes'] = 'Major life changes';
  next['SessionTopics.confidence-self-esteem'] = 'Confidence or self-esteem';
  next['SessionTopics.identity-belonging'] = 'Identity or belonging';
  next['SessionTopics.everyday-conversation'] = 'Everyday conversation';
  next['SessionTopics.advice-perspective'] = 'Advice or perspective';
  next['SessionTopics.hobbies-interests'] = 'Hobbies or interests';
  next['SessionTopics.celebrations-good-news'] = 'Celebrations or good news';
  next['SessionTopics.spirituality-meaning'] = 'Spirituality or meaning';
  next['SessionTopics.other'] = 'Other';
  next['SearchPage.schemaTitle'] = 'Find a listener | {marketplaceName}';
  next['SearchPage.schemaTitleWithKeywords'] = 'Listener for “{keywords}” | {marketplaceName}';
  next['SearchFiltersMobile.noResults'] = 'No listeners matched.';
  next['ListenerKeepShare.kicker'] = 'Listen, and earn';
  next['ListenerKeepShare.heading'] = 'You earn {share} of your rates';
  next['ListenerKeepShare.lede'] =
    'Make people feel better by listening, and get paid for every completed session.';
  next['ListenerKeepShare.notTherapy'] = 'Friendly conversation, not professional advice.';
  next['ListenerKeepShare.schedule'] =
    'You set available now or a calendar for when you want to listen.';
  next['ContentPage.howItWorks.schemaDescription'] =
    'Find someone to talk to, then pay someone to listen at the price they set.';
  next['ContentPage.howItWorks.faq4a'] =
    'No. Feel heard, lighter and more connected. Your first 15 minutes are free.';
  next['ContentPage.safety.schemaDescription'] =
    '18+ identity, verified people, remote sessions, no street address or map, and no sexual content or personal-info exchange.';
  next['ContentPage.safety.lede'] =
    'This site is for verified adults who want someone to talk to.';
  next['ContentPage.safety.notTherapy'] =
    'Our safety rules keep every conversation kind and respectful. If you need emergency help, contact local emergency services.';
  next['GuidePage.talk-to-someone.schemaDescription'] =
    'Find someone to talk to for 15 minutes.';
  next['AuthenticationPage.termsAndConditionsAcceptText'] =
    'I agree to the Terms of Service and Privacy Policy';
  next['AuthenticationPage.termsAndConditionsAcceptRequired'] =
    'You need to agree to the Terms of Service and Privacy Policy';
  next['AuthenticationPage.termsAndConditionsTermsLinkText'] = 'Terms of Service';
  next['AuthenticationPage.termsAndConditionsPrivacyLinkText'] = 'Privacy Policy';
  next['SignupForm.confirmAge18'] = 'I confirm I am 18 or older.';
  next['SignupForm.agreeGuidelines'] = 'I agree to the Community Guidelines';
  next['SignupForm.agreeGuidelinesRequired'] =
    'Agree to the Community Guidelines to create an account.';
  next['SignupForm.agreeGuidelinesLink'] = 'Community Guidelines';
  next['GuidelinesConsentGate.title'] = 'Community Guidelines';
  next['GuidelinesConsentGate.body'] =
    'Please agree to the Community Guidelines to keep using the site.';
  next['GuidelinesConsentGate.agree'] = 'I agree to the Community Guidelines';
  next['GuidelinesConsentGate.continue'] = 'Continue';
  next['GuidelinesConsentGate.failed'] = 'Couldn’t save that. Try again.';
  next['SignupForm.displayNameLabel'] = 'Screen name';
  next['SignupForm.displayNamePlaceholder'] = 'e.g. CalmListener';
  next['ConfirmSignupForm.displayNameLabel'] = 'Screen name';
  next['ConfirmSignupForm.displayNamePlaceholder'] = 'e.g. CalmListener';
  next['SignupForm.confirmAge18Required'] = 'Confirm you are 18 or older to create an account.';
  next['SignupForm.identityNotice'] =
    'You must be 18+. On TEST, the later last-4 form is a stand-in — it does not verify anyone. Live will require a photo ID and a selfie. We do not store ID images or raw PII in logs.';
  next['AuthenticationPage.verifyIdentityTitle'] = 'TEST only: 18+ stand-in';
  next['AuthenticationPage.verifyIdentityText'] =
    'TEST only: this is a practice ID step. On TEST you confirm 18+ with a last-4 stand-in — the last 4 digits do not verify anyone. Live will require a photo ID and a selfie. We do not store ID images or raw PII in logs.';
  next['DashboardPage.identityTitle'] = 'Confirm you are 18+';
  next['DashboardPage.identityBody'] =
    'Confirm you are 18+ before you listen or book. We’ll ask for the last 4 digits of an ID.';
  next['DashboardPage.identityCta'] = 'Open 18+ check';
  next['DashboardPage.noShowTitle'] = 'Listener no-show notes';
  next['DashboardPage.noShowBody'] =
    '{count, plural, one {# no-show} other {# no-shows}}. Each note is from a session where the talker joined and you did not join within 4 minutes of start. The talker’s hold was released — they were not charged.';
  next['Identity.sandboxKicker'] = 'TEST only — practice ID step';
  next['Identity.title'] = 'TEST 18+ stand-in';
  next['Identity.lede'] =
    'This form does not verify anyone. You confirm you are 18+ and enter the last 4 digits of an ID so we can try the TEST gate. The last 4 digits do not actually verify anyone. Live will require a photo ID and a selfie. We do not store ID images or raw PII in logs.';
  next['Identity.confirmId'] = 'I understand this TEST stand-in is not a government ID check.';
  next['Identity.mockLast4Label'] =
    'Last 4 digits of an ID (TEST stand-in — does not verify anyone; not stored)';
  next['Identity.confirmSelfie'] =
    'I understand live will require a photo ID and a selfie. This TEST form does not check a selfie.';
  next['Identity.submitSandbox'] = 'Complete TEST stand-in';
  next['Identity.privacyNote'] =
    'TEST only. The last 4 digits do not verify anyone. We do not store ID images or raw PII in logs. Live will require a photo ID and a selfie.';
  next['Identity.verifiedTitle'] = 'TEST stand-in complete';
  next['Identity.verifiedBody'] =
    'You are marked 18+ for TEST only. This is not a real government ID check. The last 4 digits do not verify anyone. Live will require a photo ID and a selfie.';
  next['Identity.nextTitle'] = 'You’re ready — here’s what to do next';
  next['Identity.nextBody'] =
    'Open your Dashboard to book or join a session, or find a listener now.';
  next['Identity.nextBodyListen'] = 'Open your Dashboard to start sessions when you’re ready.';
  next['Identity.goToDashboard'] = 'Go to Dashboard';
  next['Identity.continueSetup'] = 'Continue setup';
  next['Identity.findAListener'] = 'Find someone to talk to or learn from';
  next['Identity.dismissNext'] = 'Dismiss';
  next['DashboardPage.standinSuccessTitle'] = '18+ check complete';
  next['DashboardPage.standinSuccessBody'] =
    'You’re ready to talk. Find a listener when you want a session.';
  next['DashboardPage.standinSuccessBodyListen'] =
    'You’re ready. Open Listening on your Dashboard when you want to take sessions.';
  next['DashboardPage.standinSuccessCta'] = 'Find someone to talk to or learn from';
  next['DashboardPage.standinSuccessDismiss'] = 'Dismiss';
  next['VerifiedBadge.label'] = 'Verified (TEST)';
  next['VerifiedBadge.title'] = 'TEST stand-in only. The last 4 digits do not verify anyone.';
  next['FoundingListenerBadge.label'] = 'Founding Listener';
  next['FoundingListenerBadge.title'] = 'One of our first listeners.';
  next['OpsFoundingPage.title'] = 'Founding Listeners';
  next['OpsFoundingPage.kicker'] = 'Ops';
  next['OpsFoundingPage.heading'] = 'Founding Listener badge';
  next['OpsFoundingPage.lede'] =
    'Paste listener emails. This sets publicData.founding on the user and each of their listings. It only shows the blue badge — no other booking change.';
  next['OpsFoundingPage.emailsLabel'] = 'Listener emails';
  next['OpsFoundingPage.set'] = 'Set Founding Listener';
  next['OpsFoundingPage.clear'] = 'Clear badge';
  next['OpsFoundingPage.saving'] = 'Saving…';
  next['OpsFoundingPage.denied'] = 'This page is for Sweet Listeners ops only.';
  next['OpsFoundingPage.saveError'] = 'Could not update those listeners.';
  next['OpsFoundingPage.safetyLink'] = 'Safety review';
  next['LandingPage.verified'] = 'Verified (TEST)';
  next['Page.schemaDescription'] =
    'Find someone to talk to. Pay someone to listen for 15 minutes — chat, voice, or video.';
  next['FAQPage.schemaTitle'] = 'FAQ: sessions, prices, safety | {marketplaceName}';
  next['FAQPage.schemaDescription'] =
    'Answers about 15-minute sessions, listener-set prices, 18+ safety, and how booking works. Your first 15 minutes are free.';
  next['TermsOfServicePage.schemaTitle'] = 'Terms for talkers and listeners | {marketplaceName}';
  next['TermsOfServicePage.schemaDescription'] =
    'The rules for using Sweet Listeners: 18+ only, on-platform conversations, Stripe payments, and no personal-info exchange. Read before you book or listen.';
  next['PrivacyPolicyPage.schemaTitle'] = 'How we protect your privacy | {marketplaceName}';
  next['PrivacyPolicyPage.schemaDescription'] =
    'What we collect, how we use it, and what stays on Sweet Listeners. We do not sell your personal information. Sessions stay on-platform.';
  next['ContactPage.schemaTitle'] = 'Write us — we write back | {marketplaceName}';
  next['ContactPage.schemaDescription'] =
    'Email info@sweetlisteners.com about bookings, listener payouts, safety, or tech help. We reply within one business day.';
  next['SearchPage.schemaDescription'] =
    'Search online listeners and Sweet Skillz teachers for a 15-minute conversation by text, voice or video. Each listener sets their own prices. Search by topic.';
  next['ListingImageGallery.imageAltText'] = '{name} — photo {index} of {count}';
  next['ListingImageGallery.imageThumbnailAltText'] = '{name} — thumbnail {index} of {count}';
  next['ImageCarousel.imageAltText'] = '{name} — photo {index} of {count}';
  next['BookingFixedDurationForm.requestToBook'] = 'Book a time';
  next['BookingFixedDurationForm.continue'] = 'Continue';
  next['AvailableNowBadge.label'] = 'Online';
  next['AvailableNowBadge.inConversation'] = 'In a conversation';
  next['AvailableNowBadge.inConversationNext'] = 'In a conversation • Next available at {time}';
  next['AvailableNowBadge.offline'] = 'Offline';
  next['OrderPanel.ctaButtonMessageBooking'] = 'Book a time';
  next['OrderPanel.cannotBookOwnListing'] = 'You can’t book a session with yourself';
  next['OrderPanel.ownListing'] = 'This is your Listening profile.';
  next['SessionPanel.chatEmpty'] =
    'No messages yet. Your session has started — say hello when you are ready.';
  next['SessionPanel.demoChatEmpty'] =
    'No messages yet. Your session has started — say hello when you are ready.';
  next['LandingPage.checkVerified'] =
    'On Live, every session is with a verified adult. On TEST, last-4 is a stand-in and does not verify anyone.';
  next['ListenerKeepShare.verified'] =
    '18+ check required. TEST uses a last-4 stand-in — it does not verify anyone. Live will need a photo ID and a selfie.';
  next['ListenerKeepShare.factsInline'] =
    '{minutes}-minute listener-set prices. You set available now or a calendar. Optional Featured homepage strip is ${monthly}/month or ${yearly}/year and does not change star ratings. 18+ check required — TEST last-4 is a stand-in and does not verify anyone; live will need a photo ID and a selfie. Remote only. Not counseling or professional advice, no sexual content.';
  next['ContentPage.about.p3'] = 'If you need emergency help, please contact a professional.';
  next['ContentPage.about.notTherapy'] =
    'We’re not a crisis service and don’t provide medical advice.';
  next['ContentPage.pricing.lede'] =
    'Each listener and teacher sets their own price per {minutes} minutes. You see it on their profile before you book.';
  next['PricingPage.kicker'] = 'Pricing';
  next['PricingPage.title'] = 'How pricing works.';
  next['PricingPage.lede'] =
    'Each listener and teacher sets their own price per {minutes} minutes for text, voice and video. You see their price on their profile before you book.';
  next['PricingPage.chooseChat'] = 'Choose Text Chat';
  next['PricingPage.chooseVoice'] = 'Choose Voice Call';
  next['PricingPage.chooseVideo'] = 'Choose Video Call';
  next['PricingPage.banner'] =
    'Prices are set by each listener or teacher. Sessions are {minutes} minutes. You can book a specific time or talk now (when available). All conversations stay on our secure platform.';
  next['ContactPage.heading'] = 'Say hello';
  next['ContactPage.lede'] = 'This is a place to talk. Write us a note and we’ll write back.';
  next['ContactPage.submit'] = 'Send Message';
  next['ContactPage.responseTime'] = "We typically respond within 1 business day.";
  next['ContactPage.otherTitle'] = 'Other ways to reach us';
  next['ContactPage.sendFailed'] = 'We couldn’t send that. Email {email} instead, or try again.';
  next['ContactPage.sentTitle'] = 'Message sent';
  next['ContactPage.sentBody'] = 'Thanks — this is a place to talk. We’ll write back.';
  next['ContactPage.sentOk'] = 'OK';
  next['TermsOfServicePage.heading'] = 'Terms of Service';
  next['TermsOfServicePage.lede'] =
    'Please read these terms carefully before using Sweet Listeners.';
  next['PrivacyPolicyPage.heading'] = 'Privacy Policy';
  next['PrivacyPolicyPage.lede'] =
    "Your privacy matters. We're committed to keeping your information safe and secure.";
  next['PrivacyPolicyPage.script'] = 'listen. care. connect.';
  next['PrivacyPolicyPage.sectionCollect'] = 'Information We Collect';
  next['PrivacyPolicyPage.sectionUse'] = 'How We Use Your Information';
  next['PrivacyPolicyPage.sectionShare'] = 'We Do Not Share Your Personal Information';
  next['PrivacyPolicyPage.sectionPlatform'] = 'Your Privacy on the Platform';
  next['PrivacyPolicyPage.sectionChoices'] = 'Your Choices';
  next['PrivacyPolicyPage.sectionUpdates'] = 'Updates to This Policy';
  next['PrivacyPolicyPage.contactCta'] = 'Contact Us';
  next['SafetyPage.eyebrow'] = 'A safer space for real conversations.';
  next['SafetyPage.heading'] = 'Safety & Security';
  next['SafetyPage.lede'] =
    'Your safety, privacy, and well-being are our top priorities. We’re committed to creating a respectful, supportive, and secure community for everyone.';
  next['SafetyPage.script'] = 'listen. care. connect.';
  next['SafetyPage.legalNav'] = 'Legal pages';
  next['SafetyPage.schemaTitle'] = 'Safety & Security | {marketplaceName}';
  next['SafetyPage.schemaDescription'] =
    '18+ verification, on-platform conversations, no explicit content, reporting, secure payments, and a respectful community.';
  next['SafetyPage.pillarVerifiedTitle'] = 'Verified Adults Only';
  next['SafetyPage.pillarVerifiedBody'] =
    'All users must be 18+ and complete an age verification process to use the platform. On TEST, the last-4 form is a stand-in and does not verify anyone. Live will require a photo ID and a selfie.';
  next['SafetyPage.pillarOnPlatformTitle'] = 'Keep It On-Platform';
  next['SafetyPage.pillarOnPlatformBody'] =
    'We keep conversations, payments, and communication on our secure platform. We do not allow the exchange of personal contact information (phone numbers, email addresses, or social media).';
  next['SafetyPage.pillarExplicitTitle'] = 'No Explicit Content';
  next['SafetyPage.pillarExplicitBody'] =
    'We’re a conversation-only platform. We do not allow nudity, sexual content, or any inappropriate content. We use automated tools, and our team reviews reports and flagged accounts.';
  next['SafetyPage.guidelinesTitle'] = 'Community Guidelines';
  next['SafetyPage.guidelinesOnPlatformTitle'] = 'Keep it on the site';
  next['SafetyPage.guidelinesOnPlatformBody'] =
    'Do not share or ask for phone numbers, emails, social media handles, messaging apps, payment apps (Venmo, Cash App, and the like), or addresses — in text, voice, or video. The site blocks these automatically.';
  next['SafetyPage.guidelinesWarningsTitle'] = 'Warnings';
  next['SafetyPage.guidelinesWarningsBody'] =
    "Contact info (phone numbers, emails, socials, payment apps, addresses) and sexual content are blocked. A blocked attempt gets an on-screen warning and you stay in the session. Repeated attempts disconnect you from the session immediately, with no refund. If the talker breaks the rules, the session is charged in full and the listener is paid. If the listener breaks the rules, the talker is not charged and the listener is not paid. Multiple infractions may lead to suspension of your account. Sexual content involving a minor ends the session immediately and pauses the account. Harassment, hate, and threats are flagged for our team to review and can get you removed from the session.";
  next['SafetyPage.guidelinesNudityTitle'] = 'No nudity or sexual content';
  next['SafetyPage.guidelinesNudityBody'] =
    "Never — not in speech, typed chat, or on camera. Adult sexual talk and messages are blocked with a warning, and repeated attempts end the session immediately with no refund. Multiple infractions may lead to suspension of your account. Sexual content involving a minor ends the session immediately and pauses the account. On video, clear nudity ends the session immediately and pauses the account pending review. Low-confidence catches never end a session or charge anyone.";
  next['SafetyPage.guidelinesKindTitle'] = 'Be kind and respectful';
  next['SafetyPage.guidelinesKindBody'] = 'No harassment, hate, threats, or pressure.';
  next['SafetyPage.guidelinesChecksTitle'] = 'Automated safety checks';
  next['SafetyPage.guidelinesChecksBody'] =
    'Speech and typed messages are screened for contact info and sexual content. On Voice and Video, speech is transcribed live by an automated system. Video is checked automatically for nudity. Calls are not recorded, and audio and video are never stored. Only a redacted excerpt of flagged content is kept for safety review.';
  next['SafetyPage.guidelinesCrisisTitle'] = 'Not a crisis line';
  next['SafetyPage.guidelinesCrisisBody'] =
    'In an emergency, call {nineOneOne}, or {nineEightEight} (Suicide & Crisis Lifeline).';
  next['SafetyPage.guidelinesReportTitle'] = 'Report a problem';
  next['SafetyPage.guidelinesReportBody'] = 'Use the Report button in the session.';
  next['SafetyPage.guidelinesAgeTitle'] = '18+ only';
  next['SafetyPage.guidelinesAgeBody'] = 'This site is for adults 18 and over.';
  next['SafetyPage.pillarReportTitle'] = 'Report & Support';
  next['SafetyPage.pillarReportBody'] =
    'If you see something that makes you feel uncomfortable or violates our guidelines, you can report it directly through the platform. Our team reviews all reports promptly and may remove content or accounts. If you are in crisis, contact a professional or call {nineEightEight} or {nineOneOne}. We are not an emergency service.';
  next['SafetyPage.pillarPaymentsTitle'] = 'Secure Payments';
  next['SafetyPage.pillarPaymentsBody'] =
    'All payments are processed through our trusted, encrypted payment partners. We never store your full payment details.';
  next['SafetyPage.pillarCommunityTitle'] = 'A Respectful Community';
  next['SafetyPage.pillarCommunityBody'] =
    'We expect all users to be kind, respectful, and considerate. Harassment, hate speech, or abusive behavior will not be tolerated and may result in account suspension or removal.';
  next['SafetyPage.helpTitle'] = 'We’re Here to Help';
  next['SafetyPage.helpLede'] =
    'Your safety matters. If you have any questions or concerns, our support team is always here for you. For an emergency, call {nineOneOne}. For a mental-health crisis in the US, call or text {nineEightEight}.';
  next['SafetyPage.contactCta'] = 'Contact Us';
  next['AccountSettings.setupLabel'] = 'Account type';
  next['AccountSettings.setupTalker'] = 'Talker';
  next['AccountSettings.setupListener'] = 'Listener';
  next['AccountSettings.setupBoth'] = 'Both';
  next['AccountSettings.setupEdit'] = 'Edit';
  next['AccountSettings.availableLabel'] = 'Available to listen';
  next['AccountSettings.availableHelp'] =
    "Off until you turn it on from your dashboard. While it’s on, we’ll email you — and text if SMS is on — when someone wants to talk now. It stays on until you turn it off or log out (or after 2 hours away if text alerts are off).";
  next['ContentPage.pricing.faq1a'] =
    'Each listener sets their own prices..';
  next['ContentPage.becomeListener.note'] =
    'Sign up, complete the TEST 18+ stand-in (the last 4 digits do not verify anyone; live will need a photo ID and a selfie), then set available now or a calendar. Sessions are remote.';
  next['ContentPage.safety.identityBody'] =
    'Live sessions will require an 18+ identity check with a photo ID and a selfie. On TEST, the last-4 form is a stand-in and does not verify anyone. We do not store ID images or raw PII in logs. Unverified people cannot publish a Listening profile, send messages, or join a paid session.';
  next['ContentPage.safety.verifiedBody'] =
    'Look for the Verified (TEST) badge. On TEST, it means they completed the last-4 stand-in — not a real government ID check. Live will require a photo ID and a selfie. Featured homepage placement is paid advertising ($10/month or $100/year). It is not a ranking or a hire.';
  next['ContentPage.safety.faq1a'] =
    'Real sessions are for adults marked 18+. On TEST both people complete a last-4 stand-in that does not verify anyone. Live will require a photo ID and a selfie.';
  next['ContentPage.howItWorks.faq2a'] =
    "Yes, sign up or sign in to book. Talking or learning never needs an ID check. Your free first Start now session needs only an 18+ confirm. A free session booked for later needs an 18+ confirm and Apple Pay, Google Pay or a card, used only for no-shows or late cancels. Paid sessions need an 18+ confirm, and you pay with Apple Pay, Google Pay or a card when you pay.";
  next['StartNowPanel.sandboxNote'] =
    'TEST uses Stripe test cards. Your card won’t be charged until the session is completed. Both of you still need the TEST 18+ stand-in. The last 4 digits do not verify anyone.';
  next['StartNowPanel.liveIdentityNote'] =
    'Your card won’t be charged until the session is completed. Both of you need to verify you are 18+ with a photo ID and a selfie.';
  next['StartNowPanel.lede'] =
    'This listener is online. Pick chat, voice, or video for {minutes} minutes ({price}). Your card won’t be charged until the session is completed.';
  next['StartNowPanel.ledeSingle'] =
    'This listener is online. {minutes} minutes of {format} is {price}. Payment is taken after your session.';
  next['StartNowPanel.holdNote'] = 'Your card won’t be charged until the session is completed.';
  next['OrderPanel.youWontBeChargedInfo'] = SESSION_CHARGE_COPY;
  next['BookingDatesForm.youWontBeChargedInfo'] = SESSION_CHARGE_COPY;
  next['BookingTimeForm.youWontBeChargedInfo'] = SESSION_CHARGE_COPY;
  next['SessionPanel.joinIdentityRequired'] =
    'Both people must complete the TEST 18+ stand-in before a session. The last 4 digits do not verify anyone.';
  next['Identity.requiredDefault'] =
    'Complete the TEST 18+ stand-in first. The last 4 digits do not verify anyone. Live will require a photo ID and a selfie.';
  next['Identity.requiredPublish'] =
    'Complete the TEST 18+ stand-in before you publish a Listening profile. The last 4 digits do not verify anyone.';
  next['Identity.requiredBook'] =
    'Confirm you are 18+ before you book. Talkers and learners never need an ID check.';
  next['Identity.requiredJoin'] =
    'Both people must complete the TEST 18+ stand-in before a session. The last 4 digits do not verify anyone.';
  next['Identity.requiredMessage'] =
    'Complete the TEST 18+ stand-in before messaging. The last 4 digits do not verify anyone.';
  next['Identity.openSettings'] = 'Open TEST 18+ stand-in';
  next['Identity.missingDobTitle'] = 'We couldn’t confirm your date of birth';
  next['Identity.missingDobBody'] =
    'Your photo ID check finished, but we could not confirm you are 18+ from it. Please try again. We don’t keep your date of birth, and we never see the ID photos from this check.';
  next['Identity.back'] = 'Back';
  next['EmailVerificationForm.nextWhileWaiting'] = 'Go to Dashboard';
  next['EmailVerificationForm.back'] = 'Back';
  next['YourProfilePage.nextStep'] = 'Go to Dashboard';
  next['ManageListingsPage.nextStep'] = 'Go to Dashboard';
  next['Identity.continueIdCheck'] = 'Continue ID check';
  next['Identity.notFinishedContinue'] = "Not finished \u2014 continue ID check";
  next['Identity.retryHint'] = "Not finished \u2014 continue ID check. Nothing was saved from the last attempt.";
  next['Identity.listingUnverified'] =
    'This listener has not completed the TEST 18+ stand-in yet. You can still browse. Paid Start now and Book a time stay locked until they finish.';
  next['LandingPage.filterAvailableNow'] = 'Online';
  next['LandingPage.trySampleHint'] = 'Start now if they’re available, or book a time';
  next['LandingPage.cardStartNow'] = 'Start now';
  next['LandingPage.cardBookATime'] = 'Book a time';
  next['DashboardPage.trySampleHint'] = 'Start now if they’re available, or book a 15-minute time.';
  next['CheckoutPage.startNowQuoting'] = 'Getting the price for this session…';
  next['CheckoutPage.retryStartNowQuote'] = 'Try the next 15 minutes';
  next['TooManyRequestsPage.heading'] = 'We’re busy. Retrying…';
  next['TooManyRequestsPage.description'] =
    'This page is still here. We are waiting a moment and trying again. This is not a missing page.';
  next['TooManyRequestsPage.retry'] = 'Retry now';
  next['CheckoutPage.retryQuoteSameWindow'] = 'Try again';
  next['CheckoutPage.retryQuoteSameWindowWait'] = 'Try again in {seconds}s';
  next['CheckoutPage.invalidTransitionError'] =
    'This booking cannot move to the next step. Stay on this page and try again — you were not charged.';
  next['CheckoutPage.bookingTimeNotAvailableMessage'] =
    'That time is no longer available. Pick a later time, or use Start now if they are available — you were not charged.';
  next['CheckoutPage.scheduledTooSoonMessage'] =
    'This time is too soon for Book a time. Use Start now if the listener is available — you were not charged.';
  next['CheckoutPage.conflictError'] =
    'This booking is already in another step. Refresh the page or open Inbox — you were not charged.';
  next['CheckoutPage.cardPaymentFailed'] =
    'The card hold failed. Check the card and try again — you were not charged.';
  next['EditListingPhotosForm.publishIdentityRequired'] =
    'Complete the TEST 18+ stand-in on this page before publishing. The last 4 digits do not verify anyone.';
  next['EditListingStyleForm.publishIdentityRequired'] =
    'Complete the TEST 18+ stand-in on this page before publishing. The last 4 digits do not verify anyone.';
  next['CheckoutPage.default-booking.orderBreakdown'] = 'Session price';
  next['CheckoutPage.default-booking.title'] = 'Request this session';
  next['NoAccessPage.initiateTransactions.heading'] = "You can't book yet";
  next['NoAccessPage.initiateTransactions.content'] =
    'Your account does not have booking permission yet. Contact support if this looks wrong.';
  next['NoAccessPage.initiateTransactions.schemaTitle'] = "You can't book yet";
  next['NoAccessPage.postListings.heading'] = "You can't create a Listening profile yet";
  next['NoAccessPage.postListings.content'] =
    'Your account does not have permission to publish a Listening profile yet. Contact support if this looks wrong.';
  next['NoAccessPage.postListings.schemaTitle'] = "You can't create a profile yet";
  next['NoAccessPage.viewListings.heading'] = "You can't view this yet";
  next['NoAccessPage.viewListings.content'] =
    'Your account does not have permission to view this page. Contact support if this looks wrong.';
  next['NoAccessPage.viewListings.schemaTitle'] = "You can't view this yet";
  next['NotFoundPage.heading'] = "We couldn't find that page.";
  next['NotFoundPage.description'] = 'Check the link, or find a listener to talk with.';
  next['NotFoundPage.browse'] = 'Find someone to talk to or learn from';
  next['CheckoutPage.default-booking.startNowTitle'] = 'Request this session';
  next['CheckoutPage.talkSession.saveCardTitle'] = 'Save a card to request this session';
  next['CheckoutPage.talkSession.saveCardBody'] =
    'Add a card on file first. After it is saved, we’ll send this request.';
  next['CheckoutPage.talkSession.saveCardHintFree'] =
    'This saves the card only. Your free text chat costs $0.00.';
  next['CheckoutPage.talkSession.saveCardHint'] =
    'This saves the card only. Payment is taken after your session.';
  next['CheckoutPage.talkSession.requestingTitle'] = 'Requesting this session';
  next['CheckoutPage.talkSession.requestingBody'] =
    'Your card won’t be charged until the session is completed.';
  next['CheckoutPage.talkSession.retryRequest'] = 'Try again';
  next['CheckoutPage.talkSession.requestStuck'] =
    'Payment is taking longer than usual. Try again, or enter your card below.';
  next['CheckoutPage.completeBooking'] = 'Request this session';
  next['CheckoutPage.chargeDisabledMessage'] =
    'This listener isn’t set up to take bookings yet. Contact support.';
  next['CheckoutPage.startNowNote'] = 'Start now — starting now';
  next['StripePaymentForm.pickupLocationUnknown'] = 'Something’s missing. Contact support.';
  next['CheckoutPage.paymentDetails'] = 'Payment details';
  next['CheckoutPage.sessionDetails'] = 'Session details';
  next['CheckoutPage.sessionDate'] = 'Date';
  next['CheckoutPage.sessionTime'] = 'Time';
  next['CheckoutPage.sessionLengthParen'] = '({minutes} minutes)';
  next['CheckoutPage.total'] = 'Total';
  next['CheckoutPage.stepDetails'] = 'Details';
  next['CheckoutPage.stepPayment'] = 'Payment';
  next['CheckoutPage.stepReview'] = 'Review';
  next['CheckoutPage.completeBookingLegal'] =
    'By completing this booking, you agree to our {termsLink} and {privacyLink}.';
  next['CheckoutPage.termsLink'] = 'Terms of Service';
  next['CheckoutPage.privacyLink'] = 'Privacy Policy';
  next['CheckoutPage.identityRequired'] =
    'Complete the TEST 18+ stand-in before requesting a session. The last 4 digits do not verify anyone.';
  next['StripePaymentForm.submitPaymentInfo'] =
    '{isBooking, select, yes {Request this session} other {Confirm and pay {totalPrice}}}';
  next['StripePaymentForm.submitConfirmPaymentInfo'] = 'Request this session';
  next['ProductOrderForm.finePrint'] = SESSION_CHARGE_COPY;
  next['StripePaymentForm.submitConfirmPaymentFinePrint'] =
    '{isBooking, select, yes {We only place a temporary hold now. Payment is taken after your session, not when you book.} other {Your card won’t be charged until the session is completed.}}';
  next['StripePaymentForm.saveCardHeading'] = 'Save a card to request this session';
  next['StripePaymentForm.saveCardDetails'] = 'Card to save';
  next['StripePaymentForm.saveCardRequired'] = 'A card on file is required to request a session.';
  next['StripePaymentForm.saveCardRequiredHint'] =
    'This card is saved on your account. Payment is taken after your session.';
  next['StripePaymentForm.securePaymentNote'] =
    'Secure payment by Stripe. We never see or store your card number.';
  next['StripePaymentForm.cardOnFileHeading'] = 'Card on file';
  next['DashboardPage.cartTitle'] = 'Session charges';
  next['DashboardPage.cartLede'] =
    'Pending means not paid yet. We charge your saved card after the session ends.';
  next['DashboardPage.cartEmpty'] = 'No pending or paid session charges yet.';
  next['DashboardPage.cartPending'] = 'Pending — charged after session.';
  next['DashboardPage.cartPaid'] = 'Paid';
  next['DashboardPage.cartFailed'] = 'Charge failed — we’ll retry';
  next['DashboardPage.cartOpen'] = 'Open booking';
  next['TransactionPage.default-booking.customer.start-now-paid.title'] =
    'Requested — Join Session. Not charged yet.';
  next['TransactionPage.default-booking.provider.start-now-paid.title'] =
    'Start now is requested — Join Session';
  next['InboxPage.default-booking.start-now-paid.status'] = 'Join Session';
  next['TransactionPage.default-booking.customer.start-now-accepted.title'] = '';
  next['TransactionPage.default-booking.provider.start-now-accepted.title'] = '';
  next['TransactionPage.default-booking.customer.pending-payment.title'] =
    '{customerName}, your request is still being sent. Not charged yet.';
  next['TransactionPage.default-booking.customer.payment-expired.title'] =
    'This request expired before it was sent. Request the session again.';
  next['OrderBreakdown.holdReleased'] = 'Hold released';
  next['OrderBreakdown.holdReleasedFee'] = '{marketplaceName} fee released';
  next['OrderBreakdown.refund'] = 'Refund';
  next['SessionPanel.endedNoShowTitle'] = 'Session cancelled.';
  next['SessionPanel.endedNoShowBody'] =
    "{name} didn't join in time. The hold was released and you were not charged.";
  next['SessionPanel.endedNoShowListenerTitle'] = 'You missed this session';
  next['SessionPanel.endedNoShowListenerBody'] =
    'This session was cancelled because you did not join within 4 minutes of the start time.';
  next['SessionPanel.findAnotherListener'] = 'Find another listener';
  next['SessionPanel.backToMyBookings'] = 'Back to My Bookings';
  next['SessionPanel.noShowCountdownTalker'] = 'They have {time} to join';
  next['SessionPanel.noShowCountdownListener'] = 'Join within {time} or the session is cancelled';
  next['SessionPanel.waitingForListenerFree'] =
    'Waiting for them to join. If they do not join within 4 minutes of the start time, this session is canceled. Your free session stays yours to use.';
  next['SessionPanel.endedNoShowBodyFree'] =
    "{name} didn't join in time. Your free session is still yours to use.";
  next['SessionPanel.waitingForListener'] =
    'Waiting for them to join. If they do not join within 4 minutes of the start time, this session is canceled and the hold is released — you are not charged.';
  next['SessionPanel.waitingForTalker'] =
    'The talker is in the session. Join so the booking can start.';
  next['SessionPanel.waitingForTalkerToJoin'] = 'Waiting for the talker to join.';
  next['SessionPanel.waitingForTalkerToJoinScheduled'] =
    "Waiting for the talker to join. If they don't join within 4 minutes of the start time, you'll still be paid.";
  next['SessionPanel.waitingForTalkerToJoinStartNow'] =
    "Waiting for the talker to join. If they don't join, you won't be paid — Start now sessions are not charged.";
  next['SessionPanel.noShowCountdownWaitingTalker'] = 'Talker has {time} to join';
  next['SessionPanel.endedTalkerNoShowTalkerTitle'] = 'You missed this session';
  next['SessionPanel.endedTalkerNoShowTalkerBody'] =
    'You missed this session. Because it was scheduled, you were charged the full session price.';
  next['SessionPanel.endedTalkerNoShowTalkerStartNowTitle'] = 'You missed this session';
  next['SessionPanel.endedTalkerNoShowTalkerStartNowBody'] =
    'You missed this session. Because this was a Start now session, you were not charged.';
  next['SessionPanel.endedTalkerNoShowListenerTitle'] = "They didn't join";
  next['SessionPanel.endedTalkerNoShowListenerBody'] =
    "{name} didn't join. You'll still be paid for this session.";
  next['SessionPanel.endedTalkerNoShowListenerStartNowTitle'] = "They didn't join";
  next['SessionPanel.endedTalkerNoShowListenerStartNowBody'] =
    "{name} didn't join. You won't be paid for this Start now session — the hold was released.";
  // Short line + link (talker funnel, Oct 21 2026, TEST). Full rules live in the FAQ.
  next['NoShowPolicy.talkerScheduled'] = 'Free to cancel up to 1 hour before.';
  next['NoShowPolicy.talkerStartNow'] = "No charge if you don't join.";
  next['NoShowPolicy.listenerScheduled'] =
    "If the talker cancels inside 1 hour of the start, or misses a scheduled session, you'll still be paid. If you cancel, the talker is not charged.";
  next['NoShowPolicy.listenerStartNow'] =
    "If the talker doesn't join a Start now session, you won't be paid — the hold is released.";
  next['InboxPage.statusTalkerNoShowCharged'] =
    'You missed this session. You were charged the full session price.';
  next['InboxPage.statusTalkerNoShowNotCharged'] = "You missed this session. You weren't charged.";
  next['InboxPage.statusTalkerNoShowListenerPaid'] = "{name} didn't join. You'll still be paid.";
  next['InboxPage.statusTalkerNoShowListenerNotPaid'] = "{name} didn't join. You weren't paid.";
  next['NotificationMenu.talkerNoShowCharged'] =
    'You missed your session with {name}. You were charged the full session price.';
  next['NotificationMenu.talkerNoShowNotCharged'] =
    "You missed your session with {name}. You weren't charged.";
  next['NotificationMenu.talkerNoShowListenerPaid'] = "{name} didn't join. You'll still be paid.";
  next['FAQPage.qNoShow'] = 'What happens if someone doesn’t join?';
  next['FAQPage.aNoShow'] =
    'Each person has 4 minutes after the start time to join. If the listener never joins, the session is cancelled, the talker’s hold is released, and the talker is not charged. If the listener joins and the talker misses a scheduled session, the talker is charged the full session price and the listener is paid. Start now sessions are never charged if the talker doesn’t join — the hold is released and the listener is not paid.';
  next['StripePaymentForm.confirmPaymentError'] =
    'We could not confirm this request. Try again. If it is not confirmed in time, the temporary hold is released — you are not charged.';
  next['TransactionPage.chargeAfterSession.pending'] = 'Pending — charged after session.';
  next['TransactionPage.chargeAfterSession.body'] =
    "You won't be charged until the session is done. Cancel at least {notice} before the start for free; inside {notice} you're charged in full.";
  next['TransactionPage.chargeAfterSession.bodyStartNow'] =
    "You won't be charged until the session is done. You can cancel free until you join.";
  next['TransactionPage.chargeAfterSession.notCharged'] =
    "You won't be charged until the session is done. Cancel at least {notice} before the start for free; inside {notice} you're charged in full.";
  next['TransactionPage.chargeAfterSession.cancelPolicy'] =
    "You won't be charged until the session is done. Cancel at least {notice} before the start for free; inside {notice} you're charged in full.";
  next['TransactionPage.chargeAfterSession.hold'] =
    "You won't be charged until the session is done. Cancel at least {notice} before the start for free; inside {notice} you're charged in full.";
  next['TransactionPage.chargeAfterSession.checking'] = 'Checking that your card is on hold…';
  next['TransactionPage.chargeAfterSession.stripeHold'] =
    'Stripe status: {status}. This is a hold — you have not been charged.';
  next['TransactionPage.chargeAfterSession.stripeCharged'] =
    'Stripe status: {status}. This card was charged.';
  next['TransactionPage.default-booking.customer.preauthorized.title'] =
    'Your session is confirmed!';
  next['TransactionPage.default-booking.customer.preauthorized.extraInfo'] =
    "Your session with {name} is confirmed. You won't be charged until the session is done.";
  next['TransactionPage.default-booking.provider.preauthorized.extraInfo'] =
    'This session is confirmed and on your schedule.';
  next['TransactionPage.whatHappensNextTitle'] = 'What happens next?';
  next['TransactionPage.whatHappensNextBody'] =
    "Your session with {name} is confirmed. You won't be charged until the session is done.";
  next['TransactionPage.goToMyBookings'] = 'Go to My Bookings';
  next['TransactionPage.default-booking.provider.pending-payment.title'] =
    'The talker has not finished requesting this session yet.';
  next['TransactionPage.default-booking.provider.payment-expired.title'] =
    'The request expired before it was sent.';
  next['TransactionPage.default-booking.customer.accepted.title'] =
    'Your session is confirmed. Not charged yet.';
  next['TransactionPage.default-booking.customer.accepted.extraInfo'] =
    'Pending — charged after session. You won’t be charged until the session is completed.';
  next['TransactionPage.default-booking.customer.delivered.extraInfo'] =
    'Your saved card was charged for this session.';
  next['TransactionPanel.activityHeading'] = 'Booking history';
  next['TransactionPage.default-booking.provider.accepted.title'] =
    'Your session with {customerName} is confirmed.';
  // operator-accept = instant booking (or auto-confirm), not a listener click.
  next['TransactionPage.ActivityFeed.default-booking.accepted'] =
    'Your session is confirmed.';
  next['TransactionPanel.viewProfile'] = 'View profile';
  next['TransactionPage.ActivityFeed.default-booking.preauthorized'] =
    '{actor, select, you {Your session with {otherUsersName} is booked.} other {{otherUsersName} booked a session.}}';
  next['TransactionPage.ActivityFeed.default-booking.delivered'] =
    '{actor, select, you {Session completed. Your card was charged.} other {Session completed.}} {reviewLink}';
  next['TransactionPage.ActivityFeed.default-booking.canceled'] =
    '{actor, select, you {The hold on your card was released. You weren’t charged.} other {This booking was cancelled. The hold was released.}}';
  next['TransactionPage.ActivityFeed.default-booking.declined'] =
    '{actor, select, you {You declined the request. They were not charged.} operator {The request was declined. The hold was released.} other {They can’t make that time. You weren’t charged anything.}}';
  next['TransactionPage.ActivityFeed.default-booking.expired'] =
    '{actor, select, you {This request expired. The hold on your card was released. You weren’t charged.} other {This request expired. They were not charged.}}';
  next['InboxPage.default-booking.pending-payment.status'] = 'Requesting';
  next['InboxPage.default-booking.accepted.status'] = 'Upcoming';
  next['InboxPage.default-booking.in-session.status'] = 'In session';
  next['SendMessageForm.identityRequired'] =
    'Complete the TEST 18+ stand-in before sending messages. The last 4 digits do not verify anyone.';
  next['NotificationMenu.joinReady'] =
    'Click here to join {format} with {name} at your assigned time';
  next['NotificationMenu.joinReadyNow'] = 'Click here to join {format} with {name} now';
  next['NotificationMenu.startingSoon'] = 'Your session with {name} is starting soon.';
  next['NotificationMenu.buzz'] = '{name} is waiting — tap Join';
  next['NotificationMenu.pendingRequest'] = '{name} wants to talk. Accept or decline';
  next['NotificationMenu.acceptReminder'] = 'Reminder: accept or decline {name}';
  next['NotificationMenu.accepted'] =
    'Your session with {name} is confirmed. Your card won’t be charged until the session is completed.';
  next['NotificationMenu.sessionCompleted'] = 'Session with {name} completed';
  next['NotificationMenu.sessionExpired'] = 'Session with {name} expired';
  next['NotificationMenu.leaveReview'] = 'Leave a review';
  next['NotificationMenu.empty'] = "You're all caught up.";
  next['NotificationMenu.viewInbox'] = 'My Bookings';
  next['NotificationMenu.viewBooking'] = 'View booking';
  next['NotificationMenu.declined'] = "{name} can't make that time. You weren't charged anything.";
  next['NotificationMenu.declinedWithSuggestions'] =
    "{name} can't make that time, but suggested other times. Tap to book one.";
  next['NotificationMenu.declinedSlotTaken'] =
    'This time was just booked, please pick another time';
  next['NotificationMenu.cancelled'] = 'Your booking with {name} was {status}.';
  next['NotificationMenu.statusCancelled'] = 'cancelled';
  next['NotificationMenu.markAllRead'] = 'Mark all as read';
  next['NotificationMenu.viewAll'] = 'View All Notifications';
  next['NotificationMenu.tabAll'] = 'All';
  next['NotificationMenu.tabBookings'] = 'Bookings';
  next['NotificationMenu.tabReviews'] = 'Reviews';
  next['NotificationMenu.tabSystem'] = 'Updates';
  next['NotificationsPage.title'] = 'Notifications';
  next['NotificationsPage.heading'] = 'Notifications';
  next['NotificationsPage.empty'] = "You're all caught up.";
  next['NotificationMenu.cancelledNoShowNotCharged'] =
    "Canceled — {name} didn't join. You weren't charged.";
  next['NotificationMenu.cancelledNoShowRefunded'] =
    "Canceled — {name} didn't join. You were refunded.";
  next['NotificationMenu.cancelledNoShowListener'] = "Canceled — {name} didn't join.";
  next['NotificationMenu.review'] = '{name} left a review.';
  next['NotificationMenu.payout'] = 'Your payout for the session with {name} was sent.';
  next['PaymentMethodsForm.submitPaymentInfo'] = 'Save card';
  next['PaymentMethodsForm.saveHint'] = 'Save this card for future bookings.';
  next['PaymentMethodsForm.saved'] =
    'Card saved. Your card won’t be charged until the session is completed.';
  next['PaymentMethodsForm.cardIncomplete'] = 'Enter the card number, expiry, and CVC to save.';
  next['JoinSessionPopup.kickerScheduled'] = 'Your assigned time';
  next['JoinSessionPopup.kickerNow'] = 'Join Session';
  next['JoinSessionPopup.kickerBuzz'] = 'Someone is waiting';
  next['JoinSessionPopup.notNow'] = 'Not now';
  next['BookingEventPopup.newBookingTitle'] = 'You have a new booking!';
  next['BookingEventPopup.acceptedTitle'] = 'Your session with {name} is confirmed';
  next['BookingEventPopup.declinedTitle'] = "{name} can't make that time";
  next['BookingEventPopup.startingSoonTitle'] = 'Your session with {name} starts soon';
  next['BookingEventPopup.newBookingLead'] = '{name} requested a {format} session with you.';
  next['BookingEventPopup.acceptedLead'] = 'Your {format} session with {name} is confirmed.';
  next['BookingEventPopup.declinedLead'] =
    "{name} isn't able to join, but they'd love to talk another time. You weren't charged anything.";
  next['BookingEventPopup.startingSoonLead'] =
    'Your {format} session with {name} is about to start.';
  next['BookingEventPopup.minutes'] = '{minutes} minutes';
  next['BookingEventPopup.formatSession'] = '{format} session';
  next['BookingEventPopup.startsIn'] = 'Starts in {countdown}';
  next['BookingEventPopup.joinConversation'] = 'Join voice call';
  next['BookingEventPopup.goToSession'] = 'Go to session';
  next['BookingEventPopup.dismiss'] = 'Dismiss';
  next['BookingEventPopup.someone'] = 'Someone';
  next['JoinWindowModal.kicker'] = 'Your assigned time';
  next['JoinWindowModal.tooEarlyTitle'] = 'This session starts at {when}.';
  next['JoinWindowModal.tooEarlyBody'] =
    'Join will open the chat a little before then. We’ll keep this booking here for you.';
  next['JoinWindowModal.endedTitle'] = 'This session has ended.';
  next['JoinWindowModal.endedBody'] =
    'The 15 minutes are over. You can head back to Inbox whenever you’re ready.';
  next['JoinWindowModal.ok'] = 'OK';
  next['JoinWindowModal.dismiss'] = 'OK';
  next['JoinWindowModal.backToInbox'] = 'Back to My Bookings';
  next['BackToDashboard.label'] = 'Back to Dashboard';
  next['ActiveSessionBanner.kicker'] = 'In session';
  next['ActiveSessionBanner.title'] = 'In session with {name} · {minutes} min left';
  next['ActiveSessionBanner.joinKicker'] = 'Ready to join';
  next['ActiveSessionBanner.joinTitle'] = "Join {name} · starts when you're both in";
  next['ActiveSessionBanner.joinCta'] = 'Join Session';
  next['ActiveSessionBanner.continueChat'] = 'Join voice call';
  next['ActiveSessionBanner.joinChat'] = 'Join chat';
  next['ActiveSessionBanner.joinVoice'] = 'Join voice call';
  next['ActiveSessionBanner.joinVideo'] = 'Join video call';
  next['ActiveSessionBanner.joinSession'] = 'Join Session';
  next['ActiveSessionBanner.badge'] = 'Upcoming Session';
  next['ActiveSessionBanner.minutesPhrase'] = '{minutes, plural, one {# minute} other {# minutes}}';
  next['ActiveSessionBanner.desktopUpcoming'] =
    'Your conversation with {name} starts in {minutesEmphasis} ({time}).';
  next['ActiveSessionBanner.desktopStarting'] =
    "It's time for your session! Your conversation with {name} is starting now.";
  next['ActiveSessionBanner.startsIn'] = 'Starts in {minutes, plural, one {# minute} other {# minutes}}';
  next['ActiveSessionBanner.timeTitle'] = "It's time for your session!";
  next['ActiveSessionBanner.atTime'] = 'Your conversation with {name} is at {time}.';
  next['ActiveSessionBanner.startingNow'] = 'Your conversation with {name} is starting now.';
  next['ActiveSessionBanner.viewSessionCta'] = 'View Session →';
  next['ActiveSessionBanner.joinSessionCta'] = 'Join Session →';
  next['ActiveSessionBanner.dismiss'] = 'Dismiss';
  next['SessionStartBanner.joinNow'] = 'Join chat';
  next['SessionStartBanner.joinChat'] = 'Join chat';
  next['SessionStartBanner.joinVoice'] = 'Join voice call';
  next['SessionStartBanner.joinVideo'] = 'Join video call';
  next['SessionStartBanner.joinSession'] = 'Join Session';
  next['SessionStartBanner.startingSoonBody'] =
    '15-min {format} with {name}. Click here to join at your assigned time.';
  next['SessionStartBanner.startNowBody'] =
    '{name} started a 15-min {format}. Click here to join — your assigned time is now.';
  next['DashboardPage.joinChat'] = 'Join chat';
  next['DashboardPage.joinVoice'] = 'Join voice call';
  next['DashboardPage.joinVideo'] = 'Join video call';
  next['DashboardPage.joinVideoNow'] = 'Join video call';
  next['DashboardPage.joinSession'] = 'Join Session';
  next['DashboardPage.joinNow'] = 'Join Session';
  next['DashboardPage.joinChatNow'] = 'Join chat';
  next['AddToCalendar.label'] = 'Add to calendar';
  next['AddToCalendar.google'] = 'Google Calendar';
  next['AddToCalendar.outlook'] = 'Outlook';
  next['AddToCalendar.ics'] = 'Download .ics';
  next['TransactionPanel.joinReadyLede'] = '';
  next['TransactionPanel.joinReadyLedeChat'] = '';
  next['TransactionPanel.bookingDetails'] = 'Booking details';
  next['SessionPanel.timeLeft'] = '{time} left';
  next['SessionPanel.join'] = 'Join Session';
  next['SessionPanel.joinChatNow'] = 'Join chat';
  next['SessionPanel.joinVoiceCall'] = 'Join voice call';
  next['SessionPanel.joinVideoCall'] = 'Join video call';
  next['SessionPanel.rejoin'] = 'Rejoin';
  next['SessionPanel.liveInProgress'] = 'Session in progress — {time} left';
  next['SessionPanel.peerWaiting'] = '{name} is waiting';
  next['SessionPanel.liveReadyBoth'] = 'Starts when you’re both in';
  next['SessionPanel.guidelinesInline'] = 'Community guidelines apply — {guidelinesLink}';
  next['SessionPanel.contactBlocked'] = 'That was not sent. Do not share {categories} in chat.';
  next['SessionPanel.category.personal_info'] = 'personal information';
  next['SessionPanel.category.harassment'] = 'harassment';
  next['SessionPanel.category.other'] = 'a policy issue';
  next['SessionPanel.category.url'] = 'a web link';
  next['SessionPanel.category.threats'] = 'a threat';
  next['SessionPanel.category.violence'] = 'violent language';
  next['SessionPanel.category.underage'] = 'underage or a minor';
  next['SessionPanel.category.self_harm'] = 'self-harm encouragement';
  next['SessionPanel.category.illegal'] = 'illegal or scam activity';
  next['SessionPanel.category.meetup'] = 'meeting in person';
  next['SessionPanel.category.scam'] = 'a scam';
  next['SessionPanel.category.doxxing'] = 'doxxing';
  next['SessionPanel.category.nudity'] = 'nudity or sexual imagery on camera';
  next['SessionPanel.category.solicitation'] = 'selling content or an adult site';
  next['SessionPanel.usingProvider'] =
    'This {minutes}-minute call runs on {provider}. Use the moderated chat below — the in-call chat stays off.';
  next['SessionPanel.mediaPolicy'] =
    'Keep it kind. No contact info, sexual content, or underage talk. {guidelinesLink}';
  next['SessionPanel.showChat'] = 'Show chat';
  next['SessionPanel.hideChat'] = 'Hide chat';
  next['SiteFooter.communityGuidelines'] = 'Community Guidelines';
  next['SessionPanel.communityGuidelines'] = 'Community Guidelines';
  next['SessionPanel.guidelinesModalTitle'] = 'Keep conversations on the platform';
  next['SessionPanel.guidelinesModalStay'] =
    'Stay on the site. Do not share phone numbers, socials, or ask to meet up.';
  next['SessionPanel.guidelinesModalKind'] = 'Be kind. Harassment and hate are not allowed.';
  next['SessionPanel.guidelinesModalNudity'] = 'No nudity or sexual content on camera.';
  next['SessionPanel.guidelinesUnderstand'] = 'I understand';
  next['SessionPanel.removedTitle'] =
    'You were removed from this session for violating Community Guidelines';
  next['SessionPanel.suspendedNotice'] = 'This account is paused pending a safety review.';
  next['SessionPanel.tapToJoin'] = 'Tap to join';
  next['SessionPanel.mediaConsent'] =
    'This call is automatically screened for safety. Speech and messages are screened for contact info and sexual content. Speech is transcribed live by an automated system. On Video, still frames from your camera are checked for nudity or sexual content. Calls are not recorded, and audio and video are never stored. Only a redacted excerpt of flagged content is kept for safety review. Pennsylvania is an all-party-consent state. By joining, I consent.';
  next['SessionPanel.confirmSafetyWhenJoin'] = "You'll confirm the safety notice when you join.";
  next['SessionPanel.mediaConsentRequired'] = 'Check the safety-screening notice before you join.';
  next['SessionPanel.joinSuspended'] = 'This account is paused pending a safety review.';
  next['SessionPanel.joinMediaUnavailable'] =
    'Voice and video are temporarily unavailable, try text';
  next['SessionPanel.attachPhoto'] = 'Attach photo';
  next['SessionPanel.removePhoto'] = 'Remove photo';
  next['SessionPanel.endedSevereTitle'] = 'Session ended for a Community Guidelines violation';
  next['SessionPanel.endedSevereBody'] =
    'This session ended because someone broke the {guidelinesLink}. Categories are logged for safety review. Raw chat and media are not stored.';
  next['SessionPanel.endedSevereOtherTitle'] =
    'This session was ended because the other person violated our Community Guidelines';
  next['SessionPanel.endedSevereOtherTalkerBody'] = "You won't be charged.";
  next['SessionPanel.endedSevereOtherListenerBody'] = "You'll still be paid for this session.";
  next['SessionPanel.endedSevereCharged'] = 'This session is charged in full.';
  next['SessionPanel.endedSevereReleased'] = 'The hold was released.';
  next['TransactionPage.guidelinesEndedTitle'] =
    'Session ended for a Community Guidelines violation';
  next['TransactionPage.guidelinesEndedBody'] =
    'This booking was closed under the {guidelinesLink}. Join is no longer available.';
  next['ReviewThanksModal.title'] = 'Thanks for your review!';
  next['ReviewThanksModal.body'] =
    'Your feedback helps keep this a great place to talk.';
  next['ReviewThanksModal.done'] = 'Done';
  next['OpsModerationPage.title'] = 'Safety events';
  next['OpsModerationPage.heading'] = 'Recent safety events';
  next['OpsModerationPage.lede'] =
    'Session text, spoken audio, and video-frame monitoring for Sam. Categories and hashed excerpts only — no raw chat dumps or recordings.';
  next['OpsModerationPage.empty'] = 'No moderation events yet.';
  next['OpsModerationPage.denied'] = 'This page is for Sweet Listeners ops only.';
  next['OpsModerationPage.loadError'] = 'Could not load safety events.';
  next['OpsModerationPage.reviewHeading'] = 'Review';
  next['OpsModerationPage.reviewLede'] =
    'Listener accounts that look like cam or fan-site use. Two or more solicitation or sexual session strikes, or a report from a talker. Review only — not an auto-ban.';
  next['SafetyReviewPage.title'] = 'Safety review';
  next['SafetyReviewPage.kicker'] = 'Ops';
  next['SafetyReviewPage.heading'] = 'Flagged contact-info and sexual-content attempts';
  next['SafetyReviewPage.lede'] =
    'Typed contact-info and sexual-content blocks are flagged after two in one session or three across sessions. Voice and Video reports of spoken contact or sexual content land here for review. Talker removed charges in full; listener removed releases the hold. Snippets are redacted. Full transcripts are not stored.';
  next['SafetyReviewPage.empty'] = 'No flagged users or contact-info reports yet.';
  next['SafetyReviewPage.suspendedHeading'] = 'Suspended pending review';
  next['SafetyReviewPage.screeningDegradedHeading'] = 'Voice/Video screening degraded';
  next['SafetyReviewPage.screeningDegradedLede'] =
    'Spoken transcription is down for these sessions (usually OpenAI billing credit). Contact-info spoken in Voice/Video will not be caught until this clears.';
  next['SafetyReviewPage.reportsHeading'] = 'Contact-info reports';
  next['SafetyReviewPage.reportReason'] = 'Shared or asked for contact info';
  next['SafetyReviewPage.reportedBy'] = 'reported by {role}';
  next['SafetyReviewPage.sessionLabel'] = 'Session {id}';
  next['SafetyReviewPage.noteLabel'] = 'Note: {note}';
  next['SafetyReviewPage.reportsEmpty'] = 'No contact-info reports yet.';
  next['SafetyReviewPage.applyTalker'] = 'Talker removed — charge';
  next['SafetyReviewPage.applyListener'] = 'Listener removed — release hold';
  next['SafetyReviewPage.liftSuspend'] = 'Lift suspend';
  next['SafetyReviewPage.lifted'] = 'Lifted — Join and booking work again.';
  next['SafetyReviewPage.liftError'] = 'Could not lift that suspend.';
  next['SafetyReviewPage.applied'] = 'Applied';
  next['SafetyReviewPage.applyError'] = 'Could not apply that outcome.';
  next['SafetyReviewPage.applying'] = 'Applying…';
  next['SafetyReviewPage.denied'] = 'This page is for Sweet Listeners ops only.';
  next['SafetyReviewPage.loadError'] = 'Could not load flagged users.';
  next['SafetyReviewPage.loading'] = 'Loading flagged users…';
  next['SafetyReviewPage.allEvents'] = 'All safety events';
  next['SafetyReviewPage.profileBlocksHeading'] = 'Profile blocks';
  next['SafetyReviewPage.reviewHeading'] = 'Repeat-offender review';
  next['SafetyReviewPage.reviewLede'] =
    'Listeners with repeated solicitation or sexual strikes, or a talker report. Review only — no auto-ban.';
  next['SafetyReviewPage.noAutoBan'] = 'No auto-ban. Review and decide.';
  next['SendMessageForm.contentBlocked'] =
    'That message was blocked. Do not share a phone number, email, link, social handle, or sexual content.';
  next['SendMessageForm.logistics_only'] =
    'Use Inbox for booking details like running late or when you’ll Join. The talk itself happens in the paid session.';
  next['SendMessageForm.logistics_too_long'] =
    'Keep Inbox notes short. Save the conversation for Join — your 15-minute talk starts then.';
  next['SendMessageForm.charactersLeft'] = '{count} characters left';
  next['TransactionPanel.sendMessageLogisticsHint'] =
    'Use this for booking details. Your 15-minute talk starts when you Join.';
  next['TransactionPanel.sendMessageLogisticsPlaceholder'] = 'Booking note for {name}…';
  next['TransactionPanel.sendMessageNotLiveChat'] =
    'Use this for booking details. Your 15-minute talk starts when you Join.';
  next['TransactionPanel.sendMessageNotLiveSession'] =
    'Use this for booking details. Your 15-minute talk starts when you Join.';
  next['SessionPanel.rejoin'] = 'Rejoin';
  next['SessionPanel.clockWaiting'] =
    'Your {minutes} minutes start when you are both in the session.';
  next['SessionPanel.clockRunning'] = '{time} left';
  next['SessionPanel.extendTitle'] = 'Stay for another 15 minutes?';
  next['SessionPanel.extendBody'] =
    'Same {format, select, chat {text chat} voice {voice} video {video} other {session}} for {minutes} more minutes. Your saved card will be charged ${price} if the listener accepts.';
  next['SessionPanel.extendCta'] = 'Stay another {minutes} min';
  next['SessionPanel.extendListenerHint'] = 'The talker can extend by {minutes} minutes';
  next['SessionPanel.rateLimited'] = 'Couldn’t reconnect yet. Tap Rejoin session.';
  next['SessionPanel.messageSending'] = 'Sending…';
  next['SessionPanel.messageFailed'] = 'Not delivered.';
  next['SessionPanel.retrySend'] = 'Retry';
  next['CheckoutPage.startNowHeldMessage'] =
    'You already have a Start now with this listener, or this time is held by that request. Open Inbox to join or finish — or tap the logo to go home.';
  next['CheckoutPage.openInbox'] = 'Open My Bookings';
  next['CheckoutPage.backToListing'] = 'Back to profile';
  next['CheckoutPage.cancelStartNow'] = 'Cancel';
  next['TopbarSimplified.goToLandingPage'] = 'Go to the homepage';
  next['Topbar.logoIcon'] = 'Go to homepage';
  next['SessionPanel.report'] = SESSION_REPORT_BUTTON_LABEL;
  next['SessionPanel.reportProblem'] = SESSION_REPORT_BUTTON_LABEL;
  next['SessionPanel.leave'] = 'Leave session';
  next['SessionPanel.leaveChat'] = 'Leave chat';
  next['SessionPanel.endChat'] = 'End chat';
  next['SessionPanel.endCall'] = 'End call';
  next['SessionPanel.mute'] = 'Mute';
  next['SessionPanel.unmute'] = 'Unmute';
  next['SessionPanel.camera'] = 'Camera';
  next['SessionPanel.cameraOff'] = 'Camera off';
  next['SessionPanel.flip'] = 'Flip';
  next['SessionPanel.privacyLine'] = 'Keep conversations on the platform.';
  next['SessionPanel.beforeVoiceTalker'] = 'Voice call';
  next['SessionPanel.beforeVoiceListener'] = 'Your voice session';
  next['SessionPanel.beforeVideoTalker'] = 'Upcoming video call';
  next['SessionPanel.beforeVideoListener'] = 'Your video session';
  next['SessionPanel.beforeChatTalker'] = 'Upcoming chat';
  next['SessionPanel.beforeChatListener'] = 'Your chat session';
  next['SessionPanel.beforeLede'] = '{minutes}-minute {format} with {name}.';
  next['SessionPanel.connectingTitle'] = 'Connecting you…';
  next['SessionPanel.connectingReadyTitle'] = 'Ready to start?';
  next['SessionPanel.connectingBody'] = '{name} will join shortly.';
  next['SessionPanel.buzzButton'] = 'Buzz {name} to join';
  next['SessionPanel.buzzSent'] = 'Buzz sent.';
  next['SessionPanel.buzzWait'] = 'You can send another buzz in {seconds}s.';
  next['SessionPanel.buzzLimit'] = "You've sent 3 buzzes this session.";
  next['SessionPanel.buzzError'] = "Couldn't send a buzz. Try again in a moment.";
  next['SessionPanel.endedTalkerTitle'] = 'Your call has ended';
  next['SessionPanel.endedTalkerBody'] = 'How was your conversation with {name}?';
  next['SessionPanel.endedThanksTalker'] = 'Thank you for being here!';
  next['SessionPanel.endedThanksListener'] = 'Thank you for listening!';
  next['SessionPanel.afterTalkerThanksTitle'] = 'Thank you!';
  next['SessionPanel.afterTalkerThanksBody'] =
    'Your review is submitted. It helps keep our community safe and supportive.';
  next['SessionPanel.afterListenerTitle'] = 'Call completed';
  next['SessionPanel.afterListenerBody'] =
    '{minutes}-minute session with {name}. Thank you for being part of our community.';
  next['SessionPanel.afterPrivateNotes'] = 'Session notes stay private — only you can see them.';
  next['SessionPanel.afterEarnings'] = 'This session {amount}';
  next['SessionPanel.completedTalkerCharged'] = 'Payment of {amount} is being processed on your card.';
  next['SessionPanel.completedTalkerChargedUnknown'] = 'Your payment is being processed on your card.';
  next['SessionPanel.completedTalkerChargePending'] = 'Your card charge is pending.';
  next['SessionPanel.completedTalkerChargeFailed'] =
    'We could not charge your saved card. You can try again or contact support.';
  next['SessionPanel.completedListenerEarned'] =
    'You earned {amount}, paid out in a few business days.';
  next['SessionPanel.completedListenerEarnedUnknown'] =
    'You earned this session, paid out in a few business days.';
  next['SessionPanel.completedYouReviewed'] = 'You reviewed {name}';
  next['SessionPanel.writeReview'] = 'Write a review';
  next['SessionPanel.leaveReview'] = 'Leave a review';
  next['SessionPanel.reviewName'] = 'Review {name}';
  next['SessionPanel.bookAgainWith'] = 'Book again with {name}';
  next['SessionPanel.howDidItGo'] = 'How did it go?';
  next['SessionPanel.privateNoteLabel'] = 'Optional private note (only admins see this)';
  next['SessionPanel.feedbackSaved'] = 'Thanks — that stays private.';
  next['SessionPanel.feedbackSubmit'] = 'Submit';
  next['SessionPanel.feedbackEdit'] = 'Edit';
  next['SessionPanel.feedbackError'] = 'Could not save that note. Try again.';
  next['SessionPanel.reportAProblem'] = 'Report a problem';
  next['InboxPage.howDidItGo'] = 'How did it go?';
  next['InboxPage.cancelBooking'] = 'Cancel booking';
  next['SessionPanel.submitReview'] = 'Submit review';
  next['SessionPanel.bookAgain'] = 'Book again';
  next['SessionPanel.setAvailability'] = 'Set availability';
  next['SessionPanel.backToDashboard'] = 'Back to dashboard';
  next['SessionPanel.leftNote'] = 'This session has ended. Book again for another talk.';
  next['SessionPanel.peerWaiting'] = '{name} is waiting';
  next['SessionPanel.peerIn'] = 'They’re in the session';
  next['SessionPanel.peerNotJoined'] = 'Waiting for {name} to join';
  next['SessionPanel.peerNotJoinedUnknown'] = 'Waiting for them to join';
  next['SessionPanel.peerInNamed'] = '{name} is in the session';
  next['SessionPanel.peerLeft'] = 'Waiting for {name} to rejoin';
  next['SessionPanel.peerLeftUnknown'] = 'Waiting for them to rejoin';
  next['SessionPanel.peerJoinedNotice'] = 'They joined';
  next['SessionPanel.peerRejoinedNotice'] = 'They rejoined';
  next['SessionPanel.peerLeftNotice'] = '{name} stepped away — still in the session';
  next['SessionPanel.peerLeftNoticeUnknown'] = 'The other person stepped away — still in the session';
  next['SessionPanel.policyMute'] = 'Muted: {reason}';
  next['SessionPanel.policyWarn'] = 'Blocked: {reason}';
  next['SessionPanel.policyWarnLast'] =
    "Final warning: {reason}. One more violation will end this session immediately, with no refund. Multiple infractions may lead to suspension of your account.";
  next['SessionPanel.contactWarnFirst'] =
    "Blocked: {reason}. This is a warning. Breaking these rules can end the session immediately, with no refund. Multiple infractions may lead to suspension of your account.";
  next['SessionPanel.policyMuteLast'] =
    "Final warning: {reason}. One more violation will end this session immediately, with no refund. Multiple infractions may lead to suspension of your account.";
  next['SessionPanel.policyEnd'] = 'Session ended: {reason}';
  next['SessionPanel.sexualWarn'] =
    "Keep it respectful. Sexual content isn't allowed here. This is a warning. Breaking these rules can end the session immediately, with no refund. Multiple infractions may lead to suspension of your account.";
  next['SessionPanel.sexualWarnLast'] =
    "Final warning: sexual content isn’t allowed here. One more violation will end this session immediately, with no refund. Multiple infractions may lead to suspension of your account.";
  next['SessionPanel.endedSexualTalkerSelfTitle'] = 'You were removed from this session';
  next['SessionPanel.endedSexualTalkerSelfBody'] =
    "You sent sexual content after earlier warnings, so you were disconnected. This session is charged in full with no refund. The listener receives their usual payout. Multiple infractions may lead to suspension of your account.";
  next['SessionPanel.endedSexualTalkerOtherTitle'] = 'The talker was removed';
  next['SessionPanel.endedSexualTalkerOtherBody'] =
    "{name} sent sexual content after earlier warnings and was disconnected. This session is charged in full, and you’ll receive your usual payout.";
  next['SessionPanel.endedSexualListenerSelfTitle'] = 'You were removed from this session';
  next['SessionPanel.endedSexualListenerSelfBody'] =
    "You sent sexual content after earlier warnings, so you were disconnected. The talker is not charged, and you will not be paid for this session. Multiple infractions may lead to suspension of your account.";
  next['SessionPanel.endedSexualListenerOtherTitle'] = 'The listener was removed';
  next['SessionPanel.endedSexualListenerOtherBody'] =
    "{name} sent sexual content after earlier warnings and was disconnected. You will not be charged.";
  next['SessionPanel.endedContactTalkerSelfTitle'] = 'You were removed from this session';
  next['SessionPanel.endedContactTalkerSelfBody'] =
    "You shared contact info after earlier warnings, so you were disconnected. This session is charged in full with no refund. The listener receives their usual payout. Multiple infractions may lead to suspension of your account.";
  next['SessionPanel.endedContactTalkerOtherTitle'] = 'The talker was removed';
  next['SessionPanel.endedContactTalkerOtherBody'] =
    "{name} shared contact info after earlier warnings and was disconnected. This session is charged in full, and you’ll receive your usual payout.";
  next['SessionPanel.endedContactListenerSelfTitle'] = 'You were removed from this session';
  next['SessionPanel.endedContactListenerSelfBody'] =
    "You shared contact info after earlier warnings, so you were disconnected. The talker is not charged, and you will not be paid for this session. Multiple infractions may lead to suspension of your account.";
  next['SessionPanel.endedContactListenerOtherTitle'] = 'The listener was removed';
  next['SessionPanel.endedContactListenerOtherBody'] =
    "{name} shared contact info after earlier warnings and was disconnected. You will not be charged.";
  next['SessionPanel.reason.personal_info'] = 'personal contact details can’t be shared in session';
  next['SessionPanel.reason.threats'] = 'threats aren’t allowed in session';
  next['SessionPanel.reason.underage'] = 'underage or child-related talk isn’t allowed';
  next['SessionPanel.reason.self_harm'] = 'self-harm encouragement isn’t allowed';
  next['SessionPanel.reason.nudity'] = 'nudity or sexual imagery isn’t allowed on camera';
  next['SessionPanel.reason.doxxing'] = 'sharing someone’s private details isn’t allowed';
  next['SessionPanel.reason.harassment'] = 'harassment isn’t allowed in session';
  next['SessionPanel.reason.sexual_content'] = 'sexual content isn’t allowed in session';
  next['SessionPanel.reason.illegal'] = 'illegal or scam activity isn’t allowed';
  next['SessionPanel.reason.other'] = 'this broke a session safety rule';
  next['SessionPanel.liveKicker'] = 'In session';
  next['SessionPanel.voiceAudioOnly'] =
    'Voice is audio only — no camera. Mute and Speaker are on this bar. Loudness is your phone’s volume buttons.';
  next['SessionPanel.muteMic'] = 'Mute microphone';
  next['SessionPanel.unmuteMic'] = 'Unmute microphone';
  next['SessionPanel.speaker'] = 'Speaker';
  next['SessionPanel.speakerHint'] =
    'Speakerphone changes where you hear them. Loudness stays on the side buttons.';
  next['SessionPanel.hardwareVolume'] =
    'Loudness is your device volume. Speakerphone switches earpiece and speaker.';
  next['SessionPanel.youAreMuted'] = 'You are muted. The other person cannot hear you.';
  next['SessionPanel.alreadyInCall'] =
    'This tab left the call so you can rejoin. Tap Rejoin session.';
  next['SessionPanel.micDenied'] =
    'The browser blocked the microphone. Allow it for this site, then tap Unmute.';
  next['SessionPanel.openInNewWindow'] = 'Open call in a new window';
  next['SessionPanel.callStaysOnPage'] = 'The call stays on this page.';
  next['SessionPanel.connectingAudio'] = 'Connecting audio…';
  next['SessionPanel.iosAudioHint'] =
    'On iPhone, turn the Ring/Silent switch off, then tap “Tap to turn on sound”. Safari often blocks the other person until that tap.';
  next['SessionPanel.tapToEnableAudio'] = 'Tap to turn on sound';
  next['SessionPanel.audioBarLabel'] = 'Call audio';
  next['SessionPanel.demoLiveKicker'] = 'In session';
  next['SessionPanel.demoEyebrow'] = 'Paid session';
  next['SessionPanel.demoHeading'] = 'Start now or book a time';
  next['SessionPanel.demoLede'] =
    'Sign in to book this listener. Pick text, voice, or video for {minutes} minutes.';
  next['SessionPanel.demoJoin'] = 'Join Session';
  next['OrderPanel.trySample'] = 'Start now or book a time';
  next['StartNowPanel.logIn'] = 'Book';
  next['ListingPaidBooking.bookCtaGuest'] = 'Start free';
  next['StartNowPanel.cta'] = 'Start now';
  next['StartNowPanel.title'] = 'Start now';
  next['OrderPanel.startNowInstead'] = 'Start now instead';
  next['SessionPanel.startNowHeading'] = 'Start now session';
  next['CheckoutPage.destinationAccountNotCompleteStripeError'] =
    'This listener isn’t set up to take bookings yet.';
  next['ListingPaidBooking.payoutsNotReadyTitle'] =
    'This listener isn’t set up to take bookings yet.';
  next['ListingPaidBooking.payoutsNotReady'] =
    'Check back soon. They still need to finish payout setup.';
  next['ListingPaidBooking.payoutsNotReadyOwn'] = 'Finish payout setup so people can Book a time.';
  next['ListingPaidBooking.payoutsNotReadyOwnCta'] = 'Finish payout setup';
  next['ListingPaidBooking.payoutsBusyTitle'] = 'This page is busy';
  next['ListingPaidBooking.payoutsBusy'] =
    'We could not confirm booking setup. Try again in a moment.';
  next['ListingPaidBooking.payoutsBusyCta'] = 'Try again';
  next['SharedBusy.message'] = "We're busy, try again in a moment";
  next['SharedBusy.retry'] = 'Retry';
  next['YourProfilePage.listeningBusy'] = "We're busy, try again in a moment";
  next['YourProfilePage.listeningBusyCta'] = 'Retry';
  next['InboxPage.fetchFailed'] = "We're busy, try again in a moment";
  next['DashboardPage.loadError'] = "We're busy, try again in a moment";
  next['ListingPaidBooking.bookTitle'] = 'Book a time';
  next['ListingPaidBooking.bookLede'] =
    'Start now isn’t offered right now. You can still pick a 15-minute time. Your card won’t be charged until the session is completed.';
  next['ListingPaidBooking.prices'] = 'Text, voice or video · prices set by the listener';
  next['ListingPaidBooking.bookCta'] = 'Book a time';
  next['ListingSampleSession.kicker'] = 'Start now or book a time';
  next['ListingSampleSession.hint'] = 'Use Start now or Book a time.';
  next['LandingPage.safetyStrip.payment'] =
    'Payment stays on Sweet Listeners. Use Start now or Book a time.';
  next['ContentPage.pricing.faq3q'] = 'Do I need an account?';
  next['ContentPage.pricing.faq3a'] =
    'Yes. Sign up or sign in to browse and book. Paid sessions use Start now or Book a time at the listed prices.';
  next['GuidePage.pay-someone-to-listen.p2'] =
    'The amount comes from the format you pick, not from a flat price. Use Start now or Book a time.';
  next['SessionPanel.chatKicker'] = 'In session';
  next['SessionPanel.chatHeading'] = 'Session chat';
  next['BookingForm.topicsToday'] = 'Topics for today (optional)';
  next['BookingForm.topicsTodayHint'] = 'Choose from what this listener offers.';
  next['TransactionPanel.reportOrder'] = SESSION_REPORT_BUTTON_LABEL;
  next['ReportModal.title'] = SESSION_REPORT_MODAL_TITLE;
  next['ReportModal.sessionTitle'] = SESSION_REPORT_MODAL_TITLE;
  next['ReportModal.violationTitle'] = SESSION_REPORT_MODAL_TITLE;
  next['ReportModal.sessionDescription'] = SESSION_REPORT_MODAL_HELP;
  next['ReportModal.sessionReasonLabel'] = 'What kind of issue?';
  next['ReportModal.sessionSentTitle'] = 'Thanks — we’ll review';
  next['ReportModal.sessionSentMessage'] =
    'Thanks for telling us. We’ll review this violation or issue. You can keep talking, or leave this session. Reporting does not end the session for the other person.';
  next['ReportModal.reason.sexual_content'] = 'Sexual content';
  next['ReportModal.reason.personal_info'] = 'Shared or asked for contact info';
  next['ReportModal.reason.harassment'] = 'Harassment or abuse';
  next['ReportModal.reason.other'] = 'Something else';
  next['ReportModal.reason.underage'] = 'They may be under 18';
  next['SessionPanel.endedUnderageTitle'] = 'This conversation has ended';
  next['SessionPanel.endedUnderageBody'] =
    'The site is only for adults 18 and older, so we ended this session and paused this account. If you are 18 or older, contact us and we will take a look. If you need someone to talk to right now, you can call or text 988 any time.';
  next['SessionPanel.endedUnderageOtherTitle'] = 'Session ended for safety';
  next['SessionPanel.endedUnderageOtherBody'] =
    'We ended this session because the other person may be under 18. Our team will review it. You do not need to do anything.';
  next['ReportModal.leaveSession'] = 'Leave session';
  next['DashboardPage.joinChatNow'] = 'Join chat';
  next['InboxPage.archive'] = 'Delete';
  next['InboxPage.deleteConversation'] = 'Delete';
  next['InboxPage.moreActions'] = 'More';
  next['InboxPage.closeMore'] = 'Close';
  next['InboxPage.deleteHint'] =
    'Delete hides a past booking from your list. Use Cancel booking to stop an upcoming session.';
  next['InboxPage.joinAtAssignedTime'] = 'Join at your assigned time';
  next['InboxPage.editBooking'] = 'Edit booking';
  next['InboxPage.leaveReview'] = 'Leave a review';
  next['InboxPage.ordersTabTitle'] = 'Talking';
  next['InboxPage.salesTabTitle'] = 'Listening';
  next['InboxPage.ordersTitle'] = 'My Bookings';
  next['InboxPage.salesTitle'] = 'My Bookings';
  next['InboxPage.noOrdersFound'] = 'No sessions yet.';
  next['InboxPage.bookAgain'] = 'Book again';
  next['InboxPage.back'] = 'Back to Dashboard';
  next['InboxPage.reviewedOn'] = 'Reviewed {date}';
  next['InboxPage.accept'] = 'Accept';
  next['InboxPage.decline'] = 'Decline';
  next['InboxPage.declineConfirmTitle'] = 'Decline this request?';
  next['InboxPage.declineConfirmBody'] = '{name} will be notified. They were not charged.';
  next['InboxPage.keepRequest'] = 'Keep request';
  next['InboxPage.browseListeners'] = 'Browse Listeners';
  next['InboxPage.noSalesFound'] = 'No sessions yet.';
  next['InboxPage.emptyAll'] = 'No sessions yet.';
  next['InboxPage.emptyUpcoming'] = 'No upcoming sessions yet.';
  next['InboxPage.emptyPast'] = 'No past sessions yet.';
  next['InboxPage.emptyCancelled'] = 'No cancelled sessions yet.';
  next['InboxPage.emptyNewRequests'] = 'No new requests yet.';
  next['InboxPage.setAvailableTimes'] = 'Set my available times';
  next['InboxPage.title'] = 'My Bookings';
  next['InboxPage.heroBookings'] = 'My Bookings';
  next['InboxPage.heroListening'] = 'My Bookings';
  next['InboxPage.heroTalking'] = 'My Bookings';
  next['InboxPage.viewList'] = 'List';
  next['InboxPage.viewCalendar'] = 'Calendar';
  next['InboxPage.setAvailability'] = 'Set availability';
  next['InboxPage.heroAccentListening'] = 'Thank you for listening';
  next['InboxPage.heroAccentTalking'] = 'Real conversations';
  next['InboxPage.newChip'] = 'New';
  next['InboxPage.format.chat'] = 'Chat';
  next['InboxPage.format.voice'] = 'Voice';
  next['InboxPage.format.video'] = 'Video';
  next['InboxPage.lede'] = 'Your bookings, in a list or on the calendar.';
  next['InboxPage.searchLabel'] = 'Search messages';
  next['InboxPage.searchPlaceholder'] = 'Search sessions...';
  next['InboxPage.viewConversation'] = 'View';
  next['TransactionPage.editBooking'] = 'Edit booking';
  next['TransactionPage.bookingEdited'] = 'Your booking was updated.';
  next['TransactionPage.bookingEditedDetails'] = '{when} · {format}';
  next['TransactionPage.bookingEditedHold'] =
    "Your original hold was released and replaced, and you still won't be charged until after the session.";
  next['TransactionPage.bookingEditedNeedsAccept'] =
    '{name} needs to accept the new time by {deadline}.';
  next['TransactionPage.bookingEditedStillAccepted'] =
    'Your session is confirmed for the new time.';
  next['TransactionPage.backToInbox'] = 'Back';
  next['TransactionPage.cancelBooking'] = 'Cancel booking';
  next['TransactionPage.cancelBookingError'] =
    'We could not cancel this booking. Try again in a moment.';
  next['EditBookingModal.kicker'] = 'Upcoming session';
  next['EditBookingModal.title'] = 'Edit booking with {name}';
  next['EditBookingModal.lede'] =
    'Change format or pick another {minutes}-minute time. You can do this until the session starts.';
  next['EditBookingModal.current'] = 'Now booked: {format} · {when} · {price}';
  next['EditBookingModal.dateLabel'] = 'Date';
  next['EditBookingModal.timeLabel'] = 'Time';
  next['EditBookingModal.timePlaceholder'] = 'Choose a 15-minute start';
  next['EditBookingModal.timesLoading'] = 'Checking open times…';
  next['EditBookingModal.timesEmpty'] = 'No open 15-minute times on this day';
  next['EditBookingModal.preview'] = 'New booking: {format} · {when} · {price}';
  next['EditBookingModal.holdChange'] = 'Your card hold will update from {from} to {to}.';
  next['EditBookingModal.holdSame'] = 'Your {price} hold stays the same.';
  next['EditBookingModal.confirm'] = 'Confirm new booking';
  next['EditBookingModal.saveChanges'] = 'Save changes';
  next['EditBookingModal.cancel'] = 'Keep current booking';
  next['EditBookingModal.cancelThisBooking'] = 'Cancel this booking';
  next['CancelBookingModal.kicker'] = 'Upcoming session';
  next['CancelBookingModal.title'] = 'Cancel this booking?';
  next['CancelBookingModal.talkerMoney'] =
    'The temporary card hold will be released. You will not be charged. Cancel stays free when you do this at least {cutoff} before the start.';
  next['CancelBookingModal.talkerLate'] =
    'This session starts in less than {cutoff}. If you cancel now, you will be charged in full and the listener will be paid — the same as a no-show.';
  next['CancelBookingModal.talkerStartNow'] =
    'Start now sessions can be canceled without a charge until you join. You will not be charged.';
  next['CancelBookingModal.listenerMoney'] =
    'The talker will be notified. Their card hold will be released and they will not be charged.';
  next['InboxPage.statusLateCancelTalkerCharged'] =
    'You canceled inside 1 hour. You were charged the full session price.';
  next['InboxPage.statusLateCancelListenerPaid'] =
    '{name} canceled inside 1 hour. You’ll still be paid.';
  next['CancelBookingModal.confirm'] = 'Yes, cancel booking';
  next['CancelBookingModal.keep'] = 'Keep booking';
  next['CancelBookingModal.error'] = 'We could not cancel this booking. Try again in a moment.';
  next['CancelBookingModal.bothJoined'] =
    'This session has already started. Cancel is no longer available.';
  next['CancelBookingModal.legacyTitle'] = "This request can't be cancelled here";
  next['CancelBookingModal.legacyPending'] =
    "This request can't be cancelled from here. Contact support if you need to change it.";
  next['CancelBookingModal.legacyClose'] = 'Got it';
  next['EditBookingModal.finePrint'] =
    'Cancel stays on this order if you need to drop the session instead.';
  next['InboxPage.navBookings'] = 'My Bookings';
  next['InboxPage.promoImpact'] = 'You’re making a difference.';
  next['InboxPage.default-booking.accepted.status'] = 'Upcoming';
  next['InboxPage.default-booking.in-session.status'] = 'In session';
  next['InboxPage.default-booking.canceled.status'] = 'Canceled';
  next['InboxPage.default-booking.cancelled.status'] = 'Canceled';
  next['InboxPage.default-booking.delivered.status'] = 'Completed';
  next['InboxPage.default-booking.reviewed.status'] = 'Completed';
  next['TransactionPage.default-booking.customer.delivered.title'] = 'Session completed';
  next['TransactionPage.default-booking.customer.reviewed-by-customer.title'] = 'Session completed';
  next['TransactionPage.default-booking.provider.reviewed-by-customer.title'] = 'Session completed';
  next['TransactionPage.default-booking.customer.reviewed-by-provider.title'] = 'Session completed';
  next['TransactionPage.default-booking.provider.reviewed-by-provider.title'] = 'Session completed';
  next['TransactionPage.default-booking.customer.reviewed.title'] = 'Session completed';
  next['TransactionPage.default-booking.provider.reviewed.title'] = 'Session completed';
  next['TransactionPage.default-booking.provider.delivered.title'] = 'Session completed';
  next['InboxPage.default-booking.past.status'] = 'Past';
  next['InboxPage.default-booking.preauthorized.status'] = 'Requested';
  next['InboxPage.default-booking.no-confirmation.status'] = 'No confirmation';
  next['InboxPage.default-booking.expired.status'] = 'No confirmation';
  next['TransactionPage.default-booking.customer.no-confirmation.title'] =
    'No confirmation — this request was not accepted.';
  next['TransactionPage.default-booking.provider.no-confirmation.title'] =
    'No confirmation — you didn’t accept this request.';
  next['TransactionPage.default-booking.customer.expired.title'] =
    'No confirmation — this request was not accepted.';
  next['TransactionPage.default-booking.provider.expired.title'] =
    'No confirmation — you didn’t accept this request.';
  next['InboxPage.default-booking.payment-expired.status'] = 'Payment expired';
  next['DashboardPage.statusNoConfirmation'] = 'No confirmation';
  next['InboxPage.noMessages'] = 'No messages yet.';
  next['InboxPage.statusWaitingToAccept'] =
    "Your session with {name} is confirmed. You won't be charged until the session is done.";
  next['InboxPage.statusWaitingToAcceptBy'] =
    "Your session with {name} is confirmed. You won't be charged until the session is done.";
  next['InboxPage.statusAcceptBy'] =
    'This session is confirmed and on your schedule.';
  next['InboxPage.statusWantsToTalk'] = '{name} wants to talk. Accept or decline';
  next['InboxPage.statusReminderSent'] = 'Reminder sent';
  next['InboxPage.statusStarts'] = 'Starts {when}';
  next['InboxPage.statusReadyToJoin'] = 'Ready to join';
  next['InboxPage.statusUpcoming'] = 'Upcoming';
  next['InboxPage.statusComplete'] = 'Completed';
  next['InboxPage.statusCompleteReview'] = 'Leave a review';
  next['InboxPage.statusCompletedReviewed'] = 'Completed · Reviewed';
  next['InboxPage.statusCompleteHowDidItGo'] = 'Session complete. How did it go?';
  next['InboxPage.statusCompleteFeedbackSaved'] = 'Session complete · Feedback saved';
  next['InboxPage.viewConversation'] = 'View Details';
  next['InboxPage.joinConversation'] = 'Join voice call';
  next['InboxPage.upcomingCount'] = '{count} Upcoming';
  next['InboxPage.newRequestCount'] = '{count} New {count, plural, one {request} other {requests}}';
  next['InboxPage.nextSession'] = 'Next session: {when}';
  next['InboxPage.inSessionNow'] = 'In session now with {name} — {minutes} min left';
  next['InfoPage.back'] = 'Back';
  next['InfoPage.findListener'] = 'Find someone to talk to or learn from';
  next['InfoPage.bookSession'] = 'Book a session';
  next['InfoPage.goDashboard'] = 'Go to dashboard';
  next['InfoPage.becomeListener'] = 'Become a listener';
  next['InboxPage.startsWhen'] = 'Starts {when}';
  next['InboxPage.beReadyEarly'] = 'Be ready a few minutes early.';
  next['InboxPage.sessionMinutes'] = '{minutes} minutes';
  next['InboxPage.badgeUpcoming'] = 'UPCOMING';
  next['InboxPage.badgeNewRequest'] = 'NEW REQUEST';
  next['InboxPage.badgeCompleted'] = 'Completed';
  next['InboxPage.badgeCancelled'] = 'Cancelled';
  next['InboxPage.statusCancelledByTalker'] = 'Cancelled by Talker';
  next['InboxPage.statusCancelledByListener'] = 'Cancelled by Listener';
  next['InboxPage.statusReceivedStars'] = 'You received a {rating}-star review';
  next['InboxPage.statusLeftStars'] = 'You left a {rating}-star review';
  next['InboxPage.statusReceivedTip'] = 'You received a tip! {amount}';
  next['InboxPage.statusCancelled'] = 'Cancelled';
  next['InboxPage.statusCancelledNoShowNotCharged'] =
    "Canceled — {name} didn't join. You weren't charged.";
  next['InboxPage.statusCancelledNoShowRefunded'] =
    "Canceled — {name} didn't join. You were refunded.";
  next['InboxPage.statusCancelledNoShowListener'] = "Canceled — {name} didn't join.";
  next['InboxPage.statusDeclined'] = 'Declined';
  next['InboxPage.statusExpired'] = 'Expired. No charge';
  next['InboxPage.statusSeeDetails'] = 'Open for details';
  next['InboxSearchForm.sortLabel'] = 'Sort by';
  next['InboxPage.sortBy.all'] = 'All sessions';
  next['InboxPage.sortBy.upcoming'] = 'Upcoming sessions';
  next['InboxPage.sortBy.past'] = 'Past (completed)';
  next['InboxPage.sortBy.cancelled'] = 'Cancelled';
  next['InboxPage.sortBy.booked'] = 'All sessions';
  next['InboxPage.sortBy.activity'] = 'All sessions';
  next['InboxPage.sortBy.createdAt'] = 'All sessions';
  next['InboxPage.sortBy.lastMessageAt'] = 'All sessions';
  next['InboxPage.sortBy.lastTransitionedAt'] = 'All sessions';
  next['ListingPage.leaveReview'] = 'Leave a review';
  next['ChooseAccountRolePage.title'] = 'How will you use the site?';
  next['ChooseAccountRolePage.lede'] =
    'You can become a listener later if you only want to talk now.';
  next['ChooseAccountRolePage.talkTitle'] = 'Talk';
  next['ChooseAccountRolePage.talkHint'] =
    'Browse listeners and book a talk. No Listening profile needed.';
  next['ChooseAccountRolePage.listenTitle'] = 'Listen';
  next['ChooseAccountRolePage.listenHint'] =
    'Set up your Listening profile — topics, styles, bio, and pricing.';
  next['ChooseAccountRolePage.bothTitle'] = 'Both';
  next['ChooseAccountRolePage.bothHint'] =
    'Set up a Listening profile, then browse and book talks too.';
  next['ChooseAccountRolePage.saveFailed'] = 'We could not save that choice. Try again.';
  next['SiteFooter.help'] = 'Tech Support';
  next['PrivacyPolicyPage.navHelp'] = 'Tech Support';
  next['ContentPage.help.kicker'] = 'Tech Support';
  next['ContentPage.help.schemaTitle'] = 'Tech Support | {marketplaceName}';
  next['EmergencyLine.lead'] = 'In danger or crisis?';
  next['EmergencyLine.call911'] = '911';
  next['EmergencyLine.call988'] = '988';
  next['EmergencyLine.text988'] = 'Text 988';
  next['EmergencyLine.safetyLabel'] = 'Safety';
  next['EmergencyLine.safetyTips'] = 'Safety tips';
  next['EmergencyLine.listenerGuidance'] =
    'If a talker says they are in danger, encourage them to call 911 or 988. You cannot place that call for them.';
  next['ContentPage.help.schemaDescription'] =
    'Try this first, then email info@sweetlisteners.com. We reply within 1 business day. Short answers on Sign up, bookings, session Report, and listener payouts.';
  next['ContentPage.help.heading'] = 'How can we help?';
  next['ContentPage.help.lede'] = 'Write us. We read every note.';
  next['ContentPage.help.emergencyTitle'] = 'Need emergency help?';
  next['ContentPage.help.call911'] = 'Call 911';
  next['ContentPage.help.call988'] = 'Call 988';
  next['ContentPage.help.text988'] = 'Text 988';
  next['ContentPage.help.searchFaqHeading'] = 'From the FAQ';
  next['ContentPage.help.searchEmpty'] = 'Nothing matches that search.';
  next['ContentPage.help.searchEmptyLede'] = "We're here — write us and we'll help.";
  next['ContentPage.help.openInFaq'] = 'Open in FAQ →';
  next['ContentPage.help.tryFirstHeading'] = 'Try this first';
  next['ContentPage.help.tryFirstLede'] = 'A few calm checks before you write us.';
  next['ContentPage.help.tryLoginTitle'] = 'Can’t log in';
  next['ContentPage.help.tryLoginBody'] = 'Check the email spelling. Try Forgot / Reset password.';
  next['ContentPage.help.tryLoginReset'] = 'Forgot / Reset password';
  next['ContentPage.help.tryStripeTitle'] = 'Stripe or payout questions';
  next['ContentPage.help.tryStripeBody'] =
    'Bank, Connect, and tax questions go to Stripe support. We can still help with in-app payout setup. Listeners: open Payment & payouts and tap Get verified so Stripe can enable transfers.';
  next['ContentPage.help.tryStripeLink'] = 'Stripe support';
  next['ContentPage.help.tryBrokenTitle'] = 'Something looks broken or stuck';
  next['ContentPage.help.tryBrokenBody'] =
    'Hard-refresh the page. Try another browser. Restart the browser, or reboot the device.';
  next['ContentPage.help.tryVerifyTitle'] = 'No verification email';
  next['ContentPage.help.tryVerifyBody'] =
    'Check Junk or Spam. Wait a few minutes. Then tap Resend. Wrong address? Use a different email on the verify screen — you stay signed in.';
  next['ContentPage.help.tryJoinTitle'] = 'Can’t join a session';
  next['ContentPage.help.tryJoinBody'] =
    'Use Join Session from that booking or Inbox at the assigned time. For Start now, use Join Session. Refresh if it stays stuck.';
  next['ContentPage.help.tryAudioTitle'] = 'Can’t hear or use the mic (Voice or Video)';
  next['ContentPage.help.tryAudioBody'] =
    'Allow the mic in the browser. Unmute in the session. Try headphones. Use Chrome or Safari.';
  next['ContentPage.help.tryPublishTitle'] = 'Can’t publish a Listening profile';
  next['ContentPage.help.tryPublishBody'] = 'Finish Stripe payout verification in';
  next['ContentPage.help.tryPublishPayments'] = 'Account settings → Payments';
  next['ContentPage.help.tryTimeTitle'] = 'Session time looks wrong';
  next['ContentPage.help.tryTimeBody'] = 'Check the device timezone. We show local time.';
  next['ContentPage.help.tryDroppedTitle'] = 'Dropped mid-session';
  next['ContentPage.help.tryDroppedBody'] =
    'Rejoin on that booking while the session is still live.';
  next['ContentPage.help.tryTroubleTitle'] = 'Trouble in a session?';
  next['ContentPage.help.tryTroubleBody'] =
    'If the chat shows an error, freezes, messages won’t send, or you don’t see a Join or Rejoin button, refresh the page, then tap Join or Rejoin session. Your session and remaining time stay on the booking, and the other person can wait. Don’t tap End chat unless you mean to end. After both people have joined, the booked time is charged even if someone leaves. If only one person joined, End chat can cancel the booking and the talker is not charged. If it still doesn’t work, use Report a problem.';
  next['ContentPage.help.tryStuckTitle'] = 'Still stuck';
  next['ContentPage.help.tryStuckBody'] = 'Write us with your account email and what you tried.';
  next['ContentPage.help.emailLine'] = 'Email {email}.';
  next['ContentPage.help.replyTime'] = 'We reply within 1 business day.';
  next['ContentPage.help.faqHeading'] = 'Quick answers';
  next['ContentPage.help.accountTitle'] = 'Account and Sign up';
  next['ContentPage.help.accountBody'] = 'Stuck creating an account or signing in? Email {email}.';
  next['ContentPage.help.bookingsTitle'] = 'Bookings and refunds';
  next['ContentPage.help.bookingsBody'] =
    'Questions about a booking or a refund go to {email}. We’ll look at it with you.';
  next['ContentPage.help.safetyTitle'] = 'Session safety';
  next['ContentPage.help.safetyBody'] =
    'During a session, use in-app Report a problem. We review those reports.';
  next['ContentPage.help.payoutsTitle'] = 'Payments and payouts';
  next['ContentPage.help.payoutsBody'] =
    'Open Account settings → Payment & payouts. Listeners set up Stripe, update payout details, and tap Get verified there. Stripe must enable transfers before anyone can Book a time or Start now — we never show raw Stripe capability errors. Tax info (1099) is on the same page — Stripe collects SSN or EIN for year-end 1099s when required. We don’t give tax advice. Anyone who pays can add or update a card there too. Cards are stored securely by Stripe, not by us. Finish Get verified before you publish a Listening profile. Email {email} if you are stuck.';
  next['ContentPage.help.notTherapy'] =
    'This is friendly conversation, not emergency care. If you need emergency help, contact a professional. If you want someone to talk to for 15 minutes, you are in the right place.';
  next['ContentPage.help.emailCta'] = 'Email us';
  next['ContentPage.help.contactCta'] = 'Contact form';
  next['OnboardingPage.schemaTitle'] = 'Set up your account | {marketplaceName}';
  next['OnboardingPage.title'] = 'Set up your account';
  next['OnboardingPage.lede'] = 'A few short steps. You can go back and change anything.';
  next['OnboardingPage.progressTitle'] = 'Account setup';
  next['OnboardingPage.stepCount'] = 'Step {current} of {total}';
  next['OnboardingPage.stepGuidelines'] = 'Rules';
  next['OnboardingPage.stepAge'] = '18+';
  next['OnboardingPage.stepCard'] = 'Card';
  next['OnboardingPage.stepPayouts'] = 'Payouts';
  next['OnboardingPage.stepTalkProfile'] = 'You';
  next['OnboardingPage.stepListenProfile'] = 'Profile';
  next['OnboardingPage.stepAvailability'] = 'Hours';
  next['OnboardingPage.guidelinesTitle'] = 'Community Guidelines';
  next['OnboardingPage.guidelinesLede'] =
    'Please read these and agree before you book or listen.';
  next['OnboardingPage.guidelinesDoneTitle'] = 'Guidelines already accepted';
  next['OnboardingPage.guidelinesDoneLede'] = 'You’re set on Community Guidelines. Continue.';
  next['OnboardingPage.guidelinesAgree'] = 'I agree — continue';
  next['OnboardingPage.ageTitle'] = 'Confirm you are 18+';
  next['OnboardingPage.ageLede'] =
    'We check a photo ID and a matching selfie. We store only that you passed.';
  next['OnboardingPage.ageDoneTitle'] = 'You’re verified';
  next['OnboardingPage.ageDoneLede'] = 'Your 18+ check is done. Continue setup.';
  next['OnboardingPage.cardTitle'] = 'Add a payment card';
  next['OnboardingPage.cardLede'] =
    'Needed to book a paid session. You can add it later — booking still needs a real card.';
  next['OnboardingPage.payoutsTitle'] = 'Set up payouts';
  next['OnboardingPage.payoutsLede'] =
    'Stripe pays you after completed sessions. Bank details stay with Stripe.';
  next['OnboardingPage.talkProfileTitle'] = 'A short profile for listeners';
  next['OnboardingPage.talkProfileLede'] =
    'Private — no public reviews. Listeners see this when you book.';
  next['OnboardingPage.talkProfilePrivate'] =
    'This is not a public profile. No reviews. Listeners only see it on a booking with you.';
  next['OnboardingPage.photoLabel'] = 'Photo (optional)';
  next['OnboardingPage.photoReady'] = 'Photo ready. You can change it later.';
  next['OnboardingPage.photoOptional'] = 'Skip if you prefer initials.';
  next['OnboardingPage.talkAboutLabel'] = 'What I’d like to talk about (optional)';
  next['OnboardingPage.talkAboutPlaceholder'] =
    'One short line — work stress, a decision, or just a check-in.';
  next['OnboardingPage.talkAboutPrivacy'] =
    'Listeners see this after you book. It is not a public bio.';
  next['OnboardingPage.talkProfileFailed'] = 'Could not save that profile. Try again.';
  next['OnboardingPage.saveAndContinue'] = 'Save and continue';
  next['OnboardingPage.listenProfileTitle'] = 'Publish your profile';
  next['OnboardingPage.listenProfileLede'] =
    'Finish your profile and publish it so talkers can find you. You’re almost ready to start listening.';
  next['OnboardingPage.listenProfileReady'] = 'Your Listening profile is live. Continue to hours.';
  next['OnboardingPage.listenProfileCta'] = 'Publish your profile';
  next['OnboardingPage.listenProfileNext'] = 'I’ll finish the profile next';
  next['OnboardingPage.listenProfileCreateCta'] = 'Create profile';
  next['OnboardingPage.availabilityTitle'] = 'When you can listen';
  next['OnboardingPage.availabilityLede'] = 'Set weekly hours. You can change them anytime.';
  next['OnboardingPage.availabilityNeedProfile'] =
    'Add a Listening profile first so we know which calendar to edit.';
  next['OnboardingPage.back'] = 'Back';
  next['OnboardingPage.continue'] = 'Continue';
  next['OnboardingPage.finish'] = 'Go to Dashboard';
  next['OnboardingPage.addCardLater'] = 'Add later';
  // Card items: the "only charged after a session" part is shown as its own
  // emphasized line by SignupChecklist (Oct 19 2026), so it isn't repeated here.
  next['SignupChecklist.talkPay'] =
    'A card, Apple Pay or Google Pay (you add it when you book your first session)';
  next['SignupChecklist.talkCard'] =
    'A debit or credit card for booking conversations (kept securely by Stripe)';
  next['SignupChecklist.bothCard'] =
    'A debit or credit card for booking conversations (kept securely by Stripe)';
  next['TransactionPanel.talkingNote'] = 'They’d like to talk about: {note}';
  Object.assign(next, INTEREST_COPY);
  applyIdentityEnvironmentCopy(next);
  // Instant booking (Sam, Oct 19 2026): booked unless the listener cancels.
  if (process.env.REACT_APP_INSTANT_BOOKING === 'true') {
    next['FAQPage.aHowItWorks'] =
      'Pick a topic, find a listener who loves it, choose text, voice, or video, then Start now or book a 15-minute session. A booked time is confirmed right away: your listener doesn’t need to accept it. If your listener has to cancel, you’re never charged.';
    next['FAQPage.qAcceptWindow'] = 'Does the listener need to accept my booking?';
    next['FAQPage.aAcceptWindow'] =
      'No. When you book a time, it’s booked right away. Your listener gets a message and can cancel if something comes up, and then you’re not charged.';
    next['StripePaymentForm.submitPaymentInfo'] =
      '{isBooking, select, yes {Book this session} other {Confirm and pay {totalPrice}}}';
    next['StripePaymentForm.submitConfirmPaymentInfo'] = 'Book this session';
    next['StripePaymentForm.submitConfirmPaymentFinePrint'] =
      '{isBooking, select, yes {It’s booked right away. We only place a temporary hold now. Payment is taken after your session, not when you book.} other {Your card won’t be charged until the session is completed.}}';
    next['StripePaymentForm.saveCardHeading'] = 'Save a card to book this session';
    next['StripePaymentForm.saveCardRequired'] = 'A card on file is required to book a session.';
    next['BookingTimeForm.requestToBook'] = 'Book';
    next['BookingDatesForm.requestToBook'] = 'Book';
  }
  return next;
};

/**
 * Live builds must never pin TEST stand-in / test-card copy. Hosted
 * translations are overwritten here when demo sessions are off.
 *
 * @param {Object} next
 * @returns {Object}
 */
export const applyIdentityEnvironmentCopy = next => {
  if (isTestMarketplace()) {
    return next;
  }
  next['AuthenticationPage.verifyIdentityTitle'] = 'Verify you are 18+';
  next['AuthenticationPage.verifyIdentityText'] =
    'We check a photo ID and a matching selfie. We store only that you passed — not the images, name, or date of birth.';
  next['AuthenticationPage.verifyEmailJunkHint'] =
    'Check Junk or Spam if the email is missing. Resend below if nothing arrived.';
  next['SignupForm.identityNotice'] =
    'You must be 18+. We verify with a photo ID and a matching selfie. We do not store ID images or raw PII.';
  next['DashboardPage.identityTitle'] = 'Confirm you are 18+';
  next['DashboardPage.identityBody'] =
    'Confirm you are 18+ with a photo ID and a selfie before you listen or book.';
  next['DashboardPage.identityCta'] = 'Open 18+ check';
  next['Identity.sandboxKicker'] = 'Photo ID + selfie';
  next['Identity.title'] = 'Verify you are 18+';
  next['Identity.lede'] =
    'Stripe checks a photo ID and a matching selfie. We store only that you passed — not the images, name, or date of birth.';
  next['Identity.confirmId'] = 'I understand this is a photo ID and selfie check.';
  next['Identity.mockLast4Label'] = 'Date of birth is read from your ID. We do not store it.';
  next['Identity.confirmSelfie'] = 'I understand a matching selfie is required.';
  next['Identity.submitSandbox'] = 'Verify with photo ID';
  next['Identity.privacyNote'] =
    'Stripe checks your ID and we never see it. If you upload a photo instead, only our team sees it, and we delete it once we’ve reviewed it. Your card won’t be charged until a session is completed.';
  next['Identity.verifyFailed'] = 'We could not complete this identity check. Try again.';
  next['Identity.verifiedTitle'] = 'You’re verified';
  next['Identity.verifiedBody'] =
    'Your photo ID and selfie passed. You can book and join sessions.';
  next['Identity.requiredDefault'] = 'Verify you are 18+ with a photo ID and a selfie first.';
  next['Identity.requiredPublish'] =
    'Verify you are 18+ with a photo ID and a selfie before you publish a Listening profile.';
  next['Identity.requiredBook'] =
    'Confirm you are 18+ before you book. Talkers and learners never need an ID check.';
  next['Identity.requiredJoin'] =
    'Both people must verify they are 18+ with a photo ID and a selfie before a session.';
  next['Identity.requiredMessage'] =
    'Verify you are 18+ with a photo ID and a selfie before messaging.';
  next['Identity.openSettings'] = 'Open 18+ check';
  next['Identity.listingUnverified'] =
    'This listener has not verified they are 18+ yet. You can still browse. Paid Start now and Book a time stay locked until they finish.';
  next['Identity.missingDobTitle'] = 'We couldn’t confirm your date of birth';
  next['Identity.missingDobBody'] =
    'Your photo ID check finished, but we could not confirm you are 18+ from it. Please try again. We don’t keep your date of birth, and we never see the ID photos from this check.';
  next['Identity.back'] = 'Back';
  next['VerifiedBadge.label'] = 'Verified';
  next['VerifiedBadge.title'] = 'This person verified they are 18+ with a photo ID and a selfie.';
  next['LandingPage.verified'] = 'Verified';
  next['LandingPage.checkVerified'] =
    'Every session is with a verified adult. We check a photo ID and a matching selfie.';
  next['ListenerKeepShare.verified'] =
    '18+ check required. We verify with a photo ID and a selfie.';
  next['ListenerKeepShare.factsInline'] =
    '{minutes}-minute listener-set prices. You set available now or a calendar. Optional Featured homepage strip is ${monthly}/month or ${yearly}/year and does not change star ratings. 18+ check required — photo ID and a selfie. Remote only. Not counseling or professional advice, no sexual content.';
  next['ProfileReveals.under18'] =
    'You must be 18+ to be a listener. This birthdate would make you under 18.';
  next['SafetyPage.pillarVerifiedBody'] =
    'Everyone must be 18+. Listeners verify with a photo ID and a matching selfie before they can take sessions or get paid, and paid sessions need a verified account. Your free first 15 minutes with Start now can start without a card or ID. We store only that a check passed, never the ID images.';
  next['ContentPage.becomeListener.note'] =
    'Sign up, verify you are 18+ with a photo ID and a selfie, then set available now or a calendar. Sessions are remote.';
  next['ContentPage.safety.identityBody'] =
    'Sessions require an 18+ identity check with a photo ID and a selfie. We do not store ID images or raw PII. Unverified people cannot publish a Listening profile, send messages, or join a paid session.';
  next['ContentPage.safety.verifiedBody'] =
    'Look for the Verified badge. It means they verified they are 18+ with a photo ID and a selfie. Featured homepage placement is paid advertising ($10/month or $100/year). It is not a ranking or a hire.';
  next['ContentPage.safety.faq1a'] =
    'Real sessions are for adults marked 18+. Both people verify with a photo ID and a selfie.';
  next['ContentPage.howItWorks.faq2a'] =
    "Yes, sign up or sign in to book. Talking or learning never needs an ID check. Your free first Start now session needs only an 18+ confirm. A free session booked for later needs an 18+ confirm and Apple Pay, Google Pay or a card, used only for no-shows or late cancels. Paid sessions need an 18+ confirm, and you pay with Apple Pay, Google Pay or a card when you pay.";
  next['StartNowPanel.sandboxNote'] =
    'Your card won’t be charged until the session is completed. Both of you need to verify you are 18+ with a photo ID and a selfie.';
  next['StartNowPanel.liveIdentityNote'] =
    'Your card won’t be charged until the session is completed. Both of you need to verify you are 18+ with a photo ID and a selfie.';
  next['ContactDetailsForm.smsTwilioReady'] =
    'Texts are on. Opted-in numbers will receive texts.';
  next['ContactDetailsForm.smsTwilioPending'] =
    'Your number is saved. Texts stay off until SMS is enabled on the server.';
  next['SessionPanel.joinIdentityRequired'] =
    'Both people must verify they are 18+ with a photo ID and a selfie before a session.';
  next['SessionPanel.startNowLede'] =
    'This {minutes}-minute session is {format, select, chat {text chat} voice {voice} video {video} other {a talk session}}. Your card won’t be charged until the session is completed. Sharing contact details, payment information, or sexual content is not allowed.';
  next['EditListingPhotosForm.publishIdentityRequired'] =
    'Verify you are 18+ with a photo ID and a selfie on this page before publishing.';
  next['EditListingStyleForm.publishIdentityRequired'] =
    'Verify you are 18+ with a photo ID and a selfie on this page before publishing.';
  next['CheckoutPage.identityRequired'] =
    'Verify you are 18+ with a photo ID and a selfie before requesting a session.';
  next['SendMessageForm.identityRequired'] =
    'Verify you are 18+ with a photo ID and a selfie before sending messages.';
  return next;
};
